// src/private/src/pages/app/clients/ClientDashboard.jsx
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useEffect, useState, useMemo, useCallback, useRef, Fragment } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Paper,
  Divider,
  Stack,
  TextField,
  Button,
  Pagination,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip as MuiTooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BarChartIcon from '@mui/icons-material/BarChart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  DateRangePicker,
  DollarFormatter,
  PercentageFormatter,
  VerticalDropdown
} from '@src/components';
import {
  fetchDailyPulseByClient,
  fetchChannelSummaryByDate,
  fetchDailyPulseByCampaign,
  fetchDailyPulseByState,
  fetchDailyPulseByCity,
  fetchDailyPulseByCounty,
  fetchDailyPulseByDMA,
  fetchDailyPulseByTerritoryCampaign,
  fetchDailyPulseByVertical,
  fetchClientSummaryByDate,
  fetchBudgetPacing,
  fetchBudgetPacingOverall,
  fetchBudgetPacingSegments,
  triggerDailyPulseBackfill,
  fetchDailyPulseBackfillStatus,
} from '@src/api';
import api from '@src/api/clients';
import { useAuth } from '@src/context';
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { formatDateToYMD, getCurrentMonthDates, getLastSixMonthsRange } from '@src/utils/dateUtils';
import {
  buildPacingLookup,
  getDaysLeftInMonthIncludingToday,
  recalculateBudgetMetrics,
} from '@src/utils/budgetPacing';
import ClientZipMap from './components/ClientZipMap';

const stickyCol = { position: 'sticky', left: 0, backgroundColor: 'background.paper', zIndex: 1 };
const stickyHeadCol = { ...stickyCol, zIndex: 2 };
const compactTableSx = {
  '& .MuiTableCell-root': { py: 0.75, px: 1.25, whiteSpace: 'nowrap' },
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontWeight: 700,
    color: 'text.secondary',
    backgroundColor: 'background.default',
    borderBottomColor: 'divider',
  },
};
const sectionSx = {
  borderRadius: 14,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 18px 38px rgba(15, 23, 42, 0.08)',
  overflow: 'hidden',
  backgroundColor: 'transparent',
  '&:before': { display: 'none' },
};
const sectionSummarySx = {
  px: 3,
  py: 1.5,
  bgcolor: 'background.default',
};
const sectionDetailsSx = { p: 0, bgcolor: 'background.paper' };
const stickyControlsSx = {
  position: 'sticky',
  top: 72,
  zIndex: 12,
  borderRadius: 14,
  boxShadow: '0 18px 32px rgba(15, 23, 42, 0.12)',
  border: '1px solid',
  borderColor: 'divider',
};

const dayRangeOptions = [7, 14, 21, 30];
const MAIN_LEAD_FIELDS = [
  'id',
  'client_name',
  'date_created',
  'webhook_sent_datetime',
  'first_name',
  'last_name',
  'phone',
  'email',
  'source',
  'territory',
  'zip_code',
  'self_book_appointment_date_time',
  'vertical',
  'processed',
  'success_post',
  'post_response',
  'related_backend_data',
].join(',');

const DRILLDOWN_LEAD_FIELDS = [
  'id',
  'date_created',
  'first_name',
  'last_name',
  'phone',
  'email',
  'source',
  'territory',
  'zip_code',
  'self_book_appointment_date_time',
  'vertical',
  'success_post',
  'post_response',
  'campaign_id',
  'campaign',
  'campaign_name',
  'channel',
].join(',');

const axisCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const tooltipCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const CLIENT_BRAND_GRADIENTS = {
  'express-flooring': ['#0f172a', '#1d4ed8'],
};

const gradientPalette = [
  ['#0b1538', '#2196f3'],
  ['#0f172a', '#0ea5e9'],
  ['#0f172a', '#22c55e'],
  ['#111827', '#7c3aed'],
  ['#0f172a', '#ef4444'],
];

const isHexColor = (value = '') => /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test((value || '').trim());

const toFullHex = (value = '') => {
  const hex = (value || '').replace('#', '').trim();
  if (hex.length === 3) {
    return `#${hex
      .split('')
      .map((char) => char + char)
      .join('')}`;
  }
  return `#${hex}`;
};

const channelShift = (channel, factor = 0) => {
  const clamped = Math.min(Math.max(channel, 0), 255);
  const delta = factor >= 0 ? (255 - clamped) * factor : clamped * factor;
  return Math.min(Math.max(Math.round(clamped + delta), 0), 255);
};

const shadeHex = (hexColor, factor = 0) => {
  const fullHex = toFullHex(hexColor);
  const intVal = Number.parseInt(fullHex.slice(1), 16);
  if (Number.isNaN(intVal)) {
    return fullHex;
  }
  const r = (intVal >> 16) & 255;
  const g = (intVal >> 8) & 255;
  const b = intVal & 255;
  const toHex = (n) => n.toString(16).padStart(2, '0');
  return `#${toHex(channelShift(r, factor))}${toHex(channelShift(g, factor))}${toHex(
    channelShift(b, factor)
  )}`;
};

const normalizeSlug = (value = '') =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const hashString = (value = '') =>
  Array.from(value).reduce((acc, char) => acc + char.charCodeAt(0), 0);

const buildGradient = (start, end) => `linear-gradient(120deg, ${start} 0%, ${end} 100%)`;

const getClientGradient = (client) => {
  const brandColor = (client?.brand_color || '').trim();
  if (isHexColor(brandColor)) {
    const start = shadeHex(brandColor, -0.12);
    const end = shadeHex(brandColor, 0.18);
    return buildGradient(start, end);
  }

  const slug = normalizeSlug(client?.slug || client?.name || 'default');
  if (CLIENT_BRAND_GRADIENTS[slug]) {
    const [start, end] = CLIENT_BRAND_GRADIENTS[slug];
    return buildGradient(start, end);
  }

  const palette = gradientPalette;
  const hash = hashString(slug);
  const index = Number.isFinite(hash) ? hash % palette.length : 0;
  const [start, end] = palette[index];
  return buildGradient(start, end);
};

const getInitials = (value = '') =>
  value
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase?.() || '')
    .join('');

const parseDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return value;
  }
  const str = String(value).trim();
  const ymdMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymdMatch) {
    const [, y, m, d] = ymdMatch.map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0, 0));
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const fallback = new Date(str);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const parseMonthRange = (value) => {
  if (!value) return null;
  const text = String(value).trim();

  const isoMatch = text.match(/^(\d{4})[-/](\d{1,2})(?:[-/]\d{1,2})?$/);
  if (isoMatch) {
    const [, yearStr, monthStr] = isoMatch;
    const year = Number(yearStr);
    const monthIndex = Number(monthStr) - 1;
    if (Number.isFinite(year) && Number.isFinite(monthIndex) && monthIndex >= 0 && monthIndex < 12) {
      const start = new Date(year, monthIndex, 1, 0, 0, 0, 0);
      const end = new Date(year, monthIndex + 1, 0, 23, 59, 59, 999);
      return { startDate: start, endDate: end };
    }
  }

  const wordMatch = text.match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (wordMatch) {
    const [, monthName, yearStr] = wordMatch;
    const parsed = new Date(`${monthName} 1, ${yearStr}`);
    if (!Number.isNaN(parsed.getTime())) {
      const start = new Date(parsed.getFullYear(), parsed.getMonth(), 1, 0, 0, 0, 0);
      const end = new Date(parsed.getFullYear(), parsed.getMonth() + 1, 0, 23, 59, 59, 999);
      return { startDate: start, endDate: end };
    }
  }

  const fallback = new Date(text);
  if (!Number.isNaN(fallback.getTime())) {
    const start = new Date(fallback.getFullYear(), fallback.getMonth(), 1, 0, 0, 0, 0);
    const end = new Date(fallback.getFullYear(), fallback.getMonth() + 1, 0, 23, 59, 59, 999);
    return { startDate: start, endDate: end };
  }

  return null;
};

const formatFileSize = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) {
    return 'Unknown size';
  }
  if (value < 1024) {
    return `${value} B`;
  }
  if (value < 1024 * 1024) {
    return `${(value / 1024).toFixed(1)} KB`;
  }
  if (value < 1024 * 1024 * 1024) {
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const INGESTION_STATUS_EMOJI = {
  ok: '✅',
  warning: '⚠️',
  missing: '❌',
};

const formatRuntimeSeconds = (value) => {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds < 0) {
    return null;
  }
  if (seconds < 1) {
    return '<1s';
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds - minutes * 60);
  if (minutes && remainingSeconds) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  if (minutes) {
    return `${minutes}m`;
  }
  return `${remainingSeconds}s`;
};

const formatIsoInEST = (value) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return null;
  }
  return date.toLocaleString('en-US', { timeZone: 'America/New_York' });
};

const dayKey = (value) => {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return null;
  }
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
};

const isTaskFreshInLocalTimezone = (task) => {
  if (!task || task.status === 'missing') {
    return false;
  }

  if (task.name === 'backend_data') {
    if (!task.max_created_date) {
      return false;
    }
    const maxDay = dayKey(task.max_created_date);
    const nowDay = dayKey(new Date());
    if (!Number.isFinite(maxDay) || !Number.isFinite(nowDay)) {
      return Boolean(task.fresh);
    }
    const diffInDays = Math.floor((nowDay - maxDay) / (24 * 60 * 60 * 1000));
    return diffInDays <= 1;
  }

  if (task.finished_at) {
    const finishedAt = new Date(task.finished_at);
    if (Number.isFinite(finishedAt.getTime())) {
      const diffInMs = Date.now() - finishedAt.getTime();
      return diffInMs <= 24 * 60 * 60 * 1000;
    }
  }

  return Boolean(task.fresh);
};

const buildTaskStatusText = (task) => {
  const parts = [];
  const statusLabel = (task?.status || 'missing').replace(/_/g, ' ');
  parts.push(statusLabel);

  const isFresh = isTaskFreshInLocalTimezone(task);
  if (task && !isFresh && task.status !== 'missing') {
    parts.push('stale');
  }

  const runtime = formatRuntimeSeconds(task?.runtime_seconds);
  if (runtime) {
    parts.push(runtime);
  }

  if (task?.finished_at) {
    const finishedLabel = formatIsoInEST(task.finished_at);
    if (finishedLabel) {
      parts.push(finishedLabel);
    }
  } else if (task?.status !== 'missing') {
    parts.push('no recent run');
  }

  if (task?.max_created_date) {
    parts.push(`max date ${task.max_created_date}`);
  }

  return parts.join(' • ');
};

const IngestionStatusIndicator = ({ ingestionHealth }) => {
  if (!ingestionHealth) {
    return null;
  }

  const tasks = Array.isArray(ingestionHealth.tasks) ? ingestionHealth.tasks : [];
  const isHealthyTask = (task) => {
    if (!task || task.status === 'missing') {
      return false;
    }
    if (task.name === 'backend_data') {
      return isTaskFreshInLocalTimezone(task);
    }
    return (
      String(task.status).toLowerCase() === 'succeeded' &&
      isTaskFreshInLocalTimezone(task)
    );
  };
  const statusKey = ingestionHealth.has_mapping
    ? tasks.length > 0 && tasks.every(isHealthyTask)
      ? 'ok'
      : 'warning'
    : 'missing';
  const symbol = INGESTION_STATUS_EMOJI[statusKey] || '';

  const tooltipContent = (
    <Box sx={{ maxWidth: 320 }}>
      <Typography variant="subtitle2" mb={0.5}>
        Data tasks
      </Typography>
      {!ingestionHealth.has_mapping ? (
        <Typography variant="body2" color="text.secondary">
          No email, API, SFTP, or Google Sheet mappings configured.
        </Typography>
      ) : tasks.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No recent task runs recorded.
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {tasks.map((task, index) => (
            <Box key={`${task?.name || 'task'}-${index}`}>
              <Typography variant="body2" fontWeight={600}>
                {task?.name || 'Task'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {buildTaskStatusText(task)}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );

  if (!symbol) {
    return null;
  }

  return (
    <MuiTooltip arrow placement="top" title={tooltipContent}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          fontSize: '1rem',
          cursor: 'default',
        }}
      >
        {symbol}
      </Box>
    </MuiTooltip>
  );
};

const isDateInRange = (value, start, end) => {
  if (!value || !start || !end) return false;
  const parsed = parseDateValue(value);
  if (!parsed) return false;
  const ts = parsed.getTime();
  return ts >= start.getTime() && ts <= end.getTime();
};

const getDisplayValue = (row = {}, keys = []) => {
  for (const key of keys) {
    const raw = row?.[key];
    if (raw === undefined || raw === null) continue;
    const str = String(raw).trim();
    if (!str) continue;
    if (/^[0-9]+$/.test(str)) continue;
    return str;
  }
  return null;
};

const formatTerritory = (row = {}) => {
  const value = getDisplayValue(row, [
    'territory_name',
    'territory',
    'territory_label',
    'territory_display',
    'client_zip_territory_name',
    'client_zip_territory_label',
    'client_zip_territory_display',
  ]);
  return value || '-';
};

const formatZipVertical = (row = {}) => {
  const value = getDisplayValue(row, [
    'client_zip_vertical_name',
    'client_zip_vertical_label',
    'client_zip_vertical',
    'zip_vertical_name',
    'zip_vertical_label',
    'zip_vertical',
  ]);
  return value || '-';
};

const momColumnCount = 28;
const dodColumnCount = 28;
const BACKEND_PAGE_SIZE = 50;
const BACKEND_PAGE_GUARD = 100; // safety against runaway pagination

function Section({ title, subtitle, action, defaultExpanded = true, onToggle, children }) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={sectionSx}
      onChange={(_, expanded) => {
        if (typeof onToggle === 'function') {
          onToggle(expanded);
        }
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {subtitle}
              </Typography>
            )}
          </Box>
          {action}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={sectionDetailsSx}>{children}</AccordionDetails>
    </Accordion>
  );
}

const formatAxisCurrency = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return axisCurrencyFormatter.format(value);
  }
  return value;
};

const formatTooltipCurrency = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return tooltipCurrencyFormatter.format(value);
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) ? tooltipCurrencyFormatter.format(numeric) : value;
};

const spendSeriesKeys = new Set(['total_spend_sum', 'spendWithFees']);
const MULTI_SELECT_MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 48 * 4.5 + 8,
      width: 260,
    },
  },
};

const aggregateMetricRows = (
  rows,
  { labelKeys = [], fallbackLabel = 'Unknown', preserveKeys = [] } = {}
) => {
  if (!Array.isArray(rows) || !rows.length) {
    return [];
  }

  const normalizedLabelKeys = labelKeys.filter(Boolean);

  return Object.values(
    rows.reduce((acc, currentRow = {}) => {
      const labelValue = normalizedLabelKeys
        .map((key) => currentRow?.[key])
        .find((value) => typeof value === 'string' && value.trim().length > 0);

      const displayName = (labelValue || fallbackLabel).trim() || fallbackLabel;
      const bucketKey = displayName.toLowerCase();

      if (!acc[bucketKey]) {
        acc[bucketKey] = {
          uuid: currentRow.uuid || displayName,
          displayName,
        };

        normalizedLabelKeys.forEach((key) => {
          if (currentRow[key] !== undefined) {
            acc[bucketKey][key] = currentRow[key];
          }
        });

        preserveKeys.forEach((key) => {
          if (currentRow[key] !== undefined) {
            acc[bucketKey][key] = currentRow[key];
          }
        });
      }

      const bucket = acc[bucketKey];

      if (!bucket.uuid && currentRow.uuid) {
        bucket.uuid = currentRow.uuid;
      }

      Object.entries(currentRow).forEach(([key, value]) => {
        if (key === 'uuid' || normalizedLabelKeys.includes(key)) {
          return;
        }

        const numericValue = Number(value);
        if (!Number.isNaN(numericValue) && Number.isFinite(numericValue)) {
          bucket[key] = (bucket[key] || 0) + numericValue;
        } else if (bucket[key] === undefined) {
          bucket[key] = value;
        }
      });

      return acc;
    }, {})
  );
};

const normalizeSummaryDateKey = (value) => {
  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return formatDateToYMD(value);
  }
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const ymdPrefix = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  if (ymdPrefix?.[1]) {
    return ymdPrefix[1];
  }
  const parsed = new Date(trimmed);
  if (!Number.isFinite(parsed.getTime())) {
    return null;
  }
  return formatDateToYMD(parsed);
};

export const aggregateSummaryRowsByDay = (rows) => {
  if (!Array.isArray(rows) || !rows.length) {
    return [];
  }

  const buckets = new Map();

  rows.forEach((row = {}) => {
    const dayKey = normalizeSummaryDateKey(row?.date);
    if (!dayKey) {
      return;
    }

    if (!buckets.has(dayKey)) {
      buckets.set(dayKey, { date: dayKey });
    }

    const bucket = buckets.get(dayKey);

    Object.entries(row).forEach(([key, value]) => {
      if (
        key === 'date' ||
        key === 'id' ||
        key === 'pk' ||
        key === 'uuid' ||
        key === 'ingestion_health' ||
        key.endsWith('_id')
      ) {
        return;
      }
      if (typeof value === 'boolean') {
        return;
      }
      if (typeof value === 'string' && value.trim() === '') {
        return;
      }
      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) {
        bucket[key] = (bucket[key] || 0) + numericValue;
      } else if (bucket[key] === undefined) {
        bucket[key] = value;
      }
    });
  });

  return Array.from(buckets.values()).sort((a, b) => a.date.localeCompare(b.date));
};

export default function ClientDashboardPage() {
  const { clientId: clientSlug } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { state } = useAuth();
  const user = state?.user;
  const isStaff = user?.is_staff || user?.is_superuser;
  const [client, setClient] = useState(null);
  const [clientPk, setClientPk] = useState(null);
  const [parentCompanyId, setParentCompanyId] = useState(null);
  const [scopeType, setScopeType] = useState('client');
  const [scopeClients, setScopeClients] = useState([]);
  const [selectedScopeClientId, setSelectedScopeClientId] = useState('');
  const [summary, setSummary] = useState([]);
  const [budgetPacingLookup, setBudgetPacingLookup] = useState({
    clients: {},
    channels: {},
    territories: {},
  });
  const [budgetPacingRows, setBudgetPacingRows] = useState([]);
  const [overallPacingRows, setOverallPacingRows] = useState([]);
  const [segmentPacingRowsCanonical, setSegmentPacingRowsCanonical] = useState([]);
  const [channels, setChannels] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [states, setStates] = useState([]);
  const [dmas, setDmas] = useState([]);
  const [territoryCampaigns, setTerritoryCampaigns] = useState([]);
  const [territoryVerticalFilter, setTerritoryVerticalFilter] = useState([]);
  const [cities, setCities] = useState([]);
  const [counties, setCounties] = useState([]);
  const [momData, setMomData] = useState([]);
  const [momLoading, setMomLoading] = useState(true);
  const [verticalDetails, setVerticalDetails] = useState({});
  const [channelDetails, setChannelDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [backfillStatus, setBackfillStatus] = useState(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillRequestId, setBackfillRequestId] = useState(null);
  const [backfillProgress, setBackfillProgress] = useState(null);
  const [latestProcessedCsv, setLatestProcessedCsv] = useState(null);
  const [latestProcessedCsvLoading, setLatestProcessedCsvLoading] = useState(false);
  const [latestProcessedCsvError, setLatestProcessedCsvError] = useState(null);
  const [csvDownloadLoading, setCsvDownloadLoading] = useState(false);
  const [dataRefreshToken, setDataRefreshToken] = useState(0);
  const backfillPollRef = useRef(null);

  const [shouldLoadBreakdowns, setShouldLoadBreakdowns] = useState(false);
  const [breakdownsLoading, setBreakdownsLoading] = useState(false);
  const [shouldLoadMom, setShouldLoadMom] = useState(false);

  const [showFee, setShowFee] = useState(true);
  const [dayRange, setDayRange] = useState(dayRangeOptions[0]);

  const [leads, setLeads] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [momRowDetails, setMomRowDetails] = useState({});
  const [dodRowDetails, setDodRowDetails] = useState({});
  const [cityPage, setCityPage] = useState(0);
  const [cityRowsPerPage, setCityRowsPerPage] = useState(25);
  const [countyPage, setCountyPage] = useState(0);
  const [countyRowsPerPage, setCountyRowsPerPage] = useState(25);
  const [campaignDetails, setCampaignDetails] = useState({});
  const [filters, setFilters] = useState({
    phone: '',
    email: '',
    id: '',
    zip_code: '',
    source: '',
    vertical: '',
    success_post: ''
  });

  const pageSize = 100;
  const leadRequestCacheRef = useRef(new Map());
  const leadInFlightRequestRef = useRef(new Map());

  const [monthStart, monthEnd] = useMemo(() => getCurrentMonthDates(), []);
  const [startDate, setStartDate] = useState(monthStart);
  const [endDate, setEndDate] = useState(monthEnd);
  const currentRange = useMemo(() => ({ startDate, endDate }), [startDate, endDate]);

  // Keep pacing anchored to a stable local "today" rather than the selected date range.
  const [calendarToday] = useState(() => new Date());

  const payload = useMemo(() => {
    if (selectedScopeClientId) {
      return {
        client_id: selectedScopeClientId,
        date_after: formatDateToYMD(startDate),
        date_before: formatDateToYMD(endDate)
      };
    }
    if (scopeType === 'parent' && !parentCompanyId) {
      return null;
    }
    if (scopeType === 'client' && !clientPk) {
      return null;
    }
    const scopePayload =
      scopeType === 'parent'
        ? { parent_company_id: parentCompanyId }
        : { client_id: clientPk };
    return {
      ...scopePayload,
      date_after: formatDateToYMD(startDate),
      date_before: formatDateToYMD(endDate)
    };
  }, [selectedScopeClientId, scopeType, parentCompanyId, clientPk, startDate, endDate]);

  useEffect(() => {
    if (isStaff) {
      setShouldLoadBreakdowns(true);
      setShouldLoadMom(true);
    }
  }, [isStaff]);

  const handleBreakdownsToggle = useCallback(
    (expanded) => {
      if (expanded) {
        setShouldLoadBreakdowns(true);
      }
    },
    [setShouldLoadBreakdowns]
  );

  const handleMomToggle = useCallback(
    (expanded) => {
      if (expanded) {
        setShouldLoadMom(true);
      }
    },
    [setShouldLoadMom]
  );

  const leadScopeParams = useMemo(() => {
    if (selectedScopeClientId) {
      return { client: selectedScopeClientId };
    }
    if (scopeType === 'parent' && parentCompanyId) {
      return { parent_company_id: parentCompanyId };
    }
    if (scopeType === 'client' && clientPk) {
      return { client: clientPk };
    }
    return null;
  }, [selectedScopeClientId, scopeType, parentCompanyId, clientPk]);

  const backendScopeParams = useMemo(() => {
    if (selectedScopeClientId) {
      return { client: selectedScopeClientId };
    }
    if (scopeType === 'parent' && parentCompanyId) {
      return { parent_company_id: parentCompanyId };
    }
    if (scopeType === 'client' && clientPk) {
      return { client: clientPk };
    }
    return null;
  }, [selectedScopeClientId, scopeType, parentCompanyId, clientPk]);

  const activeProcessedCsvClientId = useMemo(() => {
    if (selectedScopeClientId) {
      return Number(selectedScopeClientId) || null;
    }
    if (scopeType === 'client' && clientPk) {
      return Number(clientPk) || null;
    }
    return null;
  }, [selectedScopeClientId, scopeType, clientPk]);

  const activeSummaryRow = useMemo(() => {
    if (!Array.isArray(summary) || summary.length === 0) {
      return null;
    }
    const targetClientId =
      Number(selectedScopeClientId || clientPk) || null;
    if (Number.isFinite(targetClientId)) {
      const byId = summary.find(
        (row = {}) => Number(row.client_id) === targetClientId
      );
      if (byId) {
        return byId;
      }
    }
    return summary[0] || null;
  }, [clientPk, selectedScopeClientId, summary]);

  const activeIngestionHealth = activeSummaryRow?.ingestion_health || null;
  const activeScopeClient = useMemo(() => {
    if (!selectedScopeClientId) {
      return null;
    }
    const targetId = Number(selectedScopeClientId);
    if (!Number.isFinite(targetId)) {
      return null;
    }
    return (
      scopeClients.find((row = {}) => Number(row.id) === targetId) || null
    );
  }, [scopeClients, selectedScopeClientId]);

  const backendDataUpdatedDate = useMemo(() => {
    const tasks = Array.isArray(activeIngestionHealth?.tasks)
      ? activeIngestionHealth.tasks
      : [];
    const backendTask = tasks.find((task) => task?.name === 'backend_data');
    if (backendTask?.max_created_date) {
      return backendTask.max_created_date;
    }
    const fallbackLastBackendUpdate =
      scopeType === 'parent'
        ? activeScopeClient?.last_backend_update
        : client?.last_backend_update;
    const fallbackLabel = formatIsoInEST(fallbackLastBackendUpdate);
    if (fallbackLabel) {
      return fallbackLabel;
    }
    return null;
  }, [
    activeIngestionHealth,
    activeScopeClient?.last_backend_update,
    client?.last_backend_update,
    scopeType,
  ]);

  const latestProcessedAtLabel = useMemo(() => {
    if (!latestProcessedCsv?.processed_at) {
      return null;
    }
    return formatIsoInEST(latestProcessedCsv.processed_at);
  }, [latestProcessedCsv]);

  useEffect(() => {
    leadRequestCacheRef.current.clear();
    leadInFlightRequestRef.current.clear();
  }, [leadScopeParams, backendScopeParams, dataRefreshToken, startDate, endDate]);

  useEffect(() => {
    let isMounted = true;

    if (!activeProcessedCsvClientId) {
      setLatestProcessedCsv(null);
      setLatestProcessedCsvError(null);
      setLatestProcessedCsvLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const loadLatestProcessedCsv = async () => {
      setLatestProcessedCsvLoading(true);
      setLatestProcessedCsvError(null);
      try {
        const res = await api.request({
          url: `dashboard/client-processed-csv/latest/?client=${activeProcessedCsvClientId}`,
          method: 'GET',
        });
        if (!isMounted) {
          return;
        }
        setLatestProcessedCsv(res?.data?.id ? res.data : null);
      } catch (err) {
        if (!isMounted) {
          return;
        }
        console.error('Failed to load latest processed CSV', err);
        setLatestProcessedCsv(null);
        setLatestProcessedCsvError('Unable to load latest processed CSV.');
      } finally {
        if (isMounted) {
          setLatestProcessedCsvLoading(false);
        }
      }
    };

    loadLatestProcessedCsv();
    return () => {
      isMounted = false;
    };
  }, [activeProcessedCsvClientId, dataRefreshToken]);

  const territoryVerticalOptions = useMemo(() => {
    const map = new Map();
    territoryCampaigns.forEach((row = {}) => {
      const rawId = row.vertical_fk ?? row.vertical_fk_id;
      if (rawId === null || rawId === undefined) {
        return;
      }
      const id = Number(rawId);
      if (Number.isNaN(id)) {
        return;
      }
      if (map.has(id)) {
        return;
      }
      const name = (row.vertical || '').trim() || 'Unspecified';
      map.set(id, { id, name });
    });
    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [territoryCampaigns]);

  const filteredTerritoryCampaigns = useMemo(() => {
    if (!territoryVerticalFilter.length) {
      return territoryCampaigns;
    }
    const allowed = new Set(territoryVerticalFilter);
    return territoryCampaigns.filter((row = {}) => {
      const rawId = row.vertical_fk ?? row.vertical_fk_id;
      if (rawId === null || rawId === undefined) {
        return false;
      }
      const id = Number(rawId);
      if (Number.isNaN(id)) {
        return false;
      }
      return allowed.has(id);
    });
  }, [territoryCampaigns, territoryVerticalFilter]);

  const territories = useMemo(
    () =>
      aggregateMetricRows(filteredTerritoryCampaigns, {
        labelKeys: ['client_territory__name', 'territory_name'],
        fallbackLabel: 'Unknown Territory',
      }),
    [filteredTerritoryCampaigns]
  );

  const fetchCachedLeadPage = useCallback(async (url, { force = false } = {}) => {
    if (!url) {
      return { rows: [], next: null, prev: null, count: 0 };
    }

    if (!force && leadRequestCacheRef.current.has(url)) {
      return leadRequestCacheRef.current.get(url);
    }

    if (!force && leadInFlightRequestRef.current.has(url)) {
      return leadInFlightRequestRef.current.get(url);
    }

    const requestPromise = api
      .request({ url, method: 'GET' })
      .then((res) => {
        const { results = [], next, previous, count } = res?.data || {};
        const payload = {
          rows: results,
          next: next || null,
          prev: previous || null,
          count: count || 0,
        };
        leadRequestCacheRef.current.set(url, payload);
        return payload;
      })
      .finally(() => {
        leadInFlightRequestRef.current.delete(url);
      });

    leadInFlightRequestRef.current.set(url, requestPromise);
    return requestPromise;
  }, []);

  const fetchLeads = useCallback(
    async (pageParam = page, { force = false } = {}) => {
      if (!leadScopeParams) {
        return;
      }
      setLeadsLoading(true);
      try {
        const { phone, email, id, ...rest } = filters;
        const params = new URLSearchParams({
          page: pageParam,
          page_size: pageSize,
          fields: MAIN_LEAD_FIELDS,
          ...leadScopeParams,
          ...Object.fromEntries(Object.entries(rest).filter(([_, v]) => v !== '')),
        });
        const searchValue = phone || email || id;
        if (searchValue) {
          params.set('search', searchValue);
        }
        const { rows, count } = await fetchCachedLeadPage(`dashboard/leads/?${params.toString()}`, {
          force,
        });
        setLeads(rows);
        setTotalPages(Math.max(1, Math.ceil((count || 0) / pageSize)));
      } catch (err) {
        console.error('Error fetching leads:', err);
      } finally {
        setLeadsLoading(false);
      }
    },
    [filters, leadScopeParams, page, fetchCachedLeadPage]
  );

  useEffect(() => {
    if (!leadScopeParams) {
      return;
    }
    fetchLeads(page);
  }, [page, leadScopeParams]);

  useEffect(() => {
    setCityPage(0);
  }, [cities]);

  useEffect(() => {
    setCountyPage(0);
  }, [counties]);

  useEffect(() => {
    setPage(1);
    setMomRowDetails({});
    setDodRowDetails({});
    setVerticalDetails({});
    setChannelDetails({});
    setCampaignDetails({});
  }, [selectedScopeClientId]);

  const handleInputChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearch = useCallback(() => {
    const newPage = 1;
    if (page !== newPage) {
      setPage(newPage);
      return;
    }
    fetchLeads(newPage);
  }, [fetchLeads, page]);

  const handleDownloadLatestProcessedCsv = useCallback(async () => {
    if (!latestProcessedCsv?.id) {
      return;
    }
    setCsvDownloadLoading(true);
    try {
      const res = await api.request({
        url: `dashboard/client-processed-csv/${latestProcessedCsv.id}/download/`,
        method: 'GET',
        options: { responseType: 'blob' },
      });
      const disposition = res?.headers?.['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename\*?=(?:UTF-8''|")?([^\";]+)/i);
      const filename = filenameMatch
        ? decodeURIComponent(filenameMatch[1].replace(/"/g, '').trim())
        : latestProcessedCsv.original_filename || `processed-${latestProcessedCsv.id}.csv`;
      const blob = res?.data instanceof Blob ? res.data : new Blob([res?.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download latest processed CSV', err);
    } finally {
      setCsvDownloadLoading(false);
    }
  }, [latestProcessedCsv]);

  const formatToEST = useCallback(
    (utc) =>
      utc
        ? new Date(utc).toLocaleString('en-US', {
            timeZone: 'America/New_York'
          })
        : '-',
    []
  );

  const fetchSinglePage = useCallback(async (url) => {
    return fetchCachedLeadPage(url);
  }, [fetchCachedLeadPage]);

  const fetchMonthLeads = useCallback(
    async ({ startDate: rangeStart, endDate: rangeEnd } = {}, nextUrl = null, extraParams = {}) => {
      if (!leadScopeParams || (!rangeStart && !nextUrl)) return { rows: [], next: null, prev: null, count: 0 };
      const baseUrl =
        nextUrl ||
        (() => {
          const params = new URLSearchParams({
            date_after: formatDateToYMD(rangeStart),
            date_before: formatDateToYMD(rangeEnd),
            page_size: 50,
            fields: DRILLDOWN_LEAD_FIELDS,
            ...leadScopeParams,
          });
          Object.entries(extraParams || {}).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== '') {
              params.set(k, v);
            }
          });
          return `dashboard/leads/?${params.toString()}`;
        })();
      return fetchSinglePage(baseUrl);
    },
    [leadScopeParams, fetchSinglePage]
  );

  const fetchMonthBackendData = useCallback(
    async ({ startDate: rangeStart, endDate: rangeEnd } = {}, nextUrl = null, dateField = 'sold_date', extraParams = {}) => {
      if (!backendScopeParams || (!rangeStart && !nextUrl)) return { rows: [], next: null, prev: null, count: 0 };
      const baseUrl =
        nextUrl ||
        (() => {
          const params = new URLSearchParams({
            date_field: dateField,
            date_after: formatDateToYMD(rangeStart),
            date_before: formatDateToYMD(rangeEnd),
            ordering: `-${dateField}`,
            page_size: BACKEND_PAGE_SIZE,
            ...backendScopeParams,
          });
          Object.entries(extraParams || {}).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== '') {
              params.set(k, v);
            }
          });
          return `dashboard/backend-data/?${params.toString()}`;
        })();
      return fetchSinglePage(baseUrl);
    },
    [backendScopeParams, fetchSinglePage]
  );

  const mergeBackendRows = (soldRows = [], apptRows = []) => {
    const mergedMap = new Map();
    [...soldRows, ...apptRows].forEach((row = {}) => {
      const key =
        row.unique_id ||
        row.id ||
        row.prospect_id ||
        `${row.sold_date || ''}-${row.appointment_date || ''}-${row.email || ''}-${row.phone || ''}`;
      if (!mergedMap.has(key)) {
        mergedMap.set(key, row);
      }
    });
    return Array.from(mergedMap.values());
  };

  // Shared helpers used across drilldowns
  const normalizeValue = (value = '') => (value || '').toString().trim().toLowerCase();

  const matchesVerticalName = (row = {}, target = '') => {
    const normalized = normalizeValue(target);
    if (!normalized) return false;
    const candidates = [
      row.vertical,
      row.channel_vertical,
      row.territory_vertical,
      row.client_zip_vertical,
      row.vertical_name,
    ];
    return candidates.some((val) => normalizeValue(val) === normalized);
  };

  const matchesChannelName = (row = {}, target = '') => {
    const normalized = normalizeValue(target);
    if (!normalized) return false;
    const candidates = [
      row.channel__name,
      row.channel_name,
      row.channel,
      row.source_channel,
      row.source,
      row.campaign_channel,
    ];
    return candidates.some((val) => normalizeValue(val) === normalized);
  };

  const getChannelId = (row = {}) =>
    row.channel || row.channel_id || row.channel_pk || row.channel__id;

  const matchesChannel = (row = {}, targetName = '', targetId = null) => {
    const rowId = Number(getChannelId(row));
    if (Number.isFinite(rowId) && Number.isFinite(targetId) && rowId === targetId) {
      return true;
    }
    return matchesChannelName(row, targetName);
  };

  const matchesCampaignName = (row = {}, target = '') => {
    const normalized = normalizeValue(target);
    if (!normalized) return false;
    const candidates = [
      row.campaign_name,
      row.campaign__name,
      row.campaign,
      row.campaign_title,
      row.source_campaign,
    ];
    return candidates.some((val) => normalizeValue(val) === normalized);
  };

  const getCampaignId = (row = {}) =>
    row.campaign_id || row.campaign_pk || row.campaign__id;

  const matchesCampaign = (row = {}, targetName = '', targetId = null) => {
    const rowId = Number(getCampaignId(row));
    if (Number.isFinite(rowId) && Number.isFinite(targetId) && rowId === targetId) {
      return true;
    }
    return matchesCampaignName(row, targetName);
  };

  const handleToggleMomDetails = useCallback(
    async (monthLabel, summaryRow = null) => {
      if (!monthLabel) return;
      const current = momRowDetails[monthLabel];
      if (current?.expanded && !current?.loading) {
        setMomRowDetails((prev) => ({
          ...prev,
          [monthLabel]: { ...prev[monthLabel], expanded: false },
        }));
        return;
      }

      if (current?.backendLoaded || current?.leadsLoaded) {
        setMomRowDetails((prev) => ({
          ...prev,
          [monthLabel]: { ...(prev[monthLabel] || {}), expanded: true },
        }));
        return;
      }

      setMomRowDetails((prev) => ({
        ...prev,
        [monthLabel]: { ...(prev[monthLabel] || {}), expanded: true, loading: true, error: null },
      }));

      const range = parseMonthRange(monthLabel);
      if (!range || !payload) {
        setMomRowDetails((prev) => ({
          ...prev,
          [monthLabel]: {
            ...(prev[monthLabel] || {}),
            expanded: true,
            loading: false,
            error: 'Could not resolve month range for this row.',
          },
        }));
        return;
      }

      try {
        const [soldResp, apptResp] = await Promise.all([
          fetchMonthBackendData(range, null, 'sold_date', { page: 1 }),
          fetchMonthBackendData(range, null, 'appointment_date', { page: 1 }),
        ]);
        const mergedBackend = mergeBackendRows(soldResp.rows || [], apptResp.rows || []);
        const backendCount = Math.max(soldResp.count || 0, apptResp.count || 0, mergedBackend.length);
        const summary = summaryRow || momData.find((item) => item?.month === monthLabel) || {};
        const backendTotals = buildBackendTotalsFromSummary(summary);

        setMomRowDetails((prev) => ({
          ...prev,
          [monthLabel]: {
            expanded: true,
            loading: false,
            error: null,
            activeTab: 'backend',
            leads: [],
            backend: mergedBackend,
            backendNext: soldResp.next || apptResp.next || null,
            backendPrev: soldResp.prev || apptResp.prev || null,
            leadsCount: 0,
            backendCount,
            backendTotals,
            backendFilters: {},
            leadsFilters: {},
            leadsPage: 0,
            backendPage: 0,
            dateRange: range,
            leadsLoaded: false,
            backendLoaded: true,
            },
        }));
      } catch (err) {
        console.error('Failed loading month drilldown', err);
        setMomRowDetails((prev) => ({
          ...prev,
          [monthLabel]: {
            ...(prev[monthLabel] || {}),
            expanded: true,
            loading: false,
            error: 'Unable to load records for this month.',
          },
        }));
      }
    },
    [fetchMonthBackendData, momData, momRowDetails, payload]
  );

  const handleMomPageChange = useCallback(
    async (monthLabel, type, newPage) => {
      const detail = momRowDetails[monthLabel];
      if (!detail || detail.loading) return;
      const isLeads = type === 'leads';
      const range = detail.dateRange || currentRange;

      setMomRowDetails((prev) => ({
        ...prev,
        [monthLabel]: { ...(prev[monthLabel] || {}), loading: true },
      }));

      try {
        if (isLeads) {
          const { rows, next, prev, count } = await fetchMonthLeads(range, null, { page: newPage + 1 });
          setMomRowDetails((prev) => {
            const existing = prev[monthLabel] || {};
            return {
              ...prev,
              [monthLabel]: {
                ...existing,
                loading: false,
                leads: rows,
                leadsNext: next,
                leadsPrev: prev,
                leadsCount: count ?? rows.length,
                leadsPage: newPage,
                leadsLoaded: true,
              },
            };
          });
        } else {
          const [soldResp, apptResp] = await Promise.all([
            fetchMonthBackendData(range, null, 'sold_date', { page: newPage + 1 }),
            fetchMonthBackendData(range, null, 'appointment_date', { page: newPage + 1 }),
          ]);
          const mergedBackend = mergeBackendRows(soldResp.rows || [], apptResp.rows || []);
          const backendCount = Math.max(soldResp.count || 0, apptResp.count || 0, mergedBackend.length);
          setMomRowDetails((prev) => {
            const existing = prev[monthLabel] || {};
            return {
              ...prev,
              [monthLabel]: {
                ...existing,
                loading: false,
                backend: mergedBackend,
                backendNext: soldResp.next || apptResp.next || null,
                backendPrev: soldResp.prev || apptResp.prev || null,
                backendCount,
                backendPage: newPage,
                backendLoaded: true,
              },
            };
          });
        }
      } catch (err) {
        console.error('Failed loading month drilldown page', err);
        setMomRowDetails((prev) => ({
          ...prev,
          [monthLabel]: { ...(prev[monthLabel] || {}), loading: false },
        }));
      }
    },
    [currentRange, fetchMonthBackendData, fetchMonthLeads, mergeBackendRows, momRowDetails]
  );

  const handleToggleDayDetails = useCallback(
    async (dateKey, summaryRow = null) => {
      if (!dateKey) return;
      const current = dodRowDetails[dateKey];
      if (current?.expanded && !current?.loading) {
        setDodRowDetails((prev) => ({
          ...prev,
          [dateKey]: { ...prev[dateKey], expanded: false },
        }));
        return;
      }

      if (current?.backendLoaded || current?.leadsLoaded) {
        setDodRowDetails((prev) => ({
          ...prev,
          [dateKey]: { ...(prev[dateKey] || {}), expanded: true },
        }));
        return;
      }

      setDodRowDetails((prev) => ({
        ...prev,
        [dateKey]: { ...(prev[dateKey] || {}), expanded: true, loading: true, error: null },
      }));

      const range = buildSingleDayRange(dateKey);
      if (!range || !payload) {
        setDodRowDetails((prev) => ({
          ...prev,
          [dateKey]: {
            ...(prev[dateKey] || {}),
            expanded: true,
            loading: false,
            error: 'Could not resolve date range for this day.',
          },
        }));
        return;
      }

      try {
        const [soldResp, apptResp] = await Promise.all([
          fetchMonthBackendData(range, null, 'sold_date', { page: 1 }),
          fetchMonthBackendData(range, null, 'appointment_date', { page: 1 }),
        ]);
        const mergedBackend = mergeBackendRows(soldResp.rows || [], apptResp.rows || []);
        const backendCount = Math.max(soldResp.count || 0, apptResp.count || 0, mergedBackend.length);
        const summary = summaryRow || {};
        const backendTotals = buildBackendTotalsFromSummary(summary);

        setDodRowDetails((prev) => ({
          ...prev,
          [dateKey]: {
            expanded: true,
            loading: false,
            error: null,
            activeTab: 'backend',
            leads: [],
            backend: mergedBackend,
            backendNext: soldResp.next || apptResp.next || null,
            backendPrev: soldResp.prev || apptResp.prev || null,
            leadsCount: 0,
            backendCount,
            backendTotals,
            backendFilters: {},
            leadsFilters: {},
            leadsPage: 0,
            backendPage: 0,
            dateRange: range,
            leadsLoaded: false,
            backendLoaded: true,
          },
        }));
      } catch (err) {
        console.error('Failed loading day drilldown', err);
        setDodRowDetails((prev) => ({
          ...prev,
          [dateKey]: {
            ...(prev[dateKey] || {}),
            expanded: true,
            loading: false,
            error: 'Unable to load records for this day.',
          },
        }));
      }
    },
    [dodRowDetails, fetchMonthBackendData, payload]
  );

  const handleDodTabChange = useCallback(
    async (dateKey, tab) => {
      if (!dateKey || !tab) return;
      const detail = dodRowDetails[dateKey];
      if (!detail || detail.loading) return;

      if (tab === 'leads' && !detail.leadsLoaded) {
        setDodRowDetails((prev) => ({
          ...prev,
          [dateKey]: { ...(prev[dateKey] || {}), activeTab: tab, loading: true },
        }));
        try {
          const { rows: leads, next, prev, count } = await fetchMonthLeads(detail.dateRange);
          setDodRowDetails((prev) => {
            const existing = prev[dateKey] || {};
            return {
              ...prev,
              [dateKey]: {
                ...existing,
                loading: false,
                activeTab: tab,
                leads,
                leadsNext: next,
                leadsPrev: prev,
                leadsCount: count,
                leadsLoaded: true,
              },
            };
          });
        } catch (err) {
          console.error('Failed to load day leads', err);
          setDodRowDetails((prev) => ({
            ...prev,
            [dateKey]: { ...(prev[dateKey] || {}), loading: false, error: 'Unable to load leads.' },
          }));
        }
      } else {
        setDodRowDetails((prev) => ({
          ...prev,
          [dateKey]: { ...(prev[dateKey] || {}), activeTab: tab },
        }));
      }
    },
    [dodRowDetails, fetchMonthLeads]
  );

  const handleDodPageChange = useCallback(
    async (dateKey, type, newPage) => {
      const detail = dodRowDetails[dateKey];
      if (!detail || detail.loading) return;
      const isLeads = type === 'leads';
      const range = detail.dateRange || currentRange;

      setDodRowDetails((prev) => ({
        ...prev,
        [dateKey]: { ...(prev[dateKey] || {}), loading: true },
      }));

      try {
        if (isLeads) {
          const { rows, next, prev, count } = await fetchMonthLeads(range, null, { page: newPage + 1 });
          setDodRowDetails((prev) => {
            const existing = prev[dateKey] || {};
            return {
              ...prev,
              [dateKey]: {
                ...existing,
                loading: false,
                leads: rows,
                leadsNext: next,
                leadsPrev: prev,
                leadsCount: count ?? rows.length,
                leadsPage: newPage,
                leadsLoaded: true,
              },
            };
          });
        } else {
          const [soldResp, apptResp] = await Promise.all([
            fetchMonthBackendData(range, null, 'sold_date', { page: newPage + 1 }),
            fetchMonthBackendData(range, null, 'appointment_date', { page: newPage + 1 }),
          ]);
          const mergedBackend = mergeBackendRows(soldResp.rows || [], apptResp.rows || []);
          const backendCount = Math.max(soldResp.count || 0, apptResp.count || 0, mergedBackend.length);
          setDodRowDetails((prev) => {
            const existing = prev[dateKey] || {};
            return {
              ...prev,
              [dateKey]: {
                ...existing,
                loading: false,
                backend: mergedBackend,
                backendNext: soldResp.next || apptResp.next || null,
                backendPrev: soldResp.prev || apptResp.prev || null,
                backendCount,
                backendPage: newPage,
                backendLoaded: true,
              },
            };
          });
        }
      } catch (err) {
        console.error('Failed paging day drilldown', err);
        setDodRowDetails((prev) => ({
          ...prev,
          [dateKey]: { ...(prev[dateKey] || {}), loading: false },
        }));
      }
    },
    [currentRange, dodRowDetails, fetchMonthBackendData, fetchMonthLeads, mergeBackendRows]
  );

  const handleToggleVerticalRow = useCallback(
    async (verticalName) => {
      if (!verticalName) return;
      const current = verticalDetails[verticalName];
      if (current?.loading) return;
      if (current?.expanded && !current?.loading) {
        setVerticalDetails((prev) => ({
          ...prev,
          [verticalName]: { ...prev[verticalName], expanded: false },
        }));
        return;
      }

      // If we already loaded this vertical before, just expand without refetching.
      if (current?.backendLoaded || current?.leadsLoaded) {
        setVerticalDetails((prev) => ({
          ...prev,
          [verticalName]: { ...(prev[verticalName] || {}), expanded: true },
        }));
        return;
      }

      setVerticalDetails((prev) => ({
        ...prev,
        [verticalName]: { ...(prev[verticalName] || {}), expanded: true, loading: true, error: null },
      }));

      if (!payload) {
        setVerticalDetails((prev) => ({
          ...prev,
          [verticalName]: { ...(prev[verticalName] || {}), expanded: true, loading: false, error: 'Missing client id.' },
        }));
        return;
      }

      try {
        const filters = { vertical: verticalName };
        const [soldResp, apptResp] = await Promise.all([
          fetchMonthBackendData(currentRange, null, 'sold_date', filters),
          fetchMonthBackendData(currentRange, null, 'appointment_date', filters),
        ]);
        const soldRows = soldResp.rows || soldResp || [];
        const apptRows = apptResp.rows || apptResp || [];
        const mergedBackend = mergeBackendRows(soldRows, apptRows).filter((row) => matchesVerticalName(row, verticalName));
        const backendCount = Math.max(soldResp.count || 0, apptResp.count || 0, mergedBackend.length);
        const leadsCount = Number(
          (verticals || []).find((row = {}) => normalizeValue(row.vertical) === normalizeValue(verticalName))
            ?.total_leads_sum
        ) || 0;
        setVerticalDetails((prev) => ({
          ...prev,
          [verticalName]: {
            expanded: true,
            loading: false,
            error: null,
            activeTab: 'backend',
            leads: [],
            backend: mergedBackend,
            backendFull: mergedBackend,
            leadsNext: null,
            leadsPrev: null,
            backendNext: null,
            backendPrev: null,
            leadsCount,
            backendCount,
            leadsFilters: { vertical: verticalName },
            backendFilters: filters,
            leadsPage: 0,
            backendPage: 0,
            dateRange: currentRange,
            leadsLoaded: false,
            backendLoaded: true,
          },
        }));
      } catch (err) {
        console.error('Failed loading vertical drilldown', err);
        setVerticalDetails((prev) => ({
          ...prev,
          [verticalName]: {
            ...(prev[verticalName] || {}),
            expanded: true,
            loading: false,
            error: 'Unable to load records for this vertical.',
          },
        }));
      }
    },
    [currentRange, fetchMonthBackendData, mergeBackendRows, payload, verticalDetails, verticals]
  );

  const handleVerticalTabChange = useCallback(
    async (verticalName, tab) => {
      if (!verticalName || !tab) return;
      const detail = verticalDetails[verticalName];
      if (!detail || detail.loading) return;

      if (tab === 'leads' && !detail.leadsLoaded) {
        setVerticalDetails((prev) => ({
          ...prev,
          [verticalName]: { ...(prev[verticalName] || {}), activeTab: tab, loading: true },
        }));
        try {
          const { rows, next, prev, count } = await fetchMonthLeads(
            detail.dateRange || currentRange,
            null,
            detail.leadsFilters || { vertical: verticalName }
          );
          const leads = (rows || []).filter((lead = {}) =>
            normalizeValue(lead.vertical) === normalizeValue(verticalName)
          );
          setVerticalDetails((prev) => ({
            ...prev,
            [verticalName]: {
              ...(prev[verticalName] || {}),
              activeTab: tab,
              loading: false,
              leads,
              leadsNext: next,
              leadsPrev: prev,
              leadsCount: count ?? leads.length,
              leadsLoaded: true,
              leadsPage: 0,
            },
          }));
        } catch (err) {
          console.error('Failed to load vertical leads', err);
          setVerticalDetails((prev) => ({
            ...prev,
            [verticalName]: { ...(prev[verticalName] || {}), loading: false, error: 'Unable to load leads.' },
          }));
        }
        return;
      }

      setVerticalDetails((prev) => ({
        ...prev,
        [verticalName]: { ...(prev[verticalName] || {}), activeTab: tab },
      }));
    },
    [currentRange, fetchMonthLeads, verticalDetails]
  );

  const handleVerticalPageChange = useCallback(
    async (verticalName, type, newPage) => {
      if (!verticalName) return;
      const detail = verticalDetails[verticalName];
      if (!detail) return;
      const range = detail.dateRange || currentRange;

      setVerticalDetails((prev) => ({
        ...prev,
        [verticalName]: { ...(prev[verticalName] || {}), loading: true },
      }));

      try {
        if (type === 'leads') {
          const { rows, next, prev, count } = await fetchMonthLeads(range, null, {
            ...(detail.leadsFilters || { vertical: verticalName }),
            page: newPage + 1,
          });
          setVerticalDetails((prev) => ({
            ...prev,
            [verticalName]: {
              ...(prev[verticalName] || {}),
              leads: rows,
              leadsNext: next,
              leadsPrev: prev,
              leadsCount: count ?? rows.length,
              leadsPage: newPage,
              loading: false,
            },
          }));
        } else {
          const backendFilters = detail.backendFilters || { vertical: verticalName };
          const [soldResp, apptResp] = await Promise.all([
            fetchMonthBackendData(range, null, 'sold_date', { ...backendFilters, page: newPage + 1 }),
            fetchMonthBackendData(range, null, 'appointment_date', { ...backendFilters, page: newPage + 1 }),
          ]);
          const mergedBackend = mergeBackendRows(soldResp.rows || soldResp || [], apptResp.rows || apptResp || []);
          const backendCount = Math.max(soldResp.count || 0, apptResp.count || 0, mergedBackend.length);
          setVerticalDetails((prev) => ({
            ...prev,
            [verticalName]: {
              ...(prev[verticalName] || {}),
              backend: mergedBackend,
              backendFull: mergedBackend,
              backendNext: soldResp.next || apptResp.next || null,
              backendPrev: soldResp.prev || apptResp.prev || null,
              backendCount,
              backendPage: newPage,
              loading: false,
            },
          }));
        }
      } catch (err) {
        console.error('Failed to page vertical drilldown', err);
        setVerticalDetails((prev) => ({
          ...prev,
          [verticalName]: { ...(prev[verticalName] || {}), loading: false, error: 'Unable to load page.' },
        }));
      }
    },
    [currentRange, fetchMonthBackendData, fetchMonthLeads, mergeBackendRows, verticalDetails]
  );

  const handleToggleChannelRow = useCallback(
    async (channelName, channelId = null) => {
      if (!channelName) return;
      const current = channelDetails[channelName];
      if (current?.loading) return;
      if (current?.expanded && !current?.loading) {
        setChannelDetails((prev) => ({
          ...prev,
          [channelName]: { ...prev[channelName], expanded: false },
        }));
        return;
      }

      // If already loaded once, just expand without refetching.
      if (current?.backendLoaded || current?.leadsLoaded) {
        setChannelDetails((prev) => ({
          ...prev,
          [channelName]: { ...(prev[channelName] || {}), expanded: true },
        }));
        return;
      }

      setChannelDetails((prev) => ({
        ...prev,
        [channelName]: { ...(prev[channelName] || {}), expanded: true, loading: true, error: null },
      }));

      if (!payload) {
        setChannelDetails((prev) => ({
          ...prev,
          [channelName]: { ...(prev[channelName] || {}), expanded: true, loading: false, error: 'Missing client id.' },
        }));
        return;
      }

      try {
        const filters = channelId ? { channel: channelId } : { channel: channelName };
        const [soldResp, apptResp] = await Promise.all([
          fetchMonthBackendData(currentRange, null, 'sold_date', filters),
          fetchMonthBackendData(currentRange, null, 'appointment_date', filters),
        ]);
        const soldRows = soldResp.rows || soldResp || [];
        const apptRows = apptResp.rows || apptResp || [];
        const mergedBackend = mergeBackendRows(soldRows, apptRows).filter((row) =>
          matchesChannel(row, channelName, channelId)
        );
        const backendCount = Math.max(soldResp.count || 0, apptResp.count || 0, mergedBackend.length);
        const leadsCount = Number(
          (channels || []).find((row = {}) => matchesChannel(row, channelName, channelId))?.total_leads_sum
        ) || 0;
        setChannelDetails((prev) => ({
          ...prev,
          [channelName]: {
            expanded: true,
            loading: false,
            error: null,
            activeTab: 'backend',
            leads: [],
            backend: mergedBackend,
            backendFull: mergedBackend,
            leadsNext: null,
            leadsPrev: null,
            backendNext: null,
            backendPrev: null,
            leadsCount,
            backendCount,
            leadsFilters: { source: channelName },
            backendFilters: filters,
            channelId,
            leadsPage: 0,
            backendPage: 0,
            dateRange: currentRange,
            leadsLoaded: false,
            backendLoaded: true,
          },
        }));
      } catch (err) {
        console.error('Failed loading channel drilldown', err);
        setChannelDetails((prev) => ({
          ...prev,
          [channelName]: {
            ...(prev[channelName] || {}),
            expanded: true,
            loading: false,
            error: 'Unable to load records for this channel.',
          },
        }));
      }
    },
    [channelDetails, channels, currentRange, fetchMonthBackendData, mergeBackendRows, payload]
  );

  const handleChannelTabChange = useCallback(
    async (channelName, tab) => {
      if (!channelName || !tab) return;
      const detail = channelDetails[channelName];
      if (!detail || detail.loading) return;

      if (tab === 'leads' && !detail.leadsLoaded) {
        setChannelDetails((prev) => ({
          ...prev,
          [channelName]: { ...(prev[channelName] || {}), activeTab: tab, loading: true },
        }));
        try {
          const { rows, next, prev, count } = await fetchMonthLeads(
            detail.dateRange || currentRange,
            null,
            detail.leadsFilters || { source: channelName }
          );
          const leads = (rows || []).filter((lead = {}) =>
            matchesChannel({ channel: lead.source || lead.channel }, channelName, detail.channelId)
          );
          setChannelDetails((prev) => ({
            ...prev,
            [channelName]: {
              ...(prev[channelName] || {}),
              activeTab: tab,
              loading: false,
              leads,
              leadsNext: next,
              leadsPrev: prev,
              leadsCount: count ?? leads.length,
              leadsLoaded: true,
              leadsPage: 0,
            },
          }));
        } catch (err) {
          console.error('Failed to load channel leads', err);
          setChannelDetails((prev) => ({
            ...prev,
            [channelName]: { ...(prev[channelName] || {}), loading: false, error: 'Unable to load leads.' },
          }));
        }
        return;
      }

      setChannelDetails((prev) => ({
        ...prev,
        [channelName]: { ...(prev[channelName] || {}), activeTab: tab },
      }));
    },
    [channelDetails, currentRange, fetchMonthLeads]
  );

  const handleChannelPageChange = useCallback(
    async (channelName, type, newPage) => {
      if (!channelName) return;
      const detail = channelDetails[channelName];
      if (!detail) return;
      const range = detail.dateRange || currentRange;

      setChannelDetails((prev) => ({
        ...prev,
        [channelName]: { ...(prev[channelName] || {}), loading: true },
      }));

      try {
        if (type === 'leads') {
          const { rows, next, prev, count } = await fetchMonthLeads(range, null, {
            ...(detail.leadsFilters || { source: channelName }),
            page: newPage + 1,
          });
          setChannelDetails((prev) => ({
            ...prev,
            [channelName]: {
              ...(prev[channelName] || {}),
              leads: rows,
              leadsNext: next,
              leadsPrev: prev,
              leadsCount: count ?? rows.length,
              leadsPage: newPage,
              loading: false,
            },
          }));
        } else {
          const backendFilters = detail.backendFilters || { channel: detail.channelId || channelName };
          const [soldResp, apptResp] = await Promise.all([
            fetchMonthBackendData(range, null, 'sold_date', { ...backendFilters, page: newPage + 1 }),
            fetchMonthBackendData(range, null, 'appointment_date', { ...backendFilters, page: newPage + 1 }),
          ]);
          const mergedBackend = mergeBackendRows(soldResp.rows || soldResp || [], apptResp.rows || apptResp || []);
          const backendCount = Math.max(soldResp.count || 0, apptResp.count || 0, mergedBackend.length);
          setChannelDetails((prev) => ({
            ...prev,
            [channelName]: {
              ...(prev[channelName] || {}),
              backend: mergedBackend,
              backendFull: mergedBackend,
              backendNext: soldResp.next || apptResp.next || null,
              backendPrev: soldResp.prev || apptResp.prev || null,
              backendCount,
              backendPage: newPage,
              loading: false,
            },
          }));
        }
      } catch (err) {
        console.error('Failed to page channel drilldown', err);
        setChannelDetails((prev) => ({
          ...prev,
          [channelName]: { ...(prev[channelName] || {}), loading: false, error: 'Unable to load page.' },
        }));
      }
    },
    [channelDetails, currentRange, fetchMonthBackendData, fetchMonthLeads, mergeBackendRows]
  );

  const handleToggleCampaignRow = useCallback(
    async (campaignKey, campaignName, campaignId = null) => {
      if (!campaignKey) return;
      const current = campaignDetails[campaignKey];
      if (current?.loading) return;
      if (current?.expanded && !current?.loading) {
        setCampaignDetails((prev) => ({
          ...prev,
          [campaignKey]: { ...prev[campaignKey], expanded: false },
        }));
        return;
      }

      if (current?.backendLoaded || current?.leadsLoaded) {
        setCampaignDetails((prev) => ({
          ...prev,
          [campaignKey]: { ...(prev[campaignKey] || {}), expanded: true },
        }));
        return;
      }

      setCampaignDetails((prev) => ({
        ...prev,
        [campaignKey]: { ...(prev[campaignKey] || {}), expanded: true, loading: true, error: null },
      }));

      if (!payload) {
        setCampaignDetails((prev) => ({
          ...prev,
          [campaignKey]: { ...(prev[campaignKey] || {}), expanded: true, loading: false, error: 'Missing client id.' },
        }));
        return;
      }

      try {
        const backendFilters = campaignId ? { campaign_id: campaignId } : { campaign: campaignName };
        const leadFilters = campaignId ? { campaign_id: campaignId } : { campaign: campaignName };
        const displayName = campaignId
          ? ((campaigns || []).find((c = {}) => Number(c.campaign_id ?? c.id) === Number(campaignId))?.campaign || campaignName)
          : campaignName;
        const targetIdNum = Number(campaignId);
        const [soldResp, apptResp] = await Promise.all([
          fetchMonthBackendData(currentRange, null, 'sold_date', backendFilters),
          fetchMonthBackendData(currentRange, null, 'appointment_date', backendFilters),
        ]);
        const soldRows = soldResp.rows || soldResp || [];
        const apptRows = apptResp.rows || apptResp || [];
        const mergedBackend = mergeBackendRows(soldRows, apptRows)
          .filter((row = {}) => {
            const rowId = Number(getCampaignId(row));
            if (Number.isFinite(targetIdNum)) {
              return Number.isFinite(rowId) ? rowId === targetIdNum : matchesCampaignName(row, campaignName);
            }
            return matchesCampaignName(row, campaignName);
          })
          .map((row = {}) => ({
            ...row,
            campaign_id: row.campaign_id ?? row.campaign ?? campaignId,
            campaign: displayName || row.campaign || campaignName,
            campaign_name: displayName || row.campaign_name || campaignName,
          }));
        const backendCount = Math.max(
          soldResp.count || 0,
          apptResp.count || 0,
          mergedBackend.length
        );
        const leadsCount = Number(
          (campaigns || []).find((row = {}) => {
            const rowId = Number(row.campaign_id ?? row.id);
            if (Number.isFinite(targetIdNum)) {
              return Number.isFinite(rowId)
                ? rowId === targetIdNum
                : matchesCampaignName(row, campaignName);
            }
            return matchesCampaignName(row, campaignName);
          })?.total_leads_sum
        ) || 0;
        setCampaignDetails((prev) => ({
          ...prev,
          [campaignKey]: {
            expanded: true,
            loading: false,
            error: null,
            activeTab: 'backend',
            leads: [],
            backend: mergedBackend,
            backendFull: mergedBackend,
            leadsNext: null,
            leadsPrev: null,
            backendNext: null,
            backendPrev: null,
            leadsCount,
            backendCount,
            leadsFilters: leadFilters,
            backendFilters: backendFilters,
            campaignId,
            campaignName: displayName,
            displayName,
            leadsPage: 0,
            backendPage: 0,
            dateRange: currentRange,
            leadsLoaded: false,
            backendLoaded: true,
          },
        }));
      } catch (err) {
        console.error('Failed loading campaign drilldown', err);
        setCampaignDetails((prev) => ({
          ...prev,
          [campaignKey]: {
            ...(prev[campaignKey] || {}),
            expanded: true,
            loading: false,
            error: 'Unable to load records for this campaign.',
          },
        }));
      }
    },
    [campaignDetails, campaigns, currentRange, fetchMonthBackendData, mergeBackendRows, payload]
  );

  const handleCampaignTabChange = useCallback(
    async (campaignKey, tab) => {
      if (!campaignKey || !tab) return;
      const detail = campaignDetails[campaignKey];
      if (!detail || detail.loading) return;
      const campaignName = detail.displayName || detail.campaignName || campaignKey.split('__')[0];
      const campaignId = detail.campaignId ?? null;
      const targetIdNum = Number(campaignId);

      if (tab === 'leads' && !detail.leadsLoaded) {
        setCampaignDetails((prev) => ({
          ...prev,
          [campaignKey]: { ...(prev[campaignKey] || {}), activeTab: tab, loading: true },
        }));
        try {
          const { rows, next, prev, count } = await fetchMonthLeads(
            detail.dateRange || currentRange,
            null,
            detail.leadsFilters || { campaign: campaignName, campaign_id: campaignId }
          );
          const leads = (rows || [])
            .filter((lead = {}) => {
              const rowId = Number(lead.campaign_id);
              if (Number.isFinite(targetIdNum)) {
                return Number.isFinite(rowId)
                  ? rowId === targetIdNum
                  : matchesCampaignName(lead, campaignName);
              }
              return matchesCampaignName(lead, campaignName);
            })
            .map((lead = {}) => ({
              ...lead,
              campaign_id: lead.campaign_id ?? campaignId,
              campaign: campaignName || lead.campaign,
              campaign_name: campaignName || lead.campaign_name || lead.campaign,
            }));
          setCampaignDetails((prev) => ({
            ...prev,
            [campaignKey]: {
              ...(prev[campaignKey] || {}),
              activeTab: tab,
              loading: false,
              leads,
              leadsNext: next,
              leadsPrev: prev,
              leadsCount: count ?? leads.length,
              leadsLoaded: true,
              leadsPage: 0,
            },
          }));
        } catch (err) {
          console.error('Failed to load campaign leads', err);
          setCampaignDetails((prev) => ({
            ...prev,
            [campaignKey]: { ...(prev[campaignKey] || {}), loading: false, error: 'Unable to load leads.' },
          }));
        }
        return;
      }

      setCampaignDetails((prev) => ({
        ...prev,
        [campaignKey]: { ...(prev[campaignKey] || {}), activeTab: tab },
      }));
    },
    [campaignDetails, currentRange, fetchMonthLeads]
  );

  const handleCampaignPageChange = useCallback(
    async (campaignKey, type, newPage) => {
      if (!campaignKey) return;
      const detail = campaignDetails[campaignKey];
      if (!detail) return;
      const range = detail.dateRange || currentRange;
      const campaignName = detail.displayName || detail.campaignName || campaignKey.split('__')[0];
      const campaignId = detail.campaignId ?? null;
      const targetIdNum = Number(campaignId);

      setCampaignDetails((prev) => ({
        ...prev,
        [campaignKey]: { ...(prev[campaignKey] || {}), loading: true },
      }));

      try {
        if (type === 'leads') {
          const { rows, next, prev, count } = await fetchMonthLeads(range, null, {
            ...(detail.leadsFilters || { campaign: campaignName, campaign_id: campaignId }),
            page: newPage + 1,
          });
          const leads = rows
            .filter((lead = {}) => {
              const rowId = Number(lead.campaign_id);
              if (Number.isFinite(targetIdNum)) {
                return Number.isFinite(rowId) ? rowId === targetIdNum : matchesCampaignName(lead, campaignName);
              }
              return matchesCampaignName(lead, campaignName);
            })
            .map((lead = {}) => ({
              ...lead,
              campaign_id: lead.campaign_id ?? campaignId,
              campaign: campaignName || lead.campaign,
              campaign_name: campaignName || lead.campaign_name || lead.campaign,
            }));
          setCampaignDetails((prev) => ({
            ...prev,
            [campaignKey]: {
              ...(prev[campaignKey] || {}),
              leads,
              leadsNext: next,
              leadsPrev: prev,
              leadsCount: count ?? leads.length,
              leadsPage: newPage,
              loading: false,
            },
          }));
        } else {
          const backendFilters = detail.backendFilters || { campaign: campaignName, campaign_id: campaignId };
          const [soldResp, apptResp] = await Promise.all([
            fetchMonthBackendData(range, null, 'sold_date', { ...backendFilters, page: newPage + 1 }),
            fetchMonthBackendData(range, null, 'appointment_date', { ...backendFilters, page: newPage + 1 }),
          ]);
          const mergedBackend = mergeBackendRows(soldResp.rows || soldResp || [], apptResp.rows || apptResp || [])
            .filter((row = {}) => {
              const rowId = Number(getCampaignId(row));
              if (Number.isFinite(targetIdNum)) {
                return Number.isFinite(rowId) ? rowId === targetIdNum : matchesCampaignName(row, campaignName);
              }
              return matchesCampaignName(row, campaignName);
            })
            .map((row = {}) => ({
              ...row,
              campaign_id: row.campaign_id ?? row.campaign ?? campaignId,
              campaign: campaignName || row.campaign,
              campaign_name: campaignName || row.campaign_name || row.campaign,
            }));
          const backendCount = Math.max(soldResp.count || 0, apptResp.count || 0, mergedBackend.length);
          setCampaignDetails((prev) => ({
            ...prev,
            [campaignKey]: {
              ...(prev[campaignKey] || {}),
              backend: mergedBackend,
              backendFull: mergedBackend,
              backendNext: soldResp.next || apptResp.next || null,
              backendPrev: soldResp.prev || apptResp.prev || null,
              backendCount,
              backendPage: newPage,
              loading: false,
            },
          }));
        }
      } catch (err) {
        console.error('Failed to page campaign drilldown', err);
        setCampaignDetails((prev) => ({
          ...prev,
          [campaignKey]: { ...(prev[campaignKey] || {}), loading: false, error: 'Unable to load page.' },
        }));
      }
    },
    [campaignDetails, currentRange, fetchMonthBackendData, fetchMonthLeads, mergeBackendRows]
  );

  // No additional backend sort handler needed (fixed on sold_date desc).

  // Backend drilldown always sorts by sold_date desc to keep logic simple and consistent.

  const handleDrilldownTabChange = useCallback(
    async (monthLabel, tab) => {
      const detail = momRowDetails[monthLabel];
      if (!detail) return;
      if (detail.activeTab === tab) return;

      if (tab === 'leads' && !detail.leadsLoaded) {
        setMomRowDetails((prev) => ({
          ...prev,
          [monthLabel]: { ...(prev[monthLabel] || {}), activeTab: tab, loading: true },
        }));
        try {
          const { rows, next, prev, count } = await fetchMonthLeads(detail.dateRange);
          setMomRowDetails((prev) => ({
            ...prev,
            [monthLabel]: {
              ...(prev[monthLabel] || {}),
              activeTab: tab,
              loading: false,
              leads: rows,
              leadsNext: next,
              leadsPrev: prev,
              leadsCount: count,
              leadsLoaded: true,
              leadsPage: 0,
            },
          }));
        } catch (err) {
          console.error('Failed to load leads for month drilldown', err);
          setMomRowDetails((prev) => ({
            ...prev,
            [monthLabel]: { ...(prev[monthLabel] || {}), activeTab: tab, loading: false, error: 'Unable to load leads' },
          }));
        }
        return;
      }

      setMomRowDetails((prev) => ({
        ...prev,
        [monthLabel]: { ...(prev[monthLabel] || {}), activeTab: tab },
      }));
    },
    [fetchMonthLeads, momRowDetails]
  );

  useEffect(() => {
    const loadScope = async () => {
      setError(null);
      setClient(null);
      setClientPk(null);
      setParentCompanyId(null);
      setScopeClients([]);
      setScopeType('client');
      setSelectedScopeClientId('');
      try {
        const resp = await api.request({
          url: `dashboard/clients/${clientSlug}/?fields=id,name,client_logo_file,client_logo_url,fees,slug,brand_color,parent_company,parent_company_name,last_backend_update`,
          method: 'GET'
        });
        setScopeType('client');
        setParentCompanyId(null);
        setScopeClients(resp?.data ? [resp.data] : []);
        setSelectedScopeClientId('');
        setClient(resp.data);
        setClientPk(resp.data.id);
        if (resp.data.slug && resp.data.slug !== clientSlug) {
          navigate(`/clients/${resp.data.slug}/dashboard`, { replace: true });
        }
        return;
      } catch (err) {
        // Not a client slug; fallback to parent-company slug.
      }

      try {
        const listResp = await api.request({
          url: 'dashboard/clients/?fields=id,name,slug,fees,parent_company,parent_company_name,last_backend_update&page_size=500',
          method: 'GET',
        });
        const rows = Array.isArray(listResp?.data?.results)
          ? listResp.data.results
          : Array.isArray(listResp?.data)
            ? listResp.data
            : [];
        const byParentSlug = rows.filter((row = {}) => {
          const parentName = (row.parent_company_name || '').trim();
          return parentName && normalizeSlug(parentName) === normalizeSlug(clientSlug);
        });
        if (!byParentSlug.length) {
          setError('Client or parent company not found.');
          return;
        }
        const first = byParentSlug[0];
        const parentId = first.parent_company;
        const parentName = first.parent_company_name;
        setScopeType('parent');
        setParentCompanyId(parentId);
        setScopeClients(byParentSlug);
        setSelectedScopeClientId('');
        setClient({
          id: `parent-${parentId}`,
          name: parentName,
          slug: normalizeSlug(parentName),
          fees: 0,
          brand_color: null,
        });
        setClientPk(null);
      } catch (err) {
        setError('Client or parent company not found.');
      }
    };

    loadScope();
  }, [clientSlug, navigate]);

  useEffect(() => {
    const loadBase = async () => {
      if (!payload) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const monthSource =
          monthStart instanceof Date ? monthStart : new Date(monthStart);
        const localToday = new Date();
        const pacingPayload = {
          month: monthSource.getUTCMonth() + 1,
          year: monthSource.getUTCFullYear(),
          target_date: formatDateToYMD(localToday),
          ...(selectedScopeClientId
            ? { client_id: selectedScopeClientId }
            : scopeType === 'parent'
            ? { parent_company_id: parentCompanyId }
            : { client_id: clientPk }),
        };
        const pacingPromise = fetchBudgetPacing(pacingPayload).catch((err) => {
          console.error('Failed to load budget pacing', err);
          return { data: [] };
        });
        const overallPacingPromise = fetchBudgetPacingOverall(pacingPayload).catch((err) => {
          console.error('Failed to load overall budget pacing', err);
          return { data: [] };
        });
        const segmentPacingPromise = fetchBudgetPacingSegments(pacingPayload).catch((err) => {
          console.error('Failed to load segment budget pacing', err);
          return { data: [] };
        });
        const ingestionPromise = fetchDailyPulseByClient(payload).catch((err) => {
          console.error('Failed to load ingestion health', err);
          return { data: [] };
        });
        const [summaryResp, pacingResp, overallPacingResp, segmentPacingResp, ingestionResp] = await Promise.all([
          fetchClientSummaryByDate(payload),
          pacingPromise,
          overallPacingPromise,
          segmentPacingPromise,
          ingestionPromise,
        ]);
        const summaryRows = Array.isArray(summaryResp?.data) ? summaryResp.data : [];
        const ingestionRows = Array.isArray(ingestionResp?.data)
          ? ingestionResp.data
          : Array.isArray(ingestionResp?.data?.results)
            ? ingestionResp.data.results
            : [];
        const ingestionByClient = new Map();
        ingestionRows.forEach((row = {}) => {
          const key = Number(row.client_id);
          if (!Number.isFinite(key)) {
            return;
          }
          if (ingestionByClient.has(key)) {
            return;
          }
          ingestionByClient.set(key, row.ingestion_health || null);
        });
        const enrichedSummary = summaryRows.map((row = {}) => {
          const key = Number(row.client_id);
          const ingested = Number.isFinite(key) ? ingestionByClient.get(key) : null;
          if (!ingested || row.ingestion_health) {
            return row;
          }
          return { ...row, ingestion_health: ingested };
        });
        setSummary(enrichedSummary);
        const pacingBudgets = Array.isArray(pacingResp?.data)
          ? pacingResp.data
          : [];
        const overallPacing = Array.isArray(overallPacingResp?.data)
          ? overallPacingResp.data
          : [];
        const segmentPacing = Array.isArray(segmentPacingResp?.data)
          ? segmentPacingResp.data
          : [];
        setBudgetPacingLookup(buildPacingLookup(pacingBudgets));
        setBudgetPacingRows(pacingBudgets);
        setOverallPacingRows(overallPacing);
        setSegmentPacingRowsCanonical(segmentPacing);
      } catch (err) {
        console.error(err);
        setError('Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };
    loadBase();
  }, [payload, dataRefreshToken, monthStart]);

  useEffect(() => {
    const loadBreakdowns = async () => {
      if (!payload || !shouldLoadBreakdowns) {
        return;
      }
      setBreakdownsLoading(true);
      try {
        const [
          channelResp,
          campaignResp,
          stateResp,
          cityResp,
          countyResp,
          dmaResp,
          territoryCampaignResp,
          verticalResp,
        ] = await Promise.all([
          fetchChannelSummaryByDate(payload),
          fetchDailyPulseByCampaign(payload),
          fetchDailyPulseByState(payload),
          fetchDailyPulseByCity(payload),
          fetchDailyPulseByCounty(payload),
          fetchDailyPulseByDMA(payload),
          fetchDailyPulseByTerritoryCampaign(payload),
          fetchDailyPulseByVertical(payload),
        ]);

        const rawChannels = Array.isArray(channelResp.data)
          ? channelResp.data
          : channelResp.data?.results || [];
        const reducedChannels = Object.values(
          rawChannels.reduce((acc, cur) => {
            const name = (cur.channel__name || '').trim();
            if (!name) {
              return acc;
            }
            if (!acc[name]) {
              acc[name] = { uuid: cur.uuid || name, channel__name: name };
            }
            const existing = acc[name];
            Object.entries(cur).forEach(([key, value]) => {
              if (key === 'channel__name' || key === 'uuid') {
                return;
              }
              const isMetricKey =
                typeof key === 'string' &&
                (key.endsWith('_sum') || key.endsWith('_count'));
              if (isMetricKey) {
                const numericValue = Number(value);
                if (!Number.isNaN(numericValue)) {
                  const currentValue = Number(existing[key]) || 0;
                  existing[key] = currentValue + numericValue;
                }
              } else if (existing[key] === undefined) {
                existing[key] = value;
              }
            });
            return acc;
          }, {})
        );
        setChannels(reducedChannels);

        const rawVerticals = Array.isArray(verticalResp.data)
          ? verticalResp.data
          : verticalResp.data?.results || [];

        const mergedVerticals = Object.values(
          rawVerticals.reduce((acc, cur) => {
            const name = (cur.vertical || '').trim();
            if (!name) {
              return acc;
            }
            if (!acc[name]) {
              acc[name] = { uuid: name, vertical: name };
            }
            const existing = acc[name];
            Object.entries(cur).forEach(([key, value]) => {
              if (key === 'vertical' || key === 'uuid') return;
              const num = Number(value) || 0;
              existing[key] = (existing[key] || 0) + num;
            });
            return acc;
          }, {})
        );

        setVerticals(mergedVerticals);
        setCampaigns(campaignResp.data);

        const stateData = Array.isArray(stateResp.data)
          ? stateResp.data
          : stateResp.data?.results || [];
        const mergedStates = aggregateMetricRows(stateData, {
          labelKeys: ['state', 'state__name', 'state__state', 'state_name'],
          fallbackLabel: 'Unknown State',
        });
        setStates(mergedStates);

        const dmaData = Array.isArray(dmaResp.data)
          ? dmaResp.data
          : dmaResp.data?.results || [];
        const mergedDmas = aggregateMetricRows(dmaData, {
          labelKeys: ['dma__name', 'dma', 'dma_name'],
          fallbackLabel: 'Unknown DMA',
        });
        setDmas(mergedDmas);

        const territoryCampaignData = Array.isArray(territoryCampaignResp.data)
          ? territoryCampaignResp.data
          : territoryCampaignResp.data?.results || [];
        setTerritoryCampaigns(territoryCampaignData);

        const cityData = Array.isArray(cityResp.data)
          ? cityResp.data
          : cityResp.data?.results || [];
        setCities(cityData);

        const countyData = Array.isArray(countyResp.data)
          ? countyResp.data
          : countyResp.data?.results || [];
        setCounties(countyData);
      } catch (err) {
        console.error(err);
      } finally {
        setBreakdownsLoading(false);
      }
    };
    loadBreakdowns();
  }, [payload, dataRefreshToken, shouldLoadBreakdowns]);

  useEffect(() => {
    const loadMom = async () => {
      if (!payload || !shouldLoadMom) {
        return;
      }
      setMomLoading(true);
      try {
        const [sixStart, sixEnd] = getLastSixMonthsRange();
        const momPayload = {
          ...(scopeType === 'parent'
            ? { parent_company_id: parentCompanyId }
            : { client_id: clientPk }),
          date_after: formatDateToYMD(sixStart),
          date_before: formatDateToYMD(sixEnd),
        };
        const summaryMomResp = await fetchClientSummaryByDate(momPayload);
        const daily = Array.isArray(summaryMomResp.data) ? summaryMomResp.data : [];
        const grouped = daily.reduce((acc, cur) => {
          const d = new Date(cur.date);
          const year = d.getUTCFullYear();
          const month = d.getUTCMonth();
          const key = `${year}-${month}`;
          if (!acc[key]) {
            acc[key] = {
              year,
              month,
              total_spend_sum: 0,
              total_leads_sum: 0,
              self_books_sum: 0,
              accepted_sum: 0,
              success_sum: 0,
              out_of_areas_count_sum: 0,
              booked_sum: 0,
              issues_sum: 0,
              scheduled_sum: 0,
              demos_sum: 0,
              gross_sales_sum: 0,
              net_sales_sum: 0,
              net_amount_sum: 0,
            };
          }
          const g = acc[key];
          g.total_spend_sum += cur.total_spend_sum || 0;
          g.total_leads_sum += cur.total_leads_sum || 0;
          g.self_books_sum += cur.self_books_sum || 0;
          g.accepted_sum += cur.accepted_sum || 0;
          g.success_sum += cur.success_sum || 0;
          g.out_of_areas_count_sum += cur.out_of_areas_count_sum || 0;
          g.booked_sum += cur.booked_sum || 0;
          g.issues_sum += cur.issues_sum || 0;
          g.scheduled_sum += cur.scheduled_sum || 0;
          g.demos_sum += cur.demos_sum || 0;
          g.gross_sales_sum += cur.gross_sales_sum || cur.gross_sold_sum || 0;
          g.net_sales_sum += cur.net_sales_sum || cur.net_sold_sum || 0;
          g.net_amount_sum += cur.net_amount_sum || 0;
          return acc;
        }, {});
        const months = Object.values(grouped)
          .sort((a, b) => new Date(b.year, b.month) - new Date(a.year, a.month))
          .map((m) => ({
            ...m,
            month: new Date(m.year, m.month, 1).toLocaleString('default', {
              month: 'short',
              year: 'numeric',
            }),
          }));
        setMomData(months);
      } catch (err) {
        console.error('Failed to load MoM data.', err);
      } finally {
        setMomLoading(false);
      }
    };
    loadMom();
  }, [payload, dataRefreshToken, shouldLoadMom]);

  const safeDiv = (a, b) => (b ? a / b : 0);
  const feeRate = Number(client?.fees) || 0;
  const withFees = (label) =>
    showFee && label === 'Spend' ? `${label} (incl. fees)` : label;
  const renderBreakdownsLoadingRow = useCallback(
    (colSpan = 24) => {
      if (!shouldLoadBreakdowns || !breakdownsLoading) {
        return null;
      }
      return (
        <TableRow>
          <TableCell colSpan={colSpan} align="center">
            Loading breakdowns…
          </TableCell>
        </TableRow>
      );
    },
    [shouldLoadBreakdowns, breakdownsLoading]
  );

  const backfillMessage = useMemo(() => {
    if (backfillProgress) {
      const total = backfillProgress.expected_days ?? backfillProgress.tasks_found ?? null;
      const succeeded = backfillProgress.succeeded ?? 0;
      const failed = backfillProgress.failed ?? 0;
      const etaSeconds = backfillProgress.eta_seconds;
      const parts = [];
      if (total !== null) {
        parts.push(`${succeeded}/${total} done`);
      }
      if (failed) {
        parts.push(`${failed} failed`);
      }
      if (etaSeconds) {
        const mins = Math.max(1, Math.round(etaSeconds / 60));
        parts.push(`ETA ~${mins}m`);
      }
      const stateLabel =
        backfillProgress.state === 'success'
          ? 'Completed'
          : backfillProgress.state === 'error'
            ? 'Failed'
            : 'Refreshing';
      return {
        type:
          backfillProgress.state === 'error'
            ? 'error'
            : backfillProgress.state === 'success'
              ? 'success'
              : 'info',
        message: [stateLabel, parts.join(' • ')].filter(Boolean).join(': '),
      };
    }
    if (backfillStatus) {
      return backfillStatus;
    }
    return null;
  }, [backfillProgress, backfillStatus]);

  const selectedRangeLabel = useMemo(() => {
    const start = formatDateToYMD(startDate);
    const end = formatDateToYMD(endDate);
    if (start && end) {
      return `${start} to ${end}`;
    }
    return 'Date range not set';
  }, [startDate, endDate]);

  const getMetricValue = useCallback((row = {}, keys = []) => {
    for (const key of keys) {
      if (row[key] !== undefined && row[key] !== null) {
        const value = Number(row[key]);
        if (!Number.isNaN(value)) {
          return value;
        }
      }
    }
    return 0;
  }, []);

  const calculateTotals = useCallback(
    (rows = []) => {
      const totals = (Array.isArray(rows) ? rows : []).reduce(
        (acc, row = {}) => ({
          baseSpend: acc.baseSpend + getMetricValue(row, ['total_spend_sum']),
          leads: acc.leads + getMetricValue(row, ['total_leads_sum']),
          selfBook: acc.selfBook + getMetricValue(row, ['self_books_sum']),
          accepted: acc.accepted + getMetricValue(row, ['accepted_sum']),
          success: acc.success + getMetricValue(row, ['success_sum']),
          outOfArea: acc.outOfArea + getMetricValue(row, ['out_of_areas_count_sum']),
          booked: acc.booked + getMetricValue(row, ['booked_sum']),
          issues: acc.issues + getMetricValue(row, ['issues_sum']),
          scheduled: acc.scheduled + getMetricValue(row, ['scheduled_sum']),
          demos: acc.demos + getMetricValue(row, ['demos_sum']),
          grossSold: acc.grossSold + getMetricValue(row, ['gross_sales_sum', 'gross_sold_sum']),
          netSold: acc.netSold + getMetricValue(row, ['net_sales_sum', 'net_sold_sum']),
          netAmount: acc.netAmount + getMetricValue(row, ['net_amount_sum']),
        }),
        {
          baseSpend: 0,
          leads: 0,
          selfBook: 0,
          accepted: 0,
          success: 0,
          outOfArea: 0,
          booked: 0,
          issues: 0,
          scheduled: 0,
          demos: 0,
          grossSold: 0,
          netSold: 0,
          netAmount: 0,
        }
      );

      const spend = showFee ? totals.baseSpend * (1 + feeRate) : totals.baseSpend;

      return {
        ...totals,
        spend,
      };
    },
    [feeRate, getMetricValue, showFee]
  );

  const tooltipFormatter = (value, name, { dataKey }) => {
    if (spendSeriesKeys.has(dataKey)) {
      return [formatTooltipCurrency(value), name];
    }

    const numeric = Number(value);
    return Number.isFinite(numeric) ? [numeric.toLocaleString('en-US'), name] : [value, name];
  };

  const {
    totalSpend,
    totalLeads,
    totalBooked,
    totalIssues,
    totalNetSales,
    netContractAmount,
    cpl,
    cpBooked,
    cpIssue,
    cpNetSale,
    costOfMarketing
  } = useMemo(() => {
    const data = Array.isArray(summary) ? summary : [];
    const sum = (key) => data.reduce((acc, cur) => acc + (cur[key] || 0), 0);
    const spend = sum('total_spend_sum');
    const leads = sum('total_leads_sum');
    const booked = sum('booked_sum');
    const issues = sum('issues_sum');
    const netSales = sum('net_sales_sum');
    const netAmount = sum('net_amount_sum');
    return {
      totalSpend: spend,
      totalLeads: leads,
      totalBooked: booked,
      totalIssues: issues,
      totalNetSales: netSales,
      netContractAmount: netAmount,
      cpl: safeDiv(spend, leads),
      cpBooked: safeDiv(spend, booked),
      cpIssue: safeDiv(spend, issues),
      cpNetSale: safeDiv(spend, netSales),
      costOfMarketing: safeDiv(spend, netAmount)
    };
  }, [summary]);

  const parentClientRows = useMemo(() => {
    if (scopeType !== 'parent') {
      return [];
    }

    const byId = new Map();
    scopeClients.forEach((row = {}) => {
      const id = Number(row.id);
      if (!Number.isFinite(id)) {
        return;
      }
      if (selectedScopeClientId && String(id) !== String(selectedScopeClientId)) {
        return;
      }
      byId.set(id, {
        id,
        name: row.name || `Client ${id}`,
        slug: row.slug || null,
        feeRate: Number(row.fees || 0),
        total_spend_sum: 0,
        total_leads_sum: 0,
        self_books_sum: 0,
        accepted_sum: 0,
        success_sum: 0,
        out_of_areas_count_sum: 0,
        booked_sum: 0,
        issues_sum: 0,
        scheduled_sum: 0,
        demos_sum: 0,
        gross_sales_sum: 0,
        net_sales_sum: 0,
        net_amount_sum: 0,
        leads: 0,
      });
    });

    (summary || []).forEach((row = {}) => {
      const id = Number(row.client_id);
      if (!Number.isFinite(id)) {
        return;
      }
      const bucket = byId.get(id);
      if (!bucket) {
        return;
      }
      bucket.total_spend_sum += Number(row.total_spend_sum || 0);
      bucket.total_leads_sum += Number(row.total_leads_sum || 0);
      bucket.self_books_sum += Number(row.self_books_sum || 0);
      bucket.accepted_sum += Number(row.accepted_sum || 0);
      bucket.success_sum += Number(row.success_sum || 0);
      bucket.out_of_areas_count_sum += Number(row.out_of_areas_count_sum || 0);
      bucket.booked_sum += Number(row.booked_sum || 0);
      bucket.issues_sum += Number(row.issues_sum || 0);
      bucket.scheduled_sum += Number(row.scheduled_sum || 0);
      bucket.demos_sum += Number(row.demos_sum || 0);
      bucket.gross_sales_sum += Number(row.gross_sales_sum || row.gross_sold_sum || 0);
      bucket.net_sales_sum += Number(row.net_sales_sum || row.net_sold_sum || 0);
      bucket.net_amount_sum += Number(row.net_amount_sum || 0);
      bucket.leads = bucket.total_leads_sum;
    });

    return Array.from(byId.values()).sort((a, b) => b.total_spend_sum - a.total_spend_sum);
  }, [scopeType, scopeClients, summary, selectedScopeClientId]);

  const scopeClientOptions = useMemo(
    () =>
      (scopeClients || [])
        .map((row = {}) => ({
          id: String(row.id),
          name: row.name || `Client ${row.id}`,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [scopeClients]
  );

  const verticalSpendLookup = useMemo(() => {
    const map = new Map();
    (verticals || []).forEach((row = {}) => {
      const name = (row.vertical || '').trim() || 'Unspecified';
      const spend = Number(row.total_spend_sum || row.total_spend || 0);
      if (!Number.isNaN(spend)) {
        map.set(name, spend);
      }
    });
    return map;
  }, [verticals]);

  const channelSpendLookup = useMemo(() => {
    const map = new Map();
    (channels || []).forEach((row = {}) => {
      const key = normalizeValue(
        row.channel__name || row.channel_name || row.channel || row.name
      );
      if (!key) {
        return;
      }
      const spend = Number(row.total_spend_sum || row.total_spend || 0);
      if (!Number.isNaN(spend)) {
        map.set(key, spend);
      }
    });
    return map;
  }, [channels]);

  const territorySpendLookup = useMemo(() => {
    const map = new Map();
    (territories || []).forEach((row = {}) => {
      const key = normalizeValue(
        row.displayName || row.client_territory__name || row.territory_name
      );
      if (!key) {
        return;
      }
      const spend = Number(row.total_spend_sum || row.total_spend || 0);
      if (!Number.isNaN(spend)) {
        map.set(key, spend);
      }
    });
    return map;
  }, [territories]);

  const overallPacingByClient = useMemo(() => {
    const map = new Map();
    (overallPacingRows || []).forEach((row = {}) => {
      const clientId = Number(row.client_id);
      if (!Number.isFinite(clientId)) {
        return;
      }
      map.set(clientId, row);
    });
    return map;
  }, [overallPacingRows]);

  const {
    totals: budgetTotals,
    byVertical: budgetVerticals,
    byChannel: budgetChannels,
    byTerritory: budgetTerritories,
  } = useMemo(() => {
    const mergeBucket = (target, source) => {
      if (!source) {
        return;
      }
      target.budget_amount += Number(source.budget_amount || 0);
      target.projected_spend += Number(source.projected_spend || 0);
      target.month_to_date_spend += Number(source.month_to_date_spend || 0);
      target.yesterday_spend += Number(source.yesterday_spend || 0);
      target.budget_remaining += Number(source.budget_remaining || 0);
      target.remaining_per_day += Number(source.remaining_per_day || 0);
    };
    const emptyBucket = () => ({
      budget_amount: 0,
      projected_spend: 0,
      month_to_date_spend: 0,
      yesterday_spend: 0,
      budget_remaining: 0,
      remaining_per_day: 0,
    });

    if (scopeType === 'parent') {
      const clientIds = scopeClients
        .map((row) => Number(row?.id))
        .filter((id) =>
          selectedScopeClientId ? String(id) === String(selectedScopeClientId) : true
        )
        .filter((id) => Number.isFinite(id));
      if (!clientIds.length) {
        return { totals: null, byVertical: {}, byChannel: {}, byTerritory: {} };
      }
      const selectedClientIds = new Set(clientIds.map(String));
      const clientFeeById = new Map(
        (scopeClients || []).map((row = {}) => [Number(row.id), Number(row.fees || 0)])
      );

      const combined = emptyBucket();
      const verticalBuckets = {};
      const channelBuckets = {};
      const territoryBuckets = {};

      clientIds.forEach((id) => {
        const canonicalOverall = overallPacingByClient.get(id);
        if (canonicalOverall) {
          mergeBucket(combined, canonicalOverall);
          return;
        }
        const metrics = budgetPacingLookup?.clients?.[`client-${id}`];
        if (!metrics) {
          return;
        }
        const feeRate = Number(clientFeeById.get(id) || 0);
        const safeFeeRate = Number.isFinite(feeRate) ? feeRate : 0;
        const recalculated = recalculateBudgetMetrics(
          {
            client_fee: safeFeeRate,
            total_spend_sum: 0,
          },
          metrics,
          { ...metrics },
          {
            shouldFallbackToActuals: false,
            calendarToday,
          }
        );
        mergeBucket(combined, recalculated);

        (metrics.vertical_budget_breakdown || []).forEach((item) => {
          const key = (item.vertical || '').trim() || 'Unspecified';
          if (!verticalBuckets[key]) {
            verticalBuckets[key] = emptyBucket();
          }
          mergeBucket(verticalBuckets[key], item);
        });
      });

      Object.entries(budgetPacingLookup?.channels || {}).forEach(([key, metrics]) => {
        const match = key.match(/^client-(\d+)-channel-(.+)$/);
        if (!match) {
          return;
        }
        if (!selectedClientIds.has(match[1])) {
          return;
        }
        const channelKey = match[2];
        if (!channelBuckets[channelKey]) {
          channelBuckets[channelKey] = emptyBucket();
        }
        mergeBucket(channelBuckets[channelKey], metrics);
      });

      Object.entries(budgetPacingLookup?.territories || {}).forEach(
        ([key, metrics]) => {
          const match = key.match(/^client-(\d+)-territory-(.+)$/);
          if (!match) {
            return;
          }
          if (!selectedClientIds.has(match[1])) {
            return;
          }
          const territoryKey = match[2];
          if (!territoryBuckets[territoryKey]) {
            territoryBuckets[territoryKey] = emptyBucket();
          }
          mergeBucket(territoryBuckets[territoryKey], metrics);
        }
      );

      const totals = {
        ...combined,
        pacing_percentage: safeDiv(combined.projected_spend, combined.budget_amount) * 100,
      };

      const hasTotals = Object.values(combined).some((value) => Number(value) !== 0);
      return {
        totals: hasTotals ? totals : null,
        byVertical: verticalBuckets,
        byChannel: channelBuckets,
        byTerritory: territoryBuckets,
      };
    }

    if (!clientPk) {
      return { totals: null, byVertical: {}, byChannel: {}, byTerritory: {} };
    }

    const canonicalOverall = overallPacingByClient.get(Number(clientPk));
    const metrics = budgetPacingLookup?.clients?.[`client-${clientPk}`];
    if (!metrics && !canonicalOverall) {
      return { totals: null, byVertical: {}, byChannel: {}, byTerritory: {} };
    }

    const feeValue = Number(client?.fees ?? 0);
    const safeFeeValue = Number.isFinite(feeValue) ? feeValue : 0;
    const metricsWithSummary = { ...(canonicalOverall || metrics) };

    let clientMonthToDateRaw = 0;
    if (canonicalOverall) {
      const feeMultiplier = 1 + safeFeeValue;
      const canonicalWithFees = Number(canonicalOverall.month_to_date_spend ?? 0);
      clientMonthToDateRaw =
        feeMultiplier > 0 ? canonicalWithFees / feeMultiplier : canonicalWithFees;
    }
    const baseRow = {
      client_fee: safeFeeValue,
      total_spend_sum: clientMonthToDateRaw,
    };

    const merged = canonicalOverall
      ? { ...metricsWithSummary }
      : recalculateBudgetMetrics(
          baseRow,
          metricsWithSummary,
          { ...metricsWithSummary },
          {
            shouldFallbackToActuals: false,
            calendarToday,
          }
        );

    const totals = {
      budget_amount: merged.budget_amount ?? 0,
      projected_spend: merged.projected_spend ?? 0,
      month_to_date_spend: merged.month_to_date_spend ?? 0,
      yesterday_spend: merged.yesterday_spend ?? 0,
      budget_remaining: merged.budget_remaining ?? 0,
      remaining_per_day: merged.remaining_per_day ?? 0,
      calendar_budget_remaining_per_day: merged.calendar_budget_remaining_per_day,
      pacing_percentage: merged.pacing_percentage ?? 0,
    };

    const verticalFinal = {};
    const breakdown = Array.isArray(merged.vertical_budget_breakdown)
      ? merged.vertical_budget_breakdown
      : [];

    breakdown.forEach((item) => {
      const name = (item.vertical || '').trim() || 'Unspecified';
      const verticalRow = {
        client_fee: baseRow.client_fee,
        total_spend_sum: verticalSpendLookup.get(name) ?? 0,
      };
      const recalculated = recalculateBudgetMetrics(
        verticalRow,
        item,
        { ...item },
        {
          shouldFallbackToActuals: false,
          calendarToday,
        }
      );
      verticalFinal[name] = {
        budget_amount: recalculated.budget_amount ?? 0,
        projected_spend: recalculated.projected_spend ?? 0,
        month_to_date_spend: recalculated.month_to_date_spend ?? 0,
        budget_remaining: recalculated.budget_remaining ?? 0,
        remaining_per_day: recalculated.remaining_per_day ?? 0,
        calendar_budget_remaining_per_day:
          recalculated.calendar_budget_remaining_per_day,
        pacing_percentage: recalculated.pacing_percentage ?? 0,
      };
    });

    const channelFinal = {};
    Object.entries(budgetPacingLookup?.channels || {}).forEach(([key, item]) => {
      const prefix = `client-${clientPk}-channel-`;
      if (!key.startsWith(prefix)) {
        return;
      }
      const channelKey = key.slice(prefix.length);
      const channelRow = {
        client_fee: baseRow.client_fee,
        total_spend_sum: channelSpendLookup.get(channelKey) ?? 0,
      };
      const recalculated = recalculateBudgetMetrics(
        channelRow,
        item,
        { ...item },
        {
          shouldFallbackToActuals: false,
          calendarToday,
        }
      );
      channelFinal[channelKey] = {
        budget_amount: recalculated.budget_amount ?? 0,
        projected_spend: recalculated.projected_spend ?? 0,
        month_to_date_spend: recalculated.month_to_date_spend ?? 0,
        budget_remaining: recalculated.budget_remaining ?? 0,
        remaining_per_day: recalculated.remaining_per_day ?? 0,
        calendar_budget_remaining_per_day:
          recalculated.calendar_budget_remaining_per_day,
        pacing_percentage: recalculated.pacing_percentage ?? 0,
      };
    });

    const territoryFinal = {};
    Object.entries(budgetPacingLookup?.territories || {}).forEach(([key, item]) => {
      const prefix = `client-${clientPk}-territory-`;
      if (!key.startsWith(prefix)) {
        return;
      }
      const territoryKey = key.slice(prefix.length);
      const territoryRow = {
        client_fee: baseRow.client_fee,
        total_spend_sum: territorySpendLookup.get(territoryKey) ?? 0,
      };
      const recalculated = recalculateBudgetMetrics(
        territoryRow,
        item,
        { ...item },
        {
          shouldFallbackToActuals: false,
          calendarToday,
        }
      );
      territoryFinal[territoryKey] = {
        budget_amount: recalculated.budget_amount ?? 0,
        projected_spend: recalculated.projected_spend ?? 0,
        month_to_date_spend: recalculated.month_to_date_spend ?? 0,
        budget_remaining: recalculated.budget_remaining ?? 0,
        remaining_per_day: recalculated.remaining_per_day ?? 0,
        calendar_budget_remaining_per_day:
          recalculated.calendar_budget_remaining_per_day,
        pacing_percentage: recalculated.pacing_percentage ?? 0,
      };
    });

    const hasTotals = Object.values(totals).some((value) => Number(value) !== 0);
    return {
      totals: hasTotals ? totals : null,
      byVertical: verticalFinal,
      byChannel: channelFinal,
      byTerritory: territoryFinal,
    };
  }, [
    budgetPacingLookup,
    scopeType,
    scopeClients,
    selectedScopeClientId,
    clientPk,
    client,
    totalSpend,
    overallPacingByClient,
    calendarToday,
    verticalSpendLookup,
    channelSpendLookup,
    territorySpendLookup,
    territories,
  ]);

  const pacingDonut = useMemo(() => {
    const budgetAmount = Number(budgetTotals?.budget_amount);
    const remainingRaw = Number(budgetTotals?.budget_remaining);
    const pacingPercentRaw = Number(budgetTotals?.pacing_percentage);
    if (!Number.isFinite(budgetAmount) || budgetAmount <= 0) {
      return null;
    }
    const safeRemaining = Number.isFinite(remainingRaw) ? remainingRaw : 0;
    const remaining = Math.max(0, safeRemaining);
    const spent = Math.max(0, budgetAmount - remaining);
    const overage = Math.max(0, -safeRemaining);
    const remainingPercent = budgetAmount > 0 ? (remaining / budgetAmount) * 100 : 0;
    const pacingPercent = Number.isFinite(pacingPercentRaw) ? pacingPercentRaw : 0;
    const pacingClamped = Math.min(Math.max(pacingPercent, 0), 100);
    return {
      data: [
        { name: 'Spent', value: spent },
        { name: 'Remaining', value: remaining },
      ],
      pacingData: [
        { name: 'Paced', value: pacingClamped },
        { name: 'Remaining', value: Math.max(0, 100 - pacingClamped) },
      ],
      budgetAmount,
      remaining,
      spent,
      overage,
      remainingPercent,
      pacingPercent,
    };
  }, [budgetTotals]);

  const displaySpend = showFee ? totalSpend * (1 + feeRate) : totalSpend;
  const displayCpl = showFee ? safeDiv(displaySpend, totalLeads) : cpl;
  const displayCpBooked = showFee ? safeDiv(displaySpend, totalBooked) : cpBooked;
  const displayCpIssue = showFee ? safeDiv(displaySpend, totalIssues) : cpIssue;
  const displayCpNetSale = showFee ? safeDiv(displaySpend, totalNetSales) : cpNetSale;
  const displayCostOfMarketing = showFee
    ? safeDiv(displaySpend, netContractAmount)
    : costOfMarketing;
  const brandAccent = useMemo(() => {
    const raw = (client?.brand_color || '').trim();
    return isHexColor(raw) ? toFullHex(raw) : null;
  }, [client]);
  const heroGradient = useMemo(() => getClientGradient(client), [client]);
  const heroStats = [
    {
      label: withFees('Spend'),
      value: <DollarFormatter value={displaySpend} />,
      icon: <PaidOutlinedIcon fontSize="small" />,
    },
    {
      label: 'Leads',
      value: totalLeads.toLocaleString('en-US'),
      icon: <TrendingUpIcon fontSize="small" />,
      helper: <>CPL: <DollarFormatter value={displayCpl} /></>,
    },
    {
      label: 'Booked',
      value: totalBooked.toLocaleString('en-US'),
      icon: <ChecklistRtlIcon fontSize="small" />,
      helper: <>CP Booked: <DollarFormatter value={displayCpBooked} /></>,
    },
    {
      label: 'Issues',
      value: totalIssues.toLocaleString('en-US'),
      icon: <WarningAmberIcon fontSize="small" />,
      helper: <>CP Issue: <DollarFormatter value={displayCpIssue} /></>,
    },
    {
      label: 'Net Sales',
      value: totalNetSales.toLocaleString('en-US'),
      icon: <BarChartIcon fontSize="small" />,
      helper: <>CP Net Sale: <DollarFormatter value={displayCpNetSale} /></>,
    },
    {
      label: 'Net Amount',
      value: <DollarFormatter value={netContractAmount} />,
      icon: <StorefrontIcon fontSize="small" />,
      helper: <>Cost of Marketing: <PercentageFormatter value={displayCostOfMarketing} /></>,
    },
  ];

  const summaryByDay = useMemo(() => aggregateSummaryRowsByDay(summary), [summary]);

  const chartData = useMemo(() => {
    const multiplier = 1 + feeRate;
    return Array.isArray(summaryByDay)
      ? summaryByDay.map((s) => ({ ...s, spendWithFees: s.total_spend_sum * multiplier }))
      : [];
  }, [summaryByDay, feeRate]);

  const dailySummaryWindow = useMemo(() => {
    if (!Array.isArray(summaryByDay)) {
      return [];
    }

    const rangeCount = Number.isFinite(Number(dayRange))
      ? Number(dayRange)
      : dayRangeOptions[0];

    const sorted = [...summaryByDay].sort((a, b) => {
      const aTime = new Date(a?.date ?? '').getTime();
      const bTime = new Date(b?.date ?? '').getTime();
      const safeATime = Number.isFinite(aTime) ? aTime : -Infinity;
      const safeBTime = Number.isFinite(bTime) ? bTime : -Infinity;
      return safeBTime - safeATime;
    });

    return sorted.slice(0, rangeCount);
  }, [summaryByDay, dayRange]);

  const sortedChannels = useMemo(() => {
    const data = Array.isArray(channels) ? [...channels] : [];
    return data.sort((a, b) => (b?.total_spend_sum || 0) - (a?.total_spend_sum || 0));
  }, [channels]);

  const channelIdMap = useMemo(() => {
    const map = new Map();
    (channels || []).forEach((ch = {}) => {
      const id = getChannelId(ch);
      const name = ch.channel__name || ch.channel_name || ch.channel || ch.name;
      if (id !== undefined && id !== null) {
        map.set(Number(id), name || '');
      }
    });
    return map;
  }, [channels]);

  const sortedVerticals = useMemo(() => {
    const data = Array.isArray(verticals) ? [...verticals] : [];
    return data.sort((a, b) => (b?.total_spend_sum || 0) - (a?.total_spend_sum || 0));
  }, [verticals]);

  const sortedCampaigns = useMemo(() => {
    const data = Array.isArray(campaigns) ? [...campaigns] : [];
    return data.sort(
      (a, b) => (b?.total_spend_sum || 0) - (a?.total_spend_sum || 0)
    );
  }, [campaigns]);

  const sortedStates = useMemo(() => {
    const data = Array.isArray(states) ? [...states] : [];
    return data.sort((a, b) => (b?.total_spend_sum || 0) - (a?.total_spend_sum || 0));
  }, [states]);

  const sortedDmas = useMemo(() => {
    const data = Array.isArray(dmas) ? [...dmas] : [];
    return data.sort((a, b) => (b?.total_spend_sum || 0) - (a?.total_spend_sum || 0));
  }, [dmas]);

  const sortedTerritories = useMemo(() => {
    const data = Array.isArray(territories) ? [...territories] : [];
    return data.sort((a, b) => (b?.total_spend_sum || 0) - (a?.total_spend_sum || 0));
  }, [territories]);

  const sortedCities = useMemo(() => {
    const data = Array.isArray(cities) ? [...cities] : [];
    return data.sort((a, b) => (b?.total_leads_sum || 0) - (a?.total_leads_sum || 0));
  }, [cities]);

  const sortedCounties = useMemo(() => {
    const data = Array.isArray(counties) ? [...counties] : [];
    return data.sort((a, b) => (b?.total_leads_sum || 0) - (a?.total_leads_sum || 0));
  }, [counties]);

  const verticalTotals = useMemo(
    () => calculateTotals(sortedVerticals),
    [calculateTotals, sortedVerticals]
  );

  const channelTotals = useMemo(
    () => calculateTotals(sortedChannels),
    [calculateTotals, sortedChannels]
  );

  const campaignTotals = useMemo(
    () => calculateTotals(sortedCampaigns),
    [calculateTotals, sortedCampaigns]
  );

  const territoryTotals = useMemo(
    () => calculateTotals(sortedTerritories),
    [calculateTotals, sortedTerritories]
  );

  const summarizeBudgetSegments = (segments = {}) =>
    Object.values(segments || {}).reduce(
      (acc, item = {}) => {
        acc.budget_amount += Number(item.budget_amount || 0);
        acc.projected_spend += Number(item.projected_spend || 0);
        acc.budget_remaining += Number(item.budget_remaining || 0);
        acc.remaining_per_day += Number(
          item.calendar_budget_remaining_per_day ?? item.remaining_per_day ?? 0
        );
        return acc;
      },
      {
        budget_amount: 0,
        projected_spend: 0,
        budget_remaining: 0,
        remaining_per_day: 0,
      }
    );

  const channelBudgetTotals = useMemo(
    () => summarizeBudgetSegments(budgetChannels),
    [budgetChannels]
  );

  const territoryBudgetTotals = useMemo(
    () => summarizeBudgetSegments(budgetTerritories),
    [budgetTerritories]
  );

  const segmentPacingTableRows = useMemo(() => {
    const sortSegmentRows = (rows = []) =>
      [...rows].sort((a = {}, b = {}) => {
        const aVertical = (a.vertical || '').trim();
        const bVertical = (b.vertical || '').trim();
        const aLocation = (a.location || '').trim();
        const bLocation = (b.location || '').trim();
        const aKey = (aVertical && aVertical !== '-' ? aVertical : aLocation || '')
          .toLowerCase();
        const bKey = (bVertical && bVertical !== '-' ? bVertical : bLocation || '')
          .toLowerCase();
        const byKey = aKey.localeCompare(bKey, undefined, { sensitivity: 'base' });
        if (byKey !== 0) {
          return byKey;
        }
        return (a.segment_name || '').localeCompare(b.segment_name || '', undefined, {
          sensitivity: 'base',
        });
      });

    if (Array.isArray(segmentPacingRowsCanonical) && segmentPacingRowsCanonical.length > 0) {
      const filtered = (segmentPacingRowsCanonical || []).filter((row = {}) => {
        if (scopeType === 'parent') {
          if (selectedScopeClientId) {
            return String(row.client_id) === String(selectedScopeClientId);
          }
          return true;
        }
        if (!clientPk) {
          return false;
        }
        return Number(row.client_id) === Number(clientPk);
      });

      return sortSegmentRows(
        filtered
        .map((row = {}) => {
          const vertical = (row.vertical_name || '').trim() || '-';
          const location = (row.location_name || '').trim() || '-';
          const channel = (row.channel_name || '').trim() || '-';
          const segmentName =
            row.segment_name ||
            [location !== '-' ? location : '', vertical !== '-' ? vertical : '']
              .filter(Boolean)
              .join(' ') ||
            (channel !== '-' ? channel : 'Total Budget');

          return {
            key:
              row.segment_key ||
              `${row.client_id || ''}|${row.vertical_id || ''}|${row.location_id || ''}|${row.channel_id || ''}`,
            client_id: Number(row.client_id) || null,
            client_name: row.client__name || row.client_name || '',
            vertical,
            location,
            channel,
            segment_name: segmentName,
            budget_amount: Number(row.budget_amount || 0),
            adjustment_total: Number(row.adjustment_total || 0),
            month_to_date_spend: Number(row.month_to_date_spend || 0),
            yesterday_spend: Number(row.yesterday_spend || 0),
            projected_spend: Number(row.projected_spend || 0),
            budget_remaining: Number(row.budget_remaining || 0),
            remaining_spend_per_day: Number(row.remaining_per_day || 0),
            remaining_per_day: Number(row.remaining_per_day || 0),
            last_30_spend: Number(row.last_30_spend || 0),
            last_30_net_revenue: Number(row.last_30_net_revenue || 0),
            last_30_cost_of_marketing: Number(row.last_30_cost_of_marketing || 0),
            last_60_spend: Number(row.last_60_spend || 0),
            last_60_net_revenue: Number(row.last_60_net_revenue || 0),
            last_60_cost_of_marketing: Number(row.last_60_cost_of_marketing || 0),
            pacing_percentage: Number(row.pacing_percentage || 0),
          };
        })
      );
    }

    const grouped = new Map();
    const normalized = (value) => normalizeValue(value);

    const clientFeeById = new Map();
    if (scopeType === 'parent') {
      (scopeClients || []).forEach((c = {}) => {
        const id = Number(c.id);
        if (Number.isFinite(id)) {
          clientFeeById.set(id, Number(c.fees || 0));
        }
      });
    }

    const territoryVerticalSpend = new Map();
    (filteredTerritoryCampaigns || []).forEach((item = {}) => {
      const territoryName =
        item.client_territory__name || item.territory_name || '';
      const verticalName = item.vertical || '';
      const tKey = normalized(territoryName);
      const vKey = normalized(verticalName);
      if (!tKey || !vKey) {
        return;
      }
      const key = `${tKey}|${vKey}`;
      territoryVerticalSpend.set(
        key,
        (territoryVerticalSpend.get(key) || 0) +
          Number(item.total_spend_sum || item.total_spend || 0)
      );
    });

    (budgetPacingRows || []).forEach((row = {}) => {
      const vertical =
        (row?.vertical_data?.vertical || row?.vertical_name || '').trim() ||
        '';
      const location =
        (row?.location_data?.name || row?.territory_name || '').trim() || '';
      const channel =
        (row?.channel_data?.name || row?.channel_name || '').trim() || '';
      const clientName = (row?.client_data?.name || '').trim() || '';
      const clientId = row?.client;
      const key = [
        String(clientId || ''),
        normalizeValue(vertical),
        normalizeValue(location),
        normalizeValue(channel),
      ].join('|');

      if (!grouped.has(key)) {
          grouped.set(key, {
          key,
          client_id: Number(clientId) || null,
          client_name: clientName,
          vertical: vertical || '-',
          location: location || '-',
            channel: channel || '-',
            budget_amount: 0,
            adjustment_total: 0,
            month_to_date_spend: 0,
          projected_spend: 0,
          budget_remaining: 0,
          remaining_per_day: 0,
          yesterday_spend: 0,
          yesterday_spend_with_fees: 0,
          today_spend: 0,
          today_spend_with_fees: 0,
          pacing_month_to_date_with_fees: 0,
          month_through_yesterday_with_fees: 0,
          actual_month_to_date_with_fees: 0,
          has_yesterday_spend: false,
          has_yesterday_spend_with_fees: false,
          has_today_spend: false,
          has_today_spend_with_fees: false,
          has_pacing_month_to_date_with_fees: false,
          has_month_through_yesterday_with_fees: false,
          has_actual_month_to_date_with_fees: false,
          days_remaining: null,
        });
      }

      const bucket = grouped.get(key);
      const meta =
        (row?.__pacing_metadata && typeof row.__pacing_metadata === 'object'
          ? row.__pacing_metadata
          : null) ||
        (row?.pacing_metadata && typeof row.pacing_metadata === 'object'
          ? row.pacing_metadata
          : {});
      const baseAmount = Number(row.amount || 0);
      const effectiveAmount = Number(
        row.effective_amount !== undefined ? row.effective_amount : row.amount || 0
      );
      bucket.budget_amount += Number.isFinite(effectiveAmount)
        ? effectiveAmount
        : baseAmount;
      bucket.adjustment_total += Number.isFinite(effectiveAmount - baseAmount)
        ? effectiveAmount - baseAmount
        : Number(row.adjustment_total || 0);
      bucket.month_to_date_spend += Number(row.month_to_date_spend || 0);
      bucket.projected_spend += Number(row.projected_spend || 0);
      bucket.budget_remaining += Number(row.budget_remaining || 0);
      bucket.remaining_per_day += Number(row.remaining_per_day || 0);
      if (Number.isFinite(Number(row.yesterday_spend))) {
        bucket.yesterday_spend += Number(row.yesterday_spend || 0);
        bucket.has_yesterday_spend = true;
      }
      if (Number.isFinite(Number(row.yesterday_spend_with_fees))) {
        bucket.yesterday_spend_with_fees += Number(
          row.yesterday_spend_with_fees || 0
        );
        bucket.has_yesterday_spend_with_fees = true;
      }
      if (Number.isFinite(Number(row.today_spend))) {
        bucket.today_spend += Number(row.today_spend || 0);
        bucket.has_today_spend = true;
      }
      if (Number.isFinite(Number(row.today_spend_with_fees))) {
        bucket.today_spend_with_fees += Number(row.today_spend_with_fees || 0);
        bucket.has_today_spend_with_fees = true;
      }
      if (Number.isFinite(Number(meta.pacing_month_to_date_with_fees))) {
        bucket.pacing_month_to_date_with_fees += Number(
          meta.pacing_month_to_date_with_fees || 0
        );
        bucket.has_pacing_month_to_date_with_fees = true;
      }
      if (Number.isFinite(Number(meta.month_through_yesterday_with_fees))) {
        bucket.month_through_yesterday_with_fees += Number(
          meta.month_through_yesterday_with_fees || 0
        );
        bucket.has_month_through_yesterday_with_fees = true;
      }
      if (Number.isFinite(Number(meta.actual_month_to_date_with_fees))) {
        bucket.actual_month_to_date_with_fees += Number(
          meta.actual_month_to_date_with_fees || 0
        );
        bucket.has_actual_month_to_date_with_fees = true;
      }
      if (
        bucket.days_remaining === null &&
        Number.isFinite(Number(meta.days_remaining))
      ) {
        bucket.days_remaining = Number(meta.days_remaining);
      }
    });

    return sortSegmentRows(Array.from(grouped.values())
      .map((row) => {
        const hasVertical = row.vertical !== '-';
        const hasLocation = row.location !== '-';
        const hasChannel = row.channel !== '-';
        const locationKey = normalized(row.location);
        const verticalKey = normalized(row.vertical);
        const channelKey = normalized(row.channel);

        let segmentSpend = 0;
        if (hasVertical && hasLocation) {
          segmentSpend =
            territoryVerticalSpend.get(`${locationKey}|${verticalKey}`) || 0;
        } else if (hasVertical) {
          segmentSpend = verticalSpendLookup.get(row.vertical) ?? 0;
        } else if (hasLocation) {
          segmentSpend = territorySpendLookup.get(locationKey) ?? 0;
        } else if (hasChannel) {
          segmentSpend = channelSpendLookup.get(channelKey) ?? 0;
        } else {
          segmentSpend = Number(totalSpend || 0);
        }

        if (
          (!Number.isFinite(segmentSpend) || segmentSpend <= 0) &&
          Number.isFinite(row.month_to_date_spend)
        ) {
          segmentSpend = Number(row.month_to_date_spend);
        }

        const feeRate =
          scopeType === 'parent'
            ? clientFeeById.get(row.client_id) || 0
            : Number(client?.fees || 0);

        const rawMetrics = {
          budget_amount: row.budget_amount,
          projected_spend: row.projected_spend,
          month_to_date_spend: row.month_to_date_spend,
          budget_remaining: row.budget_remaining,
          remaining_per_day: row.remaining_per_day,
          yesterday_spend: row.has_yesterday_spend ? row.yesterday_spend : undefined,
          yesterday_spend_with_fees: row.has_yesterday_spend_with_fees
            ? row.yesterday_spend_with_fees
            : undefined,
          today_spend: row.has_today_spend ? row.today_spend : undefined,
          today_spend_with_fees: row.has_today_spend_with_fees
            ? row.today_spend_with_fees
            : undefined,
          __pacing_metadata: {
            ...(Number.isFinite(Number(row.days_remaining))
              ? { days_remaining: Number(row.days_remaining) }
              : {}),
            ...(row.has_pacing_month_to_date_with_fees
              ? {
                  pacing_month_to_date_with_fees:
                    row.pacing_month_to_date_with_fees,
                }
              : {}),
            ...(row.has_month_through_yesterday_with_fees
              ? {
                  month_through_yesterday_with_fees:
                    row.month_through_yesterday_with_fees,
                }
              : {}),
            ...(row.has_actual_month_to_date_with_fees
              ? {
                  actual_month_to_date_with_fees:
                    row.actual_month_to_date_with_fees,
                }
              : {}),
          },
        };

        const recalculated = recalculateBudgetMetrics(
          {
            client_fee: Number.isFinite(feeRate) ? feeRate : 0,
            total_spend_sum: Number.isFinite(segmentSpend) ? segmentSpend : 0,
          },
          rawMetrics,
          { ...rawMetrics },
          {
            // Segment pacing should always run on through-yesterday pacing inputs.
            // Using "actual including today" fallback inflates MTD and distorts projections.
            shouldFallbackToActuals: false,
            calendarToday,
          }
        );

        const feeMultiplier =
          1 + (Number.isFinite(Number(feeRate)) ? Number(feeRate) : 0);
        const recalculatedMonthToDate = Number(
          recalculated.month_to_date_spend ?? row.month_to_date_spend ?? 0
        );
        let recalculatedProjected = Number(
          recalculated.projected_spend ?? row.projected_spend ?? 0
        );
        const projectedFromBackend = Number(row.projected_spend || 0) * feeMultiplier;
        const shouldUseBackendProjectionFallback =
          projectedFromBackend > recalculatedMonthToDate &&
          recalculatedProjected <= recalculatedMonthToDate;
        if (shouldUseBackendProjectionFallback) {
          recalculatedProjected = projectedFromBackend;
        }

        const budgetAmount = Number(row.budget_amount || 0);
        const recalculatedPacing =
          budgetAmount > 0 ? (recalculatedProjected / budgetAmount) * 100 : 0;

        const segmentName =
          [row.location !== '-' ? row.location : '', row.vertical !== '-' ? row.vertical : '']
            .filter(Boolean)
            .join(' ') ||
          (row.channel !== '-' ? row.channel : 'Total Budget');

        return {
          ...row,
          segment_name: segmentName,
          month_to_date_spend: recalculatedMonthToDate,
          yesterday_spend: Number(
            recalculated.yesterday_spend ?? row.yesterday_spend ?? 0
          ),
          projected_spend: recalculatedProjected,
          budget_remaining: recalculated.budget_remaining ?? row.budget_remaining,
          remaining_spend_per_day:
            // Fee-excluded (media) remaining/day based on calendar days left in month.
            recalculated.calendar_budget_remaining_per_day ?? row.remaining_per_day,
          remaining_per_day:
            recalculated.calendar_budget_remaining_per_day ??
            recalculated.remaining_per_day ??
            row.remaining_per_day,
          last_30_spend: Number(row.last_30_spend || 0),
          last_30_net_revenue: Number(row.last_30_net_revenue || 0),
          last_30_cost_of_marketing: Number(row.last_30_cost_of_marketing || 0),
          last_60_spend: Number(row.last_60_spend || 0),
          last_60_net_revenue: Number(row.last_60_net_revenue || 0),
          last_60_cost_of_marketing: Number(row.last_60_cost_of_marketing || 0),
          pacing_percentage: recalculatedPacing,
        };
      })
    );
  }, [
    segmentPacingRowsCanonical,
    budgetPacingRows,
    scopeType,
    selectedScopeClientId,
    clientPk,
    scopeClients,
    filteredTerritoryCampaigns,
    verticalSpendLookup,
    territorySpendLookup,
    channelSpendLookup,
    totalSpend,
    client,
    calendarToday,
  ]);

  const segmentPacingTotals = useMemo(() => {
    if (
      scopeType === 'client' &&
      Array.isArray(segmentPacingRowsCanonical) &&
      segmentPacingRowsCanonical.length > 0 &&
      budgetTotals
    ) {
      const budgetAmount = Number(budgetTotals.budget_amount || 0);
      const projectedSpend = Number(budgetTotals.projected_spend || 0);
      const budgetRemaining = Number(budgetTotals.budget_remaining || 0);
      const monthToDateSpend = Number(budgetTotals.month_to_date_spend || 0);
      const yesterdaySpend = Number(budgetTotals.yesterday_spend || 0);
      const remainingPerDay = Number(
        budgetTotals.calendar_budget_remaining_per_day ??
          budgetTotals.remaining_per_day ??
          0
      );
      const rolling = (segmentPacingTableRows || []).reduce(
        (acc, row = {}) => {
          acc.adjustment_total += Number(row.adjustment_total || 0);
          acc.last_30_spend += Number(row.last_30_spend || 0);
          acc.last_30_net_revenue += Number(row.last_30_net_revenue || 0);
          acc.last_60_spend += Number(row.last_60_spend || 0);
          acc.last_60_net_revenue += Number(row.last_60_net_revenue || 0);
          return acc;
        },
        {
          adjustment_total: 0,
          last_30_spend: 0,
          last_30_net_revenue: 0,
          last_60_spend: 0,
          last_60_net_revenue: 0,
        }
      );
      return {
        budget_amount: budgetAmount,
        adjustment_total: rolling.adjustment_total,
        month_to_date_spend: monthToDateSpend,
        yesterday_spend: yesterdaySpend,
        projected_spend: projectedSpend,
        budget_remaining: budgetRemaining,
        remaining_spend_per_day: remainingPerDay,
        last_30_spend: rolling.last_30_spend,
        last_30_net_revenue: rolling.last_30_net_revenue,
        last_30_cost_of_marketing:
          rolling.last_30_net_revenue > 0
            ? rolling.last_30_spend / rolling.last_30_net_revenue
            : 0,
        last_60_spend: rolling.last_60_spend,
        last_60_net_revenue: rolling.last_60_net_revenue,
        last_60_cost_of_marketing:
          rolling.last_60_net_revenue > 0
            ? rolling.last_60_spend / rolling.last_60_net_revenue
            : 0,
        pacing_percentage:
          budgetAmount > 0 ? (projectedSpend / budgetAmount) * 100 : 0,
        percent_remaining:
          budgetAmount > 0 ? (budgetRemaining / budgetAmount) * 100 : 0,
      };
    }

    const totals = (segmentPacingTableRows || []).reduce(
      (acc, row = {}) => {
        acc.budget_amount += Number(row.budget_amount || 0);
        acc.adjustment_total += Number(row.adjustment_total || 0);
        acc.month_to_date_spend += Number(row.month_to_date_spend || 0);
        acc.yesterday_spend += Number(row.yesterday_spend || 0);
        acc.projected_spend += Number(row.projected_spend || 0);
        acc.budget_remaining += Number(row.budget_remaining || 0);
        acc.remaining_spend_per_day += Number(row.remaining_spend_per_day || 0);
        acc.last_30_spend += Number(row.last_30_spend || 0);
        acc.last_30_net_revenue += Number(row.last_30_net_revenue || 0);
        acc.last_60_spend += Number(row.last_60_spend || 0);
        acc.last_60_net_revenue += Number(row.last_60_net_revenue || 0);
        return acc;
      },
      {
        budget_amount: 0,
        adjustment_total: 0,
        month_to_date_spend: 0,
        yesterday_spend: 0,
        projected_spend: 0,
        budget_remaining: 0,
        remaining_spend_per_day: 0,
        last_30_spend: 0,
        last_30_net_revenue: 0,
        last_60_spend: 0,
        last_60_net_revenue: 0,
      }
    );

    totals.last_30_cost_of_marketing =
      totals.last_30_net_revenue > 0
        ? totals.last_30_spend / totals.last_30_net_revenue
        : 0;
    totals.last_60_cost_of_marketing =
      totals.last_60_net_revenue > 0
        ? totals.last_60_spend / totals.last_60_net_revenue
        : 0;

    totals.pacing_percentage =
      totals.budget_amount > 0
        ? (totals.projected_spend / totals.budget_amount) * 100
        : 0;
    totals.percent_remaining =
      totals.budget_amount > 0
        ? (totals.budget_remaining / totals.budget_amount) * 100
        : 0;

    return totals;
  }, [scopeType, segmentPacingRowsCanonical, budgetTotals, segmentPacingTableRows]);

  const overallRemainingPerDay = useMemo(() => {
    if (!budgetTotals) {
      return 0;
    }

    const calendarMedia =
      typeof budgetTotals.calendar_budget_remaining_per_day === 'number' &&
      Number.isFinite(budgetTotals.calendar_budget_remaining_per_day)
        ? Number(budgetTotals.calendar_budget_remaining_per_day)
        : null;

    // Always show media (fee-excluded) remaining/day.
    if (calendarMedia !== null) {
      return calendarMedia;
    }
    return Number(budgetTotals.remaining_per_day ?? 0);
  }, [budgetTotals]);

  const overallDaysLeftInMonth = useMemo(() => {
    const days = getDaysLeftInMonthIncludingToday(calendarToday);
    return Number.isFinite(days) ? Number(days) : 0;
  }, [calendarToday]);

  const segmentPacingVisibleColumns = useMemo(() => {
    const rows = segmentPacingTableRows || [];
    const hasValue = (value) => {
      if (value === null || value === undefined) {
        return false;
      }
      const trimmed = String(value).trim();
      return trimmed !== '' && trimmed !== '-';
    };

    const showVertical = rows.some((row = {}) => hasValue(row.vertical));
    const showLocation = rows.some((row = {}) => hasValue(row.location));
    const showChannel = rows.some((row = {}) => hasValue(row.channel));

    return { showVertical, showLocation, showChannel };
  }, [segmentPacingTableRows]);

  const dmaTotals = useMemo(
    () => calculateTotals(sortedDmas),
    [calculateTotals, sortedDmas]
  );

  const stateTotals = useMemo(
    () => calculateTotals(sortedStates),
    [calculateTotals, sortedStates]
  );

  const paginatedCities = useMemo(() => {
    const start = cityPage * cityRowsPerPage;
    return sortedCities.slice(start, start + cityRowsPerPage);
  }, [sortedCities, cityPage, cityRowsPerPage]);

  const paginatedCounties = useMemo(() => {
    const start = countyPage * countyRowsPerPage;
    return sortedCounties.slice(start, start + countyRowsPerPage);
  }, [sortedCounties, countyPage, countyRowsPerPage]);

  const clearBackfillPoll = useCallback(() => {
    if (backfillPollRef.current) {
      clearInterval(backfillPollRef.current);
      backfillPollRef.current = null;
    }
  }, []);

  const pollBackfillStatus = useCallback(
    async (requestId) => {
      if (!clientPk || scopeType !== 'client' || !requestId) {
        return;
      }
      try {
        const res = await fetchDailyPulseBackfillStatus({
          client_id: clientPk,
          start_date: formatDateToYMD(startDate),
          end_date: formatDateToYMD(endDate),
          request_id: requestId,
        });
        const data = res?.data || {};
        setBackfillProgress(data);
        if (data.state === 'success') {
          clearBackfillPoll();
          setBackfillRequestId(null);
          setBackfillStatus({ type: 'success', message: 'Refresh completed.' });
          setDataRefreshToken(Date.now());
        } else if (data.state === 'error') {
          clearBackfillPoll();
          setBackfillStatus({
            type: 'error',
            message:
              data.failed > 0
                ? `Failed after ${data.succeeded || 0}/${data.expected_days || 0} days.`
                : 'Refresh failed.',
          });
        }
      } catch (err) {
        console.error('Failed to poll backfill status', err);
        clearBackfillPoll();
        setBackfillStatus({
          type: 'error',
          message: 'Unable to check refresh status.',
        });
      }
    },
    [clientPk, scopeType, startDate, endDate, clearBackfillPoll]
  );

  const handleDateRangeChange = (e) => {
    const { startDate: s, endDate: eDate } = e.selection;
    setStartDate(s);
    setEndDate(eDate);
    setBackfillStatus(null);
    setBackfillProgress(null);
    setBackfillRequestId(null);
    clearBackfillPoll();
  };

  const handleDayRangeChange = (_, value) => {
    if (value === null || Number.isNaN(Number(value))) {
      return;
    }

    setDayRange(Number(value));
  };

  const handleBackfill = async () => {
    if (!clientPk || scopeType !== 'client' || !startDate || !endDate) {
      return;
    }
    setBackfillLoading(true);
    setBackfillStatus(null);
    setBackfillProgress(null);
    try {
      const res = await triggerDailyPulseBackfill({
        client_id: clientPk,
        start_date: formatDateToYMD(startDate),
        end_date: formatDateToYMD(endDate),
      });
      const scheduled = res?.data?.scheduled_days;
      const detail = res?.data?.detail || 'Collection scheduled.';
      const helper = scheduled ? ` ${scheduled} day(s) queued.` : '';
      const requestId = res?.data?.request_id || null;
      setBackfillRequestId(requestId);
      setBackfillStatus({ type: 'success', message: `${detail}${helper}`.trim() });
      if (requestId) {
        clearBackfillPoll();
        pollBackfillStatus(requestId);
        backfillPollRef.current = setInterval(
          () => pollBackfillStatus(requestId),
          12000
        );
      }
    } catch (err) {
      console.error('Failed to queue date range collection', err);
      const data = err?.response?.data;
      const detail =
        (typeof data === 'string' && data) ||
        data?.detail ||
        (Array.isArray(data) ? data.join(' ') : '') ||
        Object.values(data || {})
          .flat()
          .join(' ') ||
        'Failed to schedule collection.';
      setBackfillStatus({ type: 'error', message: detail });
      setBackfillRequestId(null);
      setBackfillProgress(null);
      clearBackfillPoll();
    } finally {
      setBackfillLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      clearBackfillPoll();
    };
  }, [clearBackfillPoll]);

  const handleTerritoryVerticalChange = (event) => {
    const { value } = event.target;
    const nextValue =
      typeof value === 'string' ? value.split(',') : value.slice();
    if (nextValue.includes('__all')) {
      setTerritoryVerticalFilter([]);
      return;
    }
    setTerritoryVerticalFilter(nextValue);
  };

const formatDayLabel = (value) => {
  if (!value) {
    return 'Unknown';
  }

    const normalizedDate = (() => {
      if (typeof value === 'string') {
        const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (match) {
          const [, year, month, day] = match.map(Number);
          // Construct the date with numeric parts to avoid timezone shifts that occur with YYYY-MM-DD strings.
          const dateObj = new Date(year, month - 1, day);
          if (Number.isFinite(dateObj.getTime())) {
            return dateObj;
          }
        }
      }
      const fallback = new Date(value);
      return Number.isFinite(fallback.getTime()) ? fallback : null;
    })();

    if (!normalizedDate) {
      return value;
    }

    const month = normalizedDate.getMonth() + 1;
    const day = normalizedDate.getDate();

  return `${month}/${day}`;
};

const buildSingleDayRange = (value) => {
  const parsed = parseDateValue(value);
  if (!parsed) return null;
  const start = new Date(parsed);
  start.setHours(0, 0, 0, 0);
  const end = new Date(parsed);
  end.setHours(23, 59, 59, 999);
  return { startDate: start, endDate: end };
};

const computeBackendCounts = (detail = {}) => {
  if (detail.backendTotals) {
    const t = detail.backendTotals;
    return {
      bookedCount: t.booked || 0,
      apptCount: t.scheduled || 0,
      issueCount: t.issue || 0,
      demoCount: t.demo || 0,
      soldCount: t.gross || 0,
      netCount: t.net || 0,
      grossSum: t.gross_amount || 0,
      netSum: t.net_amount || 0,
    };
  }

  const rangeStart = detail.dateRange?.startDate || null;
  const rangeEnd = detail.dateRange?.endDate || null;
  const rows = detail.backendFull || detail.backend || [];
  const getCreatedDate = (row = {}) => row.created_date || row.created_at || row.date_created || row.created || row.prospect_date_created || row.prospect_created_at;

  let bookedCount = 0;
  let soldCount = 0;
  let apptCount = 0;
  let issueCount = 0;
  let demoCount = 0;
  let netCount = 0;
  let grossSum = 0;
  let netSum = 0;

  rows.forEach((row = {}) => {
    const soldOk = isDateInRange(row?.sold_date, rangeStart, rangeEnd);
    const apptOk = isDateInRange(row?.appointment_date, rangeStart, rangeEnd);
    const createdOk = isDateInRange(getCreatedDate(row), rangeStart, rangeEnd);

    if (createdOk && row?.appointment_date) {
      bookedCount += 1;
    }

    const inRange = soldOk || apptOk;
    if (inRange) {
      if (row?.sold_date) soldCount += 1;
      if (row?.appointment_date) apptCount += 1;
      if (row?.issue) issueCount += 1;
      if (row?.demo) demoCount += 1;
      const netVal = Number(row?.net_amount);
      if (Number.isFinite(netVal) && netVal > 0) {
        netCount += 1;
      }
      const grossVal = Number(row?.gross_amount);
      if (Number.isFinite(grossVal)) {
        grossSum += grossVal;
      }
      if (Number.isFinite(netVal)) {
        netSum += netVal;
      }
    }
  });

  return { bookedCount, soldCount, apptCount, issueCount, demoCount, netCount, grossSum, netSum };
};

const buildBackendTotalsFromSummary = (summary = {}) => {
  const safe = (val) => Number(val) || 0;
  return {
    booked: safe(summary.booked_sum ?? summary.booked),
    scheduled: safe(summary.scheduled_sum ?? summary.scheduled),
    issue: safe(summary.issues_sum ?? summary.issue),
    demo: safe(summary.demos_sum ?? summary.demo),
    gross: safe(summary.gross_sales_sum ?? summary.gross_sold_sum ?? summary.gross),
    net: safe(summary.net_sales_sum ?? summary.net_sold_sum ?? summary.net),
    gross_amount: safe(summary.gross_amount_sum ?? summary.gross_amount),
    net_amount: safe(summary.net_amount_sum ?? summary.net_amount),
  };
};

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box p={3} sx={{ backgroundColor: 'background.default' }}>
      <Card sx={{ mb: 3, borderRadius: 3, overflow: 'hidden', boxShadow: '0 24px 48px rgba(15, 23, 42, 0.14)' }}>
        <Box sx={{ background: heroGradient, color: '#fff', p: 3 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2.5} alignItems="center">
              {client?.client_logo_file || client?.client_logo_url ? (
                <Box
                  sx={{
                    width: { xs: 120, sm: 156 },
                    height: 72,
                    borderRadius: 2,
                    bgcolor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 1.25,
                    boxShadow: '0 10px 28px rgba(0,0,0,0.18)',
                  }}
                >
                  <Box
                    component="img"
                    src={client.client_logo_file || client.client_logo_url}
                    alt={client?.name}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              ) : (
                <Avatar
                  variant="rounded"
                  sx={{ width: 56, height: 56, bgcolor: 'rgba(255,255,255,0.18)', color: '#fff' }}
                >
                  {getInitials(client?.name || 'EF')}
                </Avatar>
              )}
              <Box>
                <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  {scopeType === 'parent' ? 'Parent company dashboard' : 'Client dashboard'}
                </Typography>
                <Typography variant="h4" sx={{ lineHeight: 1.1 }}>
                  {client?.name || 'Express Flooring'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                  Fast view of spend, leads, and sales momentum.
                </Typography>
              </Box>
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.25}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={showFee}
                    onChange={(e) => setShowFee(e.target.checked)}
                    color="default"
                  />
                }
                label="Include fees"
                sx={{
                  ml: 0,
                  mr: { xs: 0, sm: 1 },
                  color: '#fff',
                  '& .MuiTypography-root': { color: '#fff' },
                }}
              />
              {isStaff && scopeType === 'client' && (
                <Button
                  variant="contained"
                  size="small"
                  color="success"
                  disabled={!clientPk || scopeType !== 'client' || backfillLoading}
                  onClick={handleBackfill}
                >
                  {backfillLoading ? <CircularProgress size={16} color="inherit" /> : 'Refresh range'}
                </Button>
              )}
            </Stack>

          </Stack>
        </Box>
        <CardContent sx={{ p: 3, backgroundColor: 'background.paper' }}>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {heroStats.map((item) => (
              <Paper
                key={item.label}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  minWidth: 180,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flex: '1 1 200px',
                }}
              >
                <Avatar
                  variant="rounded"
                  sx={{
                    width: 38,
                    height: 38,
                    bgcolor: 'primary.light',
                    color: 'primary.dark',
                  }}
                >
                  {item.icon}
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {item.value}
                  </Typography>
                  {item.helper && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.helper}
                    </Typography>
                  )}
                </Box>
              </Paper>
            ))}
          </Stack>
          {budgetTotals && (
            <Box sx={{ mt: 2.5, width: '100%', maxWidth: 820 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={3}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">
                      Pacing
                    </Typography>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={3}
                      alignItems={{ xs: 'flex-start', sm: 'baseline' }}
                    >
                      <Box>
                    <Typography variant="h4">
                      <PercentageFormatter
                        value={budgetTotals.pacing_percentage}
                        isRatio={false}
                      />
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pacing %
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h5">
                      <DollarFormatter value={budgetTotals.budget_remaining} />
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Remaining
                    </Typography>
                  </Box>
                </Stack>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  mt={1.5}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    Projected Spend: <DollarFormatter value={budgetTotals.projected_spend} />
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    MTD (Thru Yday): <DollarFormatter value={budgetTotals.month_to_date_spend} />
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    Yesterday Spend: <DollarFormatter value={budgetTotals.yesterday_spend} />
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    Budget: <DollarFormatter value={budgetTotals.budget_amount} />
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    Remaining / Day:{' '}
                    <DollarFormatter
                      value={overallRemainingPerDay}
                    />
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    Days Left In Month: {overallDaysLeftInMonth}
                  </Typography>
                    </Stack>
                    {pacingDonut?.overage ? (
                      <Typography variant="body2" color="error.main" mt={1}>
                        Over budget by <DollarFormatter value={pacingDonut.overage} />
                      </Typography>
                    ) : null}
                  </Box>
                {pacingDonut ? (
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ width: 140, height: 140, position: 'relative' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pacingDonut.data}
                            dataKey="value"
                            innerRadius="65%"
                            outerRadius="85%"
                            stroke="none"
                            startAngle={90}
                            endAngle={-270}
                          >
                            <Cell fill={theme.palette.grey[300]} />
                            <Cell fill={theme.palette.success.main} />
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          pointerEvents: 'none',
                        }}
                      >
                        <Typography variant="subtitle1">
                          {Number.isFinite(pacingDonut.remainingPercent)
                            ? pacingDonut.remainingPercent.toFixed(1)
                            : '0.0'}
                          %
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Remaining
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ width: 140, height: 140, position: 'relative' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pacingDonut.pacingData}
                            dataKey="value"
                            innerRadius="65%"
                            outerRadius="85%"
                            stroke="none"
                            startAngle={90}
                            endAngle={-270}
                          >
                            <Cell fill={theme.palette.primary.main} />
                            <Cell fill={theme.palette.grey[300]} />
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          pointerEvents: 'none',
                        }}
                      >
                        <Typography variant="subtitle1">
                          {Number.isFinite(pacingDonut.pacingPercent)
                            ? pacingDonut.pacingPercent.toFixed(1)
                            : '0.0'}
                          %
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Pacing
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                ) : null}
                </Stack>
              </Paper>
            </Box>
          )}
        </CardContent>
      </Card>

      <Paper
        sx={{
          ...stickyControlsSx,
          backgroundColor: 'background.paper',
          p: { xs: 1.25, md: 1.5 },
          mb: 2,
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', lg: 'center' }}
          justifyContent="space-between"
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            sx={{ width: '100%' }}
          >
            <Box sx={{ minWidth: { xs: '100%', md: 320 } }}>
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onChange={handleDateRangeChange}
                accentColor={brandAccent || undefined}
                style={{ width: '100%' }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              Range: {selectedRangeLabel}
            </Typography>
            {(scopeType === 'client' || selectedScopeClientId) && (
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                <Typography variant="caption" color="text.secondary">
                  Backend data updated:{' '}
                  <Typography component="span" variant="caption" fontWeight={700} color="text.primary">
                    {backendDataUpdatedDate || 'Unknown'}
                  </Typography>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Mapping tasks <IngestionStatusIndicator ingestionHealth={activeIngestionHealth} />
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Latest processed file:{' '}
                  <Typography component="span" variant="caption" fontWeight={700} color="text.primary">
                    {latestProcessedAtLabel || 'Unknown'}
                  </Typography>
                </Typography>
              </Stack>
            )}
            {scopeType === 'parent' && (
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="scope-client-filter-label">Client Filter</InputLabel>
                <Select
                  labelId="scope-client-filter-label"
                  value={selectedScopeClientId}
                  label="Client Filter"
                  onChange={(e) => setSelectedScopeClientId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>All clients</em>
                  </MenuItem>
                  {scopeClientOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>

          {isStaff && scopeType === 'client' && backfillMessage && (
            <Typography
              variant="caption"
              sx={{
                color:
                  backfillMessage.type === 'error'
                    ? 'error.main'
                    : backfillMessage.type === 'success'
                      ? 'success.main'
                      : 'text.secondary',
                maxWidth: { xs: '100%', lg: 320 },
              }}
            >
              {backfillMessage.message}
            </Typography>
          )}
        </Stack>
      </Paper>

      {isStaff && budgetTotals && (
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="overline" color="text.secondary">
                Segment Pacing
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small" sx={compactTableSx}>
                  <TableHead>
                    <TableRow>
                      {scopeType === 'parent' && <TableCell>Client</TableCell>}
                      <TableCell>Segment</TableCell>
                      {segmentPacingVisibleColumns.showVertical && (
                        <TableCell>Vertical</TableCell>
                      )}
                      {segmentPacingVisibleColumns.showLocation && (
                        <TableCell>Location</TableCell>
                      )}
                      {segmentPacingVisibleColumns.showChannel && (
                        <TableCell>Channel</TableCell>
                      )}
                      <TableCell>Budget</TableCell>
                      <TableCell>Adjustments</TableCell>
                      <TableCell>MTD (Thru Yday)</TableCell>
                      <TableCell>Yesterday Spend</TableCell>
                      <TableCell>Projected Spend</TableCell>
                      <TableCell>Remaining Spend</TableCell>
                      <TableCell>Remaining Spend By Day</TableCell>
                      <TableCell>Days Left</TableCell>
                      <TableCell>Pacing %</TableCell>
                      <TableCell>% Remaining</TableCell>
                      <TableCell>30D Net Rev</TableCell>
                      <TableCell>30D CoM</TableCell>
                      <TableCell>60D Net Rev</TableCell>
                      <TableCell>60D CoM</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {segmentPacingTableRows.length ? (
                      <>
                        {segmentPacingTableRows.map((row) => (
                        <TableRow key={row.key}>
                          {scopeType === 'parent' && (
                            <TableCell>{row.client_name || '-'}</TableCell>
                          )}
                          <TableCell>{row.segment_name}</TableCell>
                          {segmentPacingVisibleColumns.showVertical && (
                            <TableCell>{row.vertical}</TableCell>
                          )}
                          {segmentPacingVisibleColumns.showLocation && (
                            <TableCell>{row.location}</TableCell>
                          )}
                          {segmentPacingVisibleColumns.showChannel && (
                            <TableCell>{row.channel}</TableCell>
                          )}
                          <TableCell>
                            <DollarFormatter value={row.budget_amount} />
                          </TableCell>
                          <TableCell>
                            <DollarFormatter value={row.adjustment_total} />
                          </TableCell>
                          <TableCell>
                            <DollarFormatter value={row.month_to_date_spend} />
                          </TableCell>
                          <TableCell>
                            <DollarFormatter value={row.yesterday_spend} />
                          </TableCell>
                          <TableCell>
                            <DollarFormatter value={row.projected_spend} />
                          </TableCell>
                          <TableCell>
                            <DollarFormatter value={row.budget_remaining} />
                          </TableCell>
                          <TableCell>
                            <DollarFormatter value={row.remaining_spend_per_day} />
                          </TableCell>
                          <TableCell>{overallDaysLeftInMonth}</TableCell>
                          <TableCell>
                            <PercentageFormatter value={row.pacing_percentage} isRatio={false} />
                          </TableCell>
                          <TableCell>
                            <PercentageFormatter
                              value={
                                row.budget_amount > 0
                                  ? (row.budget_remaining / row.budget_amount) * 100
                                  : 0
                              }
                              isRatio={false}
                            />
                          </TableCell>
                          <TableCell>
                            <DollarFormatter value={row.last_30_net_revenue} />
                          </TableCell>
                          <TableCell>
                            <PercentageFormatter value={row.last_30_cost_of_marketing} />
                          </TableCell>
                          <TableCell>
                            <DollarFormatter value={row.last_60_net_revenue} />
                          </TableCell>
                          <TableCell>
                            <PercentageFormatter value={row.last_60_cost_of_marketing} />
                          </TableCell>
                        </TableRow>
                        ))}
                        <TableRow>
                          {scopeType === 'parent' && <TableCell sx={{ fontWeight: 700 }}>-</TableCell>}
                          <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                          {segmentPacingVisibleColumns.showVertical && (
                            <TableCell sx={{ fontWeight: 700 }}>-</TableCell>
                          )}
                          {segmentPacingVisibleColumns.showLocation && (
                            <TableCell sx={{ fontWeight: 700 }}>-</TableCell>
                          )}
                          {segmentPacingVisibleColumns.showChannel && (
                            <TableCell sx={{ fontWeight: 700 }}>-</TableCell>
                          )}
                          <TableCell sx={{ fontWeight: 700 }}>
                            <DollarFormatter value={segmentPacingTotals.budget_amount} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            <DollarFormatter value={segmentPacingTotals.adjustment_total} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            <DollarFormatter value={segmentPacingTotals.month_to_date_spend} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            <DollarFormatter value={segmentPacingTotals.yesterday_spend} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            <DollarFormatter value={segmentPacingTotals.projected_spend} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            <DollarFormatter value={segmentPacingTotals.budget_remaining} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            <DollarFormatter value={segmentPacingTotals.remaining_spend_per_day} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            {overallDaysLeftInMonth}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            <PercentageFormatter
                              value={segmentPacingTotals.pacing_percentage}
                              isRatio={false}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            <PercentageFormatter
                              value={segmentPacingTotals.percent_remaining}
                              isRatio={false}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            <DollarFormatter value={segmentPacingTotals.last_30_net_revenue} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            <PercentageFormatter value={segmentPacingTotals.last_30_cost_of_marketing} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            <DollarFormatter value={segmentPacingTotals.last_60_net_revenue} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            <PercentageFormatter value={segmentPacingTotals.last_60_cost_of_marketing} />
                          </TableCell>
                        </TableRow>
                      </>
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={
                            (scopeType === 'parent' ? 1 : 0) +
                            1 + // Segment
                            (segmentPacingVisibleColumns.showVertical ? 1 : 0) +
                            (segmentPacingVisibleColumns.showLocation ? 1 : 0) +
                            (segmentPacingVisibleColumns.showChannel ? 1 : 0) +
                            14 // Budget, Adjustments, MTD, Yesterday, Projected, Remaining, Remaining/Day, Days Left, Pacing%, %Remaining, 30D Net Rev, 30D CoM, 60D Net Rev, 60D CoM
                          }
                          align="center"
                        >
                          No segment budgets found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Section
            title="Performance Over Time"
            subtitle="Spend, leads, booked, issues and demos by day."
          >
            <Box sx={{ height: 320, p: 2 }}>
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={showFee ? chartData : summaryByDay}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis yAxisId="left" tickFormatter={formatAxisCurrency} />
                    <YAxis yAxisId="right" orientation="right" />
                    <RechartsTooltip formatter={tooltipFormatter} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey={showFee ? 'spendWithFees' : 'total_spend_sum'}
                      name={withFees('Spend')}
                      stroke="#8884d8"
                      yAxisId="left"
                    />
                    <Line type="monotone" dataKey="total_leads_sum" name="Leads" stroke="#82ca9d" yAxisId="right" />
                    <Line type="monotone" dataKey="booked_sum" name="Booked" stroke="#ffc658" yAxisId="right" />
                    <Line type="monotone" dataKey="issues_sum" name="Issues" stroke="#ff7300" yAxisId="right" />
                    <Line type="monotone" dataKey="demos_sum" name="Demos" stroke="#413ea0" yAxisId="right" />
                    <Line type="monotone" dataKey="net_sales_sum" name="Net Sold" stroke="#00C49F" yAxisId="right" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Section>
        </Grid>
        {scopeType === 'parent' && (
          <Grid item xs={12}>
            <Section
              title="Clients"
              subtitle="Rollup by client within this parent company."
              defaultExpanded={false}
            >
              <Box sx={{ p: 2 }}>
                <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={compactTableSx}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Client</TableCell>
                        <TableCell>{withFees('Spend')}</TableCell>
                        <TableCell>Leads</TableCell>
                        <TableCell>CPL</TableCell>
                        <TableCell>Self Book</TableCell>
                        <TableCell>CP Self Book</TableCell>
                        <TableCell>Accepted</TableCell>
                        <TableCell>Accepted Rate</TableCell>
                        <TableCell>Success</TableCell>
                        <TableCell>Success Rate</TableCell>
                        <TableCell>Out of Area</TableCell>
                        <TableCell>Out of Area Rate</TableCell>
                        <TableCell>Booked</TableCell>
                        <TableCell>CP Booked</TableCell>
                        <TableCell>Set Rate</TableCell>
                        <TableCell>Issues</TableCell>
                        <TableCell>CP Issue</TableCell>
                        <TableCell>Issue Rate</TableCell>
                        <TableCell>Lead To Issue</TableCell>
                        <TableCell>Demos</TableCell>
                        <TableCell>CP Demo</TableCell>
                        <TableCell>Demo Rate</TableCell>
                        <TableCell>Gross Sold</TableCell>
                        <TableCell>Net Sold</TableCell>
                        <TableCell>Net Close Rate</TableCell>
                        <TableCell>Net Amount</TableCell>
                        <TableCell>Avg Deal</TableCell>
                        <TableCell>Cost of Marketing</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parentClientRows.length ? (
                        parentClientRows.map((row) => {
                          const baseSpend = row.total_spend_sum || 0;
                          const spend = showFee ? baseSpend * (1 + (row.feeRate || 0)) : baseSpend;
                          const leads = row.total_leads_sum || 0;
                          const selfBook = row.self_books_sum || 0;
                          const accepted = row.accepted_sum || 0;
                          const success = row.success_sum || 0;
                          const outOfArea = row.out_of_areas_count_sum || 0;
                          const booked = row.booked_sum || 0;
                          const issues = row.issues_sum || 0;
                          const scheduled = row.scheduled_sum || 0;
                          const demos = row.demos_sum || 0;
                          const grossSold = row.gross_sales_sum || 0;
                          const netSold = row.net_sales_sum || 0;
                          const netAmount = row.net_amount_sum || 0;
                          return (
                          <TableRow key={row.id}>
                            <TableCell>
                              {row.slug ? (
                                <Link to={`/clients/${row.slug}/dashboard`}>{row.name}</Link>
                              ) : (
                                row.name
                              )}
                            </TableCell>
                            <TableCell><DollarFormatter value={spend} /></TableCell>
                            <TableCell>{leads}</TableCell>
                            <TableCell><DollarFormatter value={safeDiv(spend, leads)} /></TableCell>
                            <TableCell>{selfBook}</TableCell>
                            <TableCell><DollarFormatter value={safeDiv(spend, selfBook)} /></TableCell>
                            <TableCell>{accepted}</TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(accepted, leads)} /></TableCell>
                            <TableCell>{success}</TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(success, leads)} /></TableCell>
                            <TableCell>{outOfArea}</TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(outOfArea, leads)} /></TableCell>
                            <TableCell>{booked}</TableCell>
                            <TableCell><DollarFormatter value={safeDiv(spend, booked)} /></TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(booked, leads)} /></TableCell>
                            <TableCell>{issues}</TableCell>
                            <TableCell><DollarFormatter value={safeDiv(spend, issues)} /></TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(issues, scheduled)} /></TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(issues, leads)} /></TableCell>
                            <TableCell>{demos}</TableCell>
                            <TableCell><DollarFormatter value={safeDiv(spend, demos)} /></TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(demos, issues)} /></TableCell>
                            <TableCell>{grossSold}</TableCell>
                            <TableCell>{netSold}</TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(netSold, demos)} /></TableCell>
                            <TableCell><DollarFormatter value={netAmount} /></TableCell>
                            <TableCell><DollarFormatter value={safeDiv(netAmount, netSold)} /></TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(spend, netAmount)} /></TableCell>
                          </TableRow>
                        )})
                      ) : (
                        <TableRow>
                          <TableCell colSpan={28} align="center">
                            No client rollup data found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Section>
          </Grid>
        )}
        <Grid item xs={12}>
          <Section
            title="MoM Performance"
            subtitle="Month-over-month trendlines for spend, conversion, and revenue."
            defaultExpanded={false}
            onToggle={handleMomToggle}
          >
            <Box sx={{ p: 2 }}>
              {momLoading ? (
                <CircularProgress size={24} />
              ) : (
                <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={compactTableSx}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={stickyHeadCol}>Month</TableCell>
                        <TableCell>{withFees('Spend')}</TableCell>
                        <TableCell>Leads</TableCell>
                        <TableCell>CPL</TableCell>
                        <TableCell>Self Book</TableCell>
                        <TableCell>CP Self Book</TableCell>
                        <TableCell>Accepted</TableCell>
                        <TableCell>Accepted Rate</TableCell>
                        <TableCell>Success</TableCell>
                        <TableCell>Success Rate</TableCell>
                        <TableCell>Out of Area</TableCell>
                        <TableCell>Out of Area Rate</TableCell>
                        <TableCell>Booked</TableCell>
                        <TableCell>CP Booked</TableCell>
                        <TableCell>Set Rate</TableCell>
                        <TableCell>Issues</TableCell>
                        <TableCell>CP Issue</TableCell>
                        <TableCell>Issue Rate</TableCell>
                        <TableCell>Lead To Issue</TableCell>
                        <TableCell>Demos</TableCell>
                        <TableCell>CP Demo</TableCell>
                        <TableCell>Demo Rate</TableCell>
                        <TableCell>Gross Sold</TableCell>
                        <TableCell>Net Sold</TableCell>
                        <TableCell>Net Close Rate</TableCell>
                        <TableCell>Net Amount</TableCell>
                        <TableCell>Avg Deal</TableCell>
                        <TableCell>Cost of Marketing</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {momData.map((m, idx) => {
                        const baseSpend = m.total_spend_sum || 0;
                        const adjSpend = baseSpend * (1 + feeRate);
                        const spend = showFee ? adjSpend : baseSpend;
                        const leads = m.total_leads_sum || 0;
                        const selfBook = m.self_books_sum || 0;
                        const accepted = m.accepted_sum || 0;
                        const success = m.success_sum || 0;
                        const outOfArea = m.out_of_areas_count_sum || 0;
                        const booked = m.booked_sum || 0;
                        const issues = m.issues_sum || 0;
                        const scheduled = m.scheduled_sum || 0;
                        const demos = m.demos_sum || 0;
                        const grossSold = m.gross_sales_sum || m.gross_sold_sum || 0;
                        const netSold = m.net_sales_sum || m.net_sold_sum || 0;
                        const netAmount = m.net_amount_sum || 0;
                        const monthKey = m.month || `month-${idx}`;
                        const detail = momRowDetails[m.month] || {};
                        const startLabel = detail.dateRange?.startDate
                          ? formatDateToYMD(detail.dateRange.startDate)
                          : null;
                        const endLabel = detail.dateRange?.endDate
                          ? formatDateToYMD(detail.dateRange.endDate)
                          : null;

                        return (
                          <Fragment key={monthKey}>
                            <TableRow
                              hover
                              onClick={() => handleToggleMomDetails(m.month, m)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell sx={stickyCol}>{m.month}</TableCell>
                              <TableCell><DollarFormatter value={spend} /></TableCell>
                              <TableCell>{leads}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, leads)} /></TableCell>
                              <TableCell>{selfBook}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, selfBook)} /></TableCell>
                              <TableCell>{accepted}</TableCell>
                              <TableCell><PercentageFormatter value={safeDiv(accepted, leads)} /></TableCell>
                              <TableCell>{success}</TableCell>
                              <TableCell><PercentageFormatter value={safeDiv(success, leads)} /></TableCell>
                              <TableCell>{outOfArea}</TableCell>
                              <TableCell><PercentageFormatter value={safeDiv(outOfArea, leads)} /></TableCell>
                              <TableCell>{booked}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, booked)} /></TableCell>
                              <TableCell><PercentageFormatter value={safeDiv(booked, leads)} /></TableCell>
                              <TableCell>{issues}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, issues)} /></TableCell>
                              <TableCell><PercentageFormatter value={safeDiv(issues, scheduled)} /></TableCell>
                              <TableCell><PercentageFormatter value={safeDiv(issues, leads)} /></TableCell>
                              <TableCell>{demos}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, demos)} /></TableCell>
                              <TableCell><PercentageFormatter value={safeDiv(demos, issues)} /></TableCell>
                              <TableCell>{grossSold}</TableCell>
                              <TableCell>{netSold}</TableCell>
                              <TableCell><PercentageFormatter value={safeDiv(netSold, demos)} /></TableCell>
                              <TableCell><DollarFormatter value={netAmount} /></TableCell>
                              <TableCell><DollarFormatter value={safeDiv(netAmount, netSold)} /></TableCell>
                              <TableCell><PercentageFormatter value={safeDiv(spend, netAmount)} /></TableCell>
                            </TableRow>
                            {detail.expanded && (
                              <TableRow>
                                <TableCell colSpan={momColumnCount} sx={{ p: 0, bgcolor: 'background.default' }}>
                                  <Box
                                    sx={{
                                      px: 2,
                                      py: 1.5,
                                      borderTop: '1px solid',
                                      borderColor: 'divider',
                                      bgcolor: 'background.paper',
                                    }}
                                  >
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                                      <Typography variant="subtitle2" fontWeight={700}>
                                        {m.month} records
                                      </Typography>
                                      <ToggleButtonGroup
                                        size="small"
                                        color="primary"
                                        value={detail.activeTab || 'backend'}
                                        exclusive
                                        onChange={(_, val) => val && handleDrilldownTabChange(m.month, val)}
                                      >
                                        <ToggleButton value="backend">Backend</ToggleButton>
                                        <ToggleButton value="leads">Leads</ToggleButton>
                                      </ToggleButtonGroup>
                                    </Stack>
                                    {(startLabel || endLabel) && (
                                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                        Range: {startLabel || '?'} – {endLabel || '?'}
                                      </Typography>
                                    )}
                                    {(() => {
                                      const counts = computeBackendCounts(detail);
                                      return (
                                        <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                                          <Typography variant="caption" color="text.secondary">
                                            Booked: <Typography component="span" variant="caption" fontWeight={700}>{counts.bookedCount}</Typography>
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Scheduled: <Typography component="span" variant="caption" fontWeight={700}>{counts.apptCount}</Typography>
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Issue: <Typography component="span" variant="caption" fontWeight={700}>{counts.issueCount}</Typography>
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Demo: <Typography component="span" variant="caption" fontWeight={700}>{counts.demoCount}</Typography>
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Gross: <Typography component="span" variant="caption" fontWeight={700}>{counts.soldCount}</Typography>
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Net: <Typography component="span" variant="caption" fontWeight={700}>{counts.netCount}</Typography>
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Gross $: <Typography component="span" variant="caption" fontWeight={700}>{counts.grossSum.toLocaleString()}</Typography>
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Net $: <Typography component="span" variant="caption" fontWeight={700}>{counts.netSum.toLocaleString()}</Typography>
                                          </Typography>
                                        </Stack>
                                      );
                                    })()}
                                    {detail.loading ? (
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        <CircularProgress size={18} />
                                        <Typography variant="body2" color="text.secondary">
                                          Loading records…
                                        </Typography>
                                      </Stack>
                                    ) : detail.error ? (
                                      <Typography variant="body2" color="error">
                                        {detail.error}
                                      </Typography>
                                    ) : detail.activeTab === 'leads' ? (
                                      <Box sx={{ width: '100%' }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                          Lead records ({detail.leads?.length || 0})
                                        </Typography>
                                        <TableContainer component={Paper} sx={{ maxHeight: 320 }}>
                                          <Table size="small" stickyHeader>
                                            <TableHead>
                                              <TableRow>
                                                <TableCell sx={{ width: 140 }}>Date Created (EST)</TableCell>
                                                <TableCell sx={{ width: 160 }}>Name</TableCell>
                                                <TableCell sx={{ width: 140 }}>Phone</TableCell>
                                                <TableCell sx={{ width: 200 }}>Email</TableCell>
                                                <TableCell sx={{ width: 140 }}>Source</TableCell>
                                                <TableCell sx={{ width: 140 }}>Territory</TableCell>
                                                <TableCell sx={{ width: 140 }}>Vertical</TableCell>
                                                <TableCell sx={{ width: 120 }}>ZIP</TableCell>
                                                <TableCell sx={{ width: 160 }}>Self-Book Appt</TableCell>
                                                <TableCell sx={{ width: 100 }}>Success</TableCell>
                                                <TableCell sx={{ width: 180 }}>Post Response</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {(detail.leads || []).map((lead) => {
                                                const leadName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'View';
                                                return (
                                                  <TableRow key={lead.id || `${leadName}-${lead.date_created}`}>
                                                    <TableCell>{formatToEST(lead.date_created)}</TableCell>
                                                    <TableCell>
                                                      {lead.id ? (
                                                        <Link
                                                          to={`/leads/${lead.id}`}
                                                          style={{ textDecoration: 'none', color: '#2f80ed' }}
                                                        >
                                                          {leadName}
                                                        </Link>
                                                      ) : (
                                                        leadName
                                                      )}
                                                    </TableCell>
                                                    <TableCell>{lead.phone || '-'}</TableCell>
                                                    <TableCell>{lead.email || '-'}</TableCell>
                                                      <TableCell>{lead.source || '-'}</TableCell>
                                                      <TableCell>{lead.territory || '-'}</TableCell>
                                                      <TableCell>{lead.vertical || '-'}</TableCell>
                                                      <TableCell>{lead.zip_code || '-'}</TableCell>
                                                      <TableCell>{lead.self_book_appointment_date_time || '-'}</TableCell>
                                                      <TableCell>{lead.success_post ? '✅' : '❌'}</TableCell>
                                                      <TableCell>{lead.post_response || '-'}</TableCell>
                                                    </TableRow>
                                                  );
                                                })}
                                              {!detail.leads?.length && (
                                                <TableRow>
                                                    <TableCell colSpan={11} align="center">
                                                      No lead records in this month.
                                                    </TableCell>
                                                  </TableRow>
                                                )}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                          <TablePagination
                                            component="div"
                                            rowsPerPageOptions={[50]}
                                            rowsPerPage={50}
                                            count={detail.leadsCount || 0}
                                            page={detail.leadsPage || 0}
                                            onPageChange={(_, newPage) => handleMomPageChange(m.month, 'leads', newPage)}
                                          />
                                        </Box>
                                      </Box>
                                    ) : (
                                      <Box sx={{ width: '100%' }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                          Backend data ({detail.backend?.length || 0})
                                        </Typography>
                                        <TableContainer component={Paper} sx={{ maxHeight: 320 }}>
                                          <Table size="small" stickyHeader>
                                            <TableHead>
                                              <TableRow>
                                                <TableCell sx={{ width: 130 }}>Sold Date</TableCell>
                                                <TableCell sx={{ width: 140 }}>Appointment Date</TableCell>
                                                <TableCell sx={{ width: 140 }}>Phone</TableCell>
                                                <TableCell sx={{ width: 240 }}>Email</TableCell>
                                                <TableCell sx={{ width: 80 }}>Issue</TableCell>
                                                <TableCell sx={{ width: 80 }}>Demo</TableCell>
                                                <TableCell sx={{ width: 120 }}>Gross</TableCell>
                                                <TableCell sx={{ width: 120 }}>Net</TableCell>
                                                <TableCell sx={{ width: 180 }}>Status</TableCell>
                                                <TableCell sx={{ width: 170 }}>Vertical</TableCell>
                                                <TableCell sx={{ width: 140 }}>ZIP</TableCell>
                                                <TableCell sx={{ width: 170 }}>Territory</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {(() => {
                                                const key = 'sold_date';
                                                const desc = true;
                                                const rangeStart = detail.dateRange?.startDate || null;
                                                const rangeEnd = detail.dateRange?.endDate || null;
                                                const getMatchFlags = (row) => {
                                                  if (!rangeStart || !rangeEnd) {
                                                    return { soldOk: true, apptOk: true };
                                                  }
                                                  const soldOk = isDateInRange(row?.sold_date, rangeStart, rangeEnd);
                                                  const apptOk = isDateInRange(row?.appointment_date, rangeStart, rangeEnd);
                                                  return { soldOk, apptOk };
                                                };
                                                const getTime = (val) => {
                                                  const t = val ? new Date(val).getTime() : 0;
                                                  return Number.isFinite(t) ? t : 0;
                                                };
                                                const rowsWithFlags = [...(detail.backendFull || detail.backend || [])].map((row) => ({
                                                  row,
                                                  ...getMatchFlags(row),
                                                }));
                                                const rows = rowsWithFlags.filter((item) => item.soldOk || item.apptOk);
                                                const pageSize = BACKEND_PAGE_SIZE;
                                                const page = detail.backendPage || 0;
                                                const withSold = rows
                                                  .filter((item) => item.soldOk && item.row?.sold_date)
                                                  .sort((a, b) => {
                                                    const aVal = getTime(a.row?.[key]);
                                                    const bVal = getTime(b.row?.[key]);
                                                    return desc ? bVal - aVal : aVal - bVal;
                                                  });
                                                const apptOnly = rows
                                                  .filter((item) => item.apptOk && !item.soldOk && item.row?.appointment_date)
                                                  .sort((a, b) => {
                                                    const aVal = getTime(a.row?.appointment_date);
                                                    const bVal = getTime(b.row?.appointment_date);
                                                    return desc ? bVal - aVal : aVal - bVal;
                                                  });
                                                const orderedRows = [...withSold, ...apptOnly].map((item) => item.row);
                                                const startIdx = page * pageSize;
                                                const pagedRows = orderedRows.slice(startIdx, startIdx + pageSize);
                                                if (!orderedRows.length) {
                                                  return (
                                                    <TableRow>
                                                      <TableCell colSpan={12} align="center">
                                                        No backend data for this month.
                                                      </TableCell>
                                                    </TableRow>
                                                  );
                                                }
                                                return pagedRows.map((row) => (
                                                  <TableRow key={row.unique_id || row.id}>
                                                    <TableCell>{row.sold_date || '-'}</TableCell>
                                                    <TableCell>{row.appointment_date || '-'}</TableCell>
                                                    <TableCell>{row.phone || '-'}</TableCell>
                                                    <TableCell>{row.email || '-'}</TableCell>
                                                    <TableCell>{row.issue ? '✅' : '❌'}</TableCell>
                                                    <TableCell>{row.demo ? '✅' : '❌'}</TableCell>
                                                    <TableCell>
                                                      {row.gross_amount ? `$${row.gross_amount}` : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                      {row.net_amount ? `$${row.net_amount}` : '-'}
                                                    </TableCell>
                                                    <TableCell>{row.status || '-'}</TableCell>
                                                    <TableCell>{row.vertical || '-'}</TableCell>
                                                    <TableCell>
                                                      {row.zip_code ||
                                                        row.client_zip_vertical_zip_code ||
                                                        row.client_zip_vertical?.zip_code ||
                                                        '-'}
                                                    </TableCell>
                                                    <TableCell>{formatTerritory(row)}</TableCell>
                                                  </TableRow>
                                                ));
                                              })()}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                          <TablePagination
                                            component="div"
                                            rowsPerPageOptions={[50]}
                                            rowsPerPage={50}
                                            count={detail.backendCount ?? (detail.backendFull || detail.backend || []).length}
                                            page={detail.backendPage || 0}
                                            onPageChange={(_, newPage) => handleMomPageChange(m.month, 'backend', newPage)}
                                          />
                                        </Box>
                                      </Box>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section
            title="DoD Performance"
            subtitle="Daily performance window—toggle the range to stay focused."
            defaultExpanded={false}
            action={(
              <ToggleButtonGroup
                color="primary"
                value={dayRange}
                exclusive
                onChange={handleDayRangeChange}
                size="small"
              >
                {dayRangeOptions.map((option) => (
                  <ToggleButton key={option} value={option}>
                    {option}d
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
          >
            <Box sx={{ p: 2 }}>
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={compactTableSx}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={stickyHeadCol}>Day</TableCell>
                        <TableCell>{withFees('Spend')}</TableCell>
                        <TableCell>Leads</TableCell>
                        <TableCell>CPL</TableCell>
                        <TableCell>Self Book</TableCell>
                        <TableCell>CP Self Book</TableCell>
                        <TableCell>Accepted</TableCell>
                        <TableCell>Accepted Rate</TableCell>
                        <TableCell>Success</TableCell>
                        <TableCell>Success Rate</TableCell>
                        <TableCell>Out of Area</TableCell>
                        <TableCell>Out of Area Rate</TableCell>
                        <TableCell>Booked</TableCell>
                        <TableCell>CP Booked</TableCell>
                        <TableCell>Set Rate</TableCell>
                        <TableCell>Issues</TableCell>
                        <TableCell>CP Issue</TableCell>
                        <TableCell>Issue Rate</TableCell>
                        <TableCell>Lead To Issue</TableCell>
                        <TableCell>Demos</TableCell>
                        <TableCell>CP Demo</TableCell>
                        <TableCell>Demo Rate</TableCell>
                        <TableCell>Gross Sold</TableCell>
                        <TableCell>Net Sold</TableCell>
                        <TableCell>Net Close Rate</TableCell>
                        <TableCell>Net Amount</TableCell>
                        <TableCell>Avg Deal</TableCell>
                        <TableCell>Cost of Marketing</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dailySummaryWindow.map((day) => {
                        const displayDate = formatDayLabel(day.date);
                        const baseSpend = day.total_spend_sum || 0;
                        const adjSpend = baseSpend * (1 + feeRate);
                        const spend = showFee ? adjSpend : baseSpend;
                        const leads = day.total_leads_sum || 0;
                        const selfBook = day.self_books_sum || 0;
                        const accepted = day.accepted_sum || 0;
                        const success = day.success_sum || 0;
                        const outOfArea = day.out_of_areas_count_sum || 0;
                        const booked = day.booked_sum || 0;
                        const issues = day.issues_sum || 0;
                        const scheduled = day.scheduled_sum || 0;
                        const demos = day.demos_sum || 0;
                        const grossSold = day.gross_sales_sum || day.gross_sold_sum || 0;
                        const netSold = day.net_sales_sum || day.net_sold_sum || 0;
                        const netAmount = day.net_amount_sum || 0;
                        const dayKey = day.date || displayDate;
                        const detail = dodRowDetails[dayKey] || {};
                        const startLabel = detail.dateRange?.startDate
                          ? formatDateToYMD(detail.dateRange.startDate)
                          : null;
                        const endLabel = detail.dateRange?.endDate
                          ? formatDateToYMD(detail.dateRange.endDate)
                          : null;

                        return (
                          <Fragment key={dayKey}>
                            <TableRow
                              hover
                              onClick={() => handleToggleDayDetails(dayKey, day)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell sx={stickyCol}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <ExpandMoreIcon
                                    fontSize="small"
                                    sx={{
                                      transform: detail.expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                      transition: 'transform 120ms ease',
                                    }}
                                  />
                                  <Box component="span">{displayDate}</Box>
                                </Stack>
                              </TableCell>
                              <TableCell><DollarFormatter value={spend} /></TableCell>
                              <TableCell>{leads}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, leads)} /></TableCell>
                              <TableCell>{selfBook}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, selfBook)} /></TableCell>
                              <TableCell>{accepted}</TableCell>
                              <TableCell><PercentageFormatter value={safeDiv(accepted, leads)} /></TableCell>
                              <TableCell>{success}</TableCell>
                              <TableCell><PercentageFormatter value={safeDiv(success, leads)} /></TableCell>
                              <TableCell>{outOfArea}</TableCell>
                              <TableCell><PercentageFormatter value={safeDiv(outOfArea, leads)} /></TableCell>
                              <TableCell>{booked}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, booked)} /></TableCell>
                              <TableCell><PercentageFormatter value={safeDiv(booked, leads)} /></TableCell>
                              <TableCell>{issues}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, issues)} /></TableCell>
                              <TableCell><PercentageFormatter value={safeDiv(issues, scheduled)} /></TableCell>
                              <TableCell><PercentageFormatter value={safeDiv(issues, leads)} /></TableCell>
                              <TableCell>{demos}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, demos)} /></TableCell>
                              <TableCell><PercentageFormatter value={safeDiv(demos, issues)} /></TableCell>
                              <TableCell>{grossSold}</TableCell>
                              <TableCell>{netSold}</TableCell>
                              <TableCell><PercentageFormatter value={safeDiv(netSold, demos)} /></TableCell>
                              <TableCell><DollarFormatter value={netAmount} /></TableCell>
                              <TableCell><DollarFormatter value={safeDiv(netAmount, netSold)} /></TableCell>
                              <TableCell><PercentageFormatter value={safeDiv(spend, netAmount)} /></TableCell>
                            </TableRow>
                            {detail.expanded && (
                              <TableRow>
                                <TableCell colSpan={dodColumnCount} sx={{ p: 0, bgcolor: 'background.default' }}>
                                  <Box
                                    sx={{
                                      px: 2,
                                      py: 1.5,
                                      borderTop: '1px solid',
                                      borderColor: 'divider',
                                      bgcolor: 'background.paper',
                                    }}
                                  >
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                                      <Typography variant="subtitle2" fontWeight={700}>
                                        {displayDate} records
                                      </Typography>
                                      <ToggleButtonGroup
                                        size="small"
                                        color="primary"
                                        value={detail.activeTab || 'backend'}
                                        exclusive
                                        onChange={(_, val) => val && handleDodTabChange(dayKey, val)}
                                      >
                                        <ToggleButton value="backend">Backend</ToggleButton>
                                        <ToggleButton value="leads">Leads</ToggleButton>
                                      </ToggleButtonGroup>
                                    </Stack>
                                    {(startLabel || endLabel) && (
                                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                        Range: {startLabel || '?'} – {endLabel || '?'}
                                      </Typography>
                                    )}
                                    {(() => {
                                      const counts = computeBackendCounts(detail);
                                      return (
                                        <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                                          <Typography variant="caption" color="text.secondary">
                                            Booked: <Typography component="span" variant="caption" fontWeight={700}>{counts.bookedCount}</Typography>
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Scheduled: <Typography component="span" variant="caption" fontWeight={700}>{counts.apptCount}</Typography>
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Issue: <Typography component="span" variant="caption" fontWeight={700}>{counts.issueCount}</Typography>
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Demo: <Typography component="span" variant="caption" fontWeight={700}>{counts.demoCount}</Typography>
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Gross: <Typography component="span" variant="caption" fontWeight={700}>{counts.soldCount}</Typography>
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Net: <Typography component="span" variant="caption" fontWeight={700}>{counts.netCount}</Typography>
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Gross $: <Typography component="span" variant="caption" fontWeight={700}>{counts.grossSum.toLocaleString()}</Typography>
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Net $: <Typography component="span" variant="caption" fontWeight={700}>{counts.netSum.toLocaleString()}</Typography>
                                          </Typography>
                                        </Stack>
                                      );
                                    })()}
                                    {detail.loading ? (
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        <CircularProgress size={18} />
                                        <Typography variant="body2" color="text.secondary">
                                          Loading records…
                                        </Typography>
                                      </Stack>
                                    ) : detail.error ? (
                                      <Typography variant="body2" color="error">
                                        {detail.error}
                                      </Typography>
                                    ) : detail.activeTab === 'leads' ? (
                                      <Box sx={{ width: '100%' }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                          Lead records ({detail.leads?.length || 0})
                                        </Typography>
                                        <TableContainer component={Paper} sx={{ maxHeight: 320 }}>
                                          <Table size="small" stickyHeader>
                                            <TableHead>
                                              <TableRow>
                                                <TableCell sx={{ width: 140 }}>Date Created (EST)</TableCell>
                                                <TableCell sx={{ width: 160 }}>Name</TableCell>
                                                <TableCell sx={{ width: 140 }}>Phone</TableCell>
                                                <TableCell sx={{ width: 200 }}>Email</TableCell>
                                                <TableCell sx={{ width: 140 }}>Source</TableCell>
                                                <TableCell sx={{ width: 140 }}>Territory</TableCell>
                                                <TableCell sx={{ width: 140 }}>Vertical</TableCell>
                                                <TableCell sx={{ width: 120 }}>ZIP</TableCell>
                                                <TableCell sx={{ width: 160 }}>Self-Book Appt</TableCell>
                                                <TableCell sx={{ width: 100 }}>Success</TableCell>
                                                <TableCell sx={{ width: 180 }}>Post Response</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {(detail.leads || []).map((lead) => {
                                                const leadName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'View';
                                                return (
                                                  <TableRow key={lead.id || `${leadName}-${lead.date_created}`}>
                                                    <TableCell>{formatToEST(lead.date_created)}</TableCell>
                                                    <TableCell>
                                                      {lead.id ? (
                                                        <Link
                                                          to={`/leads/${lead.id}`}
                                                          style={{ textDecoration: 'none', color: '#2f80ed' }}
                                                          onClick={(e) => e.stopPropagation()}
                                                        >
                                                          {leadName}
                                                        </Link>
                                                      ) : (
                                                        leadName
                                                      )}
                                                    </TableCell>
                                                    <TableCell>{lead.phone || '-'}</TableCell>
                                                    <TableCell>{lead.email || '-'}</TableCell>
                                                    <TableCell>{lead.source || '-'}</TableCell>
                                                    <TableCell>{lead.territory || '-'}</TableCell>
                                                    <TableCell>{lead.vertical || '-'}</TableCell>
                                                    <TableCell>{lead.zip_code || '-'}</TableCell>
                                                    <TableCell>{lead.self_book_appointment_date_time || '-'}</TableCell>
                                                    <TableCell>{lead.success_post ? '✅' : '❌'}</TableCell>
                                                    <TableCell>{lead.post_response || '-'}</TableCell>
                                                  </TableRow>
                                                );
                                              })}
                                              {!detail.leads?.length && (
                                                <TableRow>
                                                  <TableCell colSpan={11} align="center">
                                                    No lead records on this day.
                                                  </TableCell>
                                                </TableRow>
                                              )}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                          <TablePagination
                                            component="div"
                                            rowsPerPageOptions={[50]}
                                            rowsPerPage={50}
                                            count={detail.leadsCount || 0}
                                            page={detail.leadsPage || 0}
                                            onPageChange={(_, newPage) => handleDodPageChange(dayKey, 'leads', newPage)}
                                          />
                                        </Box>
                                      </Box>
                                    ) : (
                                      <Box sx={{ width: '100%' }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                          Backend data ({detail.backend?.length || 0})
                                        </Typography>
                                        <TableContainer component={Paper} sx={{ maxHeight: 320 }}>
                                          <Table size="small" stickyHeader>
                                            <TableHead>
                                              <TableRow>
                                                <TableCell sx={{ width: 130 }}>Sold Date</TableCell>
                                                <TableCell sx={{ width: 140 }}>Appointment Date</TableCell>
                                                <TableCell sx={{ width: 140 }}>Phone</TableCell>
                                                <TableCell sx={{ width: 240 }}>Email</TableCell>
                                                <TableCell sx={{ width: 80 }}>Issue</TableCell>
                                                <TableCell sx={{ width: 80 }}>Demo</TableCell>
                                                <TableCell sx={{ width: 120 }}>Gross</TableCell>
                                                <TableCell sx={{ width: 120 }}>Net</TableCell>
                                                <TableCell sx={{ width: 180 }}>Status</TableCell>
                                                <TableCell sx={{ width: 170 }}>Vertical</TableCell>
                                                <TableCell sx={{ width: 140 }}>ZIP</TableCell>
                                                <TableCell sx={{ width: 170 }}>Territory</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {(() => {
                                                const key = 'sold_date';
                                                const desc = true;
                                                const rangeStart = detail.dateRange?.startDate || null;
                                                const rangeEnd = detail.dateRange?.endDate || null;
                                              const getMatchFlags = (row) => {
                                                if (!rangeStart || !rangeEnd) {
                                                  return { soldOk: true, apptOk: true };
                                                }
                                                const soldOk = isDateInRange(row?.sold_date, rangeStart, rangeEnd);
                                                const apptOk = isDateInRange(row?.appointment_date, rangeStart, rangeEnd);
                                                return { soldOk, apptOk };
                                              };
                                              const getTime = (val) => {
                                                const t = val ? new Date(val).getTime() : 0;
                                                return Number.isFinite(t) ? t : 0;
                                              };
                                              const rowsWithFlags = [...(detail.backendFull || detail.backend || [])].map((row) => ({
                                                row,
                                                ...getMatchFlags(row),
                                              }));
                                              const rows = rowsWithFlags.filter((item) => item.soldOk || item.apptOk);
                                              const pageSize = BACKEND_PAGE_SIZE;
                                              const page = detail.backendPage || 0;
                                              const withSold = rows
                                                .filter((item) => item.soldOk && item.row?.sold_date)
                                                .sort((a, b) => {
                                                  const aVal = getTime(a.row?.[key]);
                                                  const bVal = getTime(b.row?.[key]);
                                                  return desc ? bVal - aVal : aVal - bVal;
                                                });
                                              const apptOnly = rows
                                                .filter((item) => item.apptOk && !item.soldOk && item.row?.appointment_date)
                                                .sort((a, b) => {
                                                  const aVal = getTime(a.row?.appointment_date);
                                                  const bVal = getTime(b.row?.appointment_date);
                                                  return desc ? bVal - aVal : aVal - bVal;
                                                });
                                              const orderedRows = [...withSold, ...apptOnly].map((item) => item.row);
                                              const startIdx = page * pageSize;
                                              const pagedRows = orderedRows.slice(startIdx, startIdx + pageSize);
                                              if (!orderedRows.length) {
                                                return (
                                                  <TableRow>
                                                    <TableCell colSpan={12} align="center">
                                                      No backend data for this day.
                                                    </TableCell>
                                                  </TableRow>
                                                );
                                              }
                                              return pagedRows.map((row) => (
                                                <TableRow key={row.unique_id || row.id}>
                                                  <TableCell>{row.sold_date || '-'}</TableCell>
                                                  <TableCell>{row.appointment_date || '-'}</TableCell>
                                                  <TableCell>{row.phone || '-'}</TableCell>
                                                  <TableCell>{row.email || '-'}</TableCell>
                                                    <TableCell>{row.issue ? '✅' : '❌'}</TableCell>
                                                    <TableCell>{row.demo ? '✅' : '❌'}</TableCell>
                                                    <TableCell>
                                                      {row.gross_amount ? `$${row.gross_amount}` : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                      {row.net_amount ? `$${row.net_amount}` : '-'}
                                                    </TableCell>
                                                    <TableCell>{row.status || '-'}</TableCell>
                                                    <TableCell>{row.vertical || '-'}</TableCell>
                                                    <TableCell>
                                                      {row.zip_code ||
                                                        row.client_zip_vertical_zip_code ||
                                                        row.client_zip_vertical?.zip_code ||
                                                        '-'}
                                                    </TableCell>
                                                    <TableCell>{formatTerritory(row)}</TableCell>
                                                  </TableRow>
                                                ));
                                              })()}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                          <TablePagination
                                            component="div"
                                            rowsPerPageOptions={[50]}
                                            rowsPerPage={50}
                                            count={detail.backendCount ?? (detail.backendFull || detail.backend || []).length}
                                            page={detail.backendPage || 0}
                                            onPageChange={(_, newPage) => handleDodPageChange(dayKey, 'backend', newPage)}
                                          />
                                        </Box>
                                      </Box>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section
            title="Vertical Performance"
            subtitle="Compare spend, efficiency, and pacing across each vertical."
            defaultExpanded={false}
            onToggle={handleBreakdownsToggle}
          >
            <Box sx={{ p: 2 }}>
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={compactTableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={stickyHeadCol}>Vertical</TableCell>
                      <TableCell>{withFees('Spend')}</TableCell>
                      <TableCell>Leads</TableCell>
                      <TableCell>CPL</TableCell>
                      <TableCell>Self Book</TableCell>
                      <TableCell>CP Self Book</TableCell>
                      <TableCell>Accepted</TableCell>
                      <TableCell>Accepted Rate</TableCell>
                      <TableCell>Success</TableCell>
                      <TableCell>Success Rate</TableCell>
                      <TableCell>Out of Area</TableCell>
                      <TableCell>Out of Area Rate</TableCell>
                      <TableCell>Booked</TableCell>
                      <TableCell>CP Booked</TableCell>
                      <TableCell>Set Rate</TableCell>
                      <TableCell>Issues</TableCell>
                      <TableCell>CP Issue</TableCell>
                      <TableCell>Issue Rate</TableCell>
                      <TableCell>Lead To Issue</TableCell>
                      <TableCell>Demos</TableCell>
                      <TableCell>CP Demo</TableCell>
                      <TableCell>Demo Rate</TableCell>
                      <TableCell>Gross Sold</TableCell>
                      <TableCell>Net Sold</TableCell>
                      <TableCell>Net Close Rate</TableCell>
                      <TableCell>Net Amount</TableCell>
                      <TableCell>Avg Deal</TableCell>
                      <TableCell>Revenue per Lead</TableCell>
                      <TableCell>Cost of Marketing</TableCell>
                      <TableCell>Budget</TableCell>
                      <TableCell>Projected Spend</TableCell>
                      <TableCell>Pacing %</TableCell>
                      <TableCell>Remaining Budget</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedVerticals.length === 0 && renderBreakdownsLoadingRow(32)}
                    {sortedVerticals.map((v) => {
                      const name = (v.vertical || '').trim() || 'Unspecified';
                      const budgetInfo = budgetVerticals[name] || {};
                      const baseSpend = v.total_spend_sum || 0;
                      const adjSpend = baseSpend * (1 + feeRate);
                      const spend = showFee ? adjSpend : baseSpend;
                      const leads = v.total_leads_sum || 0;
                      const selfBook = v.self_books_sum || 0;
                      const accepted = v.accepted_sum || 0;
                      const success = v.success_sum || 0;
                      const outOfArea = v.out_of_areas_count_sum || 0;
                      const booked = v.booked_sum || 0;
                      const issues = v.issues_sum || 0;
                      const scheduled = v.scheduled_sum || 0;
                      const demos = v.demos_sum || 0;
                      const grossSold = v.gross_sales_sum || v.gross_sold_sum || 0;
                      const netSold = v.net_sales_sum || v.net_sold_sum || 0;
                      const netAmount = v.net_amount_sum || 0;
                      const detail = verticalDetails[name] || {};
                      const startLabel = currentRange.startDate ? formatDateToYMD(currentRange.startDate) : null;
                      const endLabel = currentRange.endDate ? formatDateToYMD(currentRange.endDate) : null;
                      return (
                        <Fragment key={v.uuid || name}>
                          <TableRow hover onClick={() => handleToggleVerticalRow(name)} sx={{ cursor: 'pointer' }}>
                            <TableCell sx={stickyCol}>{name}</TableCell>
                            <TableCell><DollarFormatter value={spend} /></TableCell>
                            <TableCell>{leads}</TableCell>
                            <TableCell><DollarFormatter value={safeDiv(spend, leads)} /></TableCell>
                            <TableCell>{selfBook}</TableCell>
                            <TableCell><DollarFormatter value={safeDiv(spend, selfBook)} /></TableCell>
                            <TableCell>{accepted}</TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(accepted, leads)} /></TableCell>
                            <TableCell>{success}</TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(success, leads)} /></TableCell>
                            <TableCell>{outOfArea}</TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(outOfArea, leads)} /></TableCell>
                            <TableCell>{booked}</TableCell>
                            <TableCell><DollarFormatter value={safeDiv(spend, booked)} /></TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(booked, leads)} /></TableCell>
                            <TableCell>{issues}</TableCell>
                            <TableCell><DollarFormatter value={safeDiv(spend, issues)} /></TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(issues, scheduled)} /></TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(issues, leads)} /></TableCell>
                            <TableCell>{demos}</TableCell>
                            <TableCell><DollarFormatter value={safeDiv(spend, demos)} /></TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(demos, issues)} /></TableCell>
                            <TableCell>{grossSold}</TableCell>
                            <TableCell>{netSold}</TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(netSold, demos)} /></TableCell>
                            <TableCell><DollarFormatter value={netAmount} /></TableCell>
                            <TableCell><DollarFormatter value={safeDiv(netAmount, netSold)} /></TableCell>
                            <TableCell><DollarFormatter value={safeDiv(netAmount, leads)} /></TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(spend, netAmount)} /></TableCell>
                            <TableCell><DollarFormatter value={budgetInfo?.budget_amount} /></TableCell>
                            <TableCell><DollarFormatter value={budgetInfo?.projected_spend} /></TableCell>
                            <TableCell>
                              <PercentageFormatter value={budgetInfo?.pacing_percentage} isRatio={false} />
                            </TableCell>
                            <TableCell><DollarFormatter value={budgetInfo?.budget_remaining} /></TableCell>
                          </TableRow>
                          {detail.expanded && (
                            <TableRow>
                              <TableCell colSpan={32} sx={{ p: 0, bgcolor: 'background.default' }}>
                                <Box
                                  sx={{
                                    px: 2,
                                    py: 1.5,
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'background.paper',
                                  }}
                                >
                                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                                    <Typography variant="subtitle2" fontWeight={700}>
                                      {name} records
                                    </Typography>
                                    <ToggleButtonGroup
                                      size="small"
                                      color="primary"
                                      value={detail.activeTab || 'backend'}
                                      exclusive
                                      onChange={(_, val) => val && handleVerticalTabChange(name, val)}
                                    >
                                      <ToggleButton value="backend">Backend</ToggleButton>
                                      <ToggleButton value="leads">Leads</ToggleButton>
                                    </ToggleButtonGroup>
                                  </Stack>
                                  {(startLabel || endLabel) && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                      Range: {startLabel || '?'} – {endLabel || '?'}
                                    </Typography>
                                  )}
                                  {(() => {
                                    const counts = computeBackendCounts(detail);
                                    return (
                                      <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                          Booked: <Typography component="span" variant="caption" fontWeight={700}>{counts.bookedCount}</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Scheduled: <Typography component="span" variant="caption" fontWeight={700}>{counts.apptCount}</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Issue: <Typography component="span" variant="caption" fontWeight={700}>{counts.issueCount}</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Demo: <Typography component="span" variant="caption" fontWeight={700}>{counts.demoCount}</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Gross: <Typography component="span" variant="caption" fontWeight={700}>{counts.soldCount}</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Net: <Typography component="span" variant="caption" fontWeight={700}>{counts.netCount}</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Gross $: <Typography component="span" variant="caption" fontWeight={700}>{counts.grossSum.toLocaleString()}</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Net $: <Typography component="span" variant="caption" fontWeight={700}>{counts.netSum.toLocaleString()}</Typography>
                                        </Typography>
                                      </Stack>
                                    );
                                  })()}
                                  {detail.loading ? (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <CircularProgress size={18} />
                                      <Typography variant="body2" color="text.secondary">
                                        Loading records…
                                      </Typography>
                                    </Stack>
                                  ) : detail.error ? (
                                    <Typography variant="body2" color="error">
                                      {detail.error}
                                    </Typography>
                                  ) : detail.activeTab === 'leads' ? (
                                    <Box sx={{ width: '100%' }}>
                                      <Typography variant="subtitle2" gutterBottom>
                                        Lead records ({detail.leads?.length || 0})
                                      </Typography>
                                      <TableContainer component={Paper} sx={{ maxHeight: 320 }}>
                                        <Table size="small" stickyHeader>
                                          <TableHead>
                                            <TableRow>
                                              <TableCell sx={{ width: 140 }}>Date Created (EST)</TableCell>
                                              <TableCell sx={{ width: 160 }}>Name</TableCell>
                                              <TableCell sx={{ width: 140 }}>Phone</TableCell>
                                              <TableCell sx={{ width: 200 }}>Email</TableCell>
                                              <TableCell sx={{ width: 140 }}>Source</TableCell>
                                              <TableCell sx={{ width: 140 }}>Territory</TableCell>
                                              <TableCell sx={{ width: 140 }}>Vertical</TableCell>
                                              <TableCell sx={{ width: 120 }}>ZIP</TableCell>
                                              <TableCell sx={{ width: 160 }}>Self-Book Appt</TableCell>
                                              <TableCell sx={{ width: 100 }}>Success</TableCell>
                                              <TableCell sx={{ width: 180 }}>Post Response</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {(detail.leads || []).slice(
                                              (detail.leadsPage || 0) * BACKEND_PAGE_SIZE,
                                              (detail.leadsPage || 0) * BACKEND_PAGE_SIZE + BACKEND_PAGE_SIZE
                                            ).map((lead) => {
                                              const leadName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'View';
                                              return (
                                                <TableRow key={lead.id || `${leadName}-${lead.date_created}`}>
                                                  <TableCell>{formatToEST(lead.date_created)}</TableCell>
                                                  <TableCell>
                                                    {lead.id ? (
                                                      <Link
                                                        to={`/leads/${lead.id}`}
                                                        style={{ textDecoration: 'none', color: '#2f80ed' }}
                                                      >
                                                        {leadName}
                                                      </Link>
                                                    ) : (
                                                      leadName
                                                    )}
                                                  </TableCell>
                                                  <TableCell>{lead.phone || '-'}</TableCell>
                                                  <TableCell>{lead.email || '-'}</TableCell>
                                                  <TableCell>{lead.source || '-'}</TableCell>
                                                  <TableCell>{lead.territory || '-'}</TableCell>
                                                  <TableCell>{lead.vertical || '-'}</TableCell>
                                                  <TableCell>{lead.zip_code || '-'}</TableCell>
                                                  <TableCell>{lead.self_book_appointment_date_time || '-'}</TableCell>
                                                  <TableCell>{lead.success_post ? '✅' : '❌'}</TableCell>
                                                  <TableCell>{lead.post_response || '-'}</TableCell>
                                                </TableRow>
                                              );
                                            })}
                                            {!detail.leads?.length && (
                                              <TableRow>
                                                <TableCell colSpan={11} align="center">
                                                  No lead records for this vertical.
                                                </TableCell>
                                              </TableRow>
                                            )}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                        <TablePagination
                                          component="div"
                                          rowsPerPageOptions={[BACKEND_PAGE_SIZE]}
                                          rowsPerPage={BACKEND_PAGE_SIZE}
                                          count={detail.leadsCount || 0}
                                          page={detail.leadsPage || 0}
                                          onPageChange={(_, newPage) => handleVerticalPageChange(name, 'leads', newPage)}
                                        />
                                      </Box>
                                    </Box>
                                  ) : (
                                    <Box sx={{ width: '100%' }}>
                                      <Typography variant="subtitle2" gutterBottom>
                                        Backend data ({detail.backend?.length || 0})
                                      </Typography>
                                      <TableContainer component={Paper} sx={{ maxHeight: 320 }}>
                                        <Table size="small" stickyHeader>
                                          <TableHead>
                                            <TableRow>
                                              <TableCell sx={{ width: 130 }}>Sold Date</TableCell>
                                              <TableCell sx={{ width: 140 }}>Appointment Date</TableCell>
                                              <TableCell sx={{ width: 140 }}>Phone</TableCell>
                                              <TableCell sx={{ width: 240 }}>Email</TableCell>
                                              <TableCell sx={{ width: 80 }}>Issue</TableCell>
                                              <TableCell sx={{ width: 80 }}>Demo</TableCell>
                                              <TableCell sx={{ width: 120 }}>Gross</TableCell>
                                              <TableCell sx={{ width: 120 }}>Net</TableCell>
                                              <TableCell sx={{ width: 180 }}>Status</TableCell>
                                              <TableCell sx={{ width: 170 }}>Vertical</TableCell>
                                              <TableCell sx={{ width: 140 }}>ZIP</TableCell>
                                              <TableCell sx={{ width: 170 }}>Territory</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {(() => {
                                              const key = 'sold_date';
                                              const desc = true;
                                              const rangeStart = detail.dateRange?.startDate || null;
                                              const rangeEnd = detail.dateRange?.endDate || null;
                                              const getMatchFlags = (row) => {
                                                if (!rangeStart || !rangeEnd) {
                                                  return { soldOk: true, apptOk: true };
                                                }
                                                const soldOk = isDateInRange(row?.sold_date, rangeStart, rangeEnd);
                                                const apptOk = isDateInRange(row?.appointment_date, rangeStart, rangeEnd);
                                                return { soldOk, apptOk };
                                              };
                                              const getTime = (val) => {
                                                const t = val ? new Date(val).getTime() : 0;
                                                return Number.isFinite(t) ? t : 0;
                                              };
                                              const rowsWithFlags = [...(detail.backendFull || detail.backend || [])].map((row) => ({
                                                row,
                                                ...getMatchFlags(row),
                                              }));
                                              const rows = rowsWithFlags.filter((item) => item.soldOk || item.apptOk);
                                              const pageSize = BACKEND_PAGE_SIZE;
                                              const page = detail.backendPage || 0;
                                              const withSold = rows
                                                .filter((item) => item.soldOk && item.row?.sold_date)
                                                .sort((a, b) => {
                                                  const aVal = getTime(a.row?.[key]);
                                                  const bVal = getTime(b.row?.[key]);
                                                  return desc ? bVal - aVal : aVal - bVal;
                                                });
                                              const apptOnly = rows
                                                .filter((item) => item.apptOk && !item.soldOk && item.row?.appointment_date)
                                                .sort((a, b) => {
                                                  const aVal = getTime(a.row?.appointment_date);
                                                  const bVal = getTime(b.row?.appointment_date);
                                                  return desc ? bVal - aVal : aVal - bVal;
                                                });
                                              const orderedRows = [...withSold, ...apptOnly].map((item) => item.row);
                                              const startIdx = page * pageSize;
                                              const pagedRows = orderedRows.slice(startIdx, startIdx + pageSize);
                                              if (!orderedRows.length) {
                                                return (
                                                  <TableRow>
                                                    <TableCell colSpan={12} align="center">
                                                      No backend data for this vertical.
                                                    </TableCell>
                                                  </TableRow>
                                                );
                                              }
                                              return pagedRows.map((row) => (
                                                <TableRow key={row.unique_id || row.id}>
                                                  <TableCell>{row.sold_date || '-'}</TableCell>
                                                  <TableCell>{row.appointment_date || '-'}</TableCell>
                                                  <TableCell>{row.phone || '-'}</TableCell>
                                                  <TableCell>{row.email || '-'}</TableCell>
                                                  <TableCell>{row.issue ? '✅' : '❌'}</TableCell>
                                                  <TableCell>{row.demo ? '✅' : '❌'}</TableCell>
                                                  <TableCell>
                                                    {row.gross_amount ? `$${row.gross_amount}` : '-'}
                                                  </TableCell>
                                                  <TableCell>
                                                    {row.net_amount ? `$${row.net_amount}` : '-'}
                                                  </TableCell>
                                                  <TableCell>{row.status || '-'}</TableCell>
                                                  <TableCell>{row.vertical || '-'}</TableCell>
                                                  <TableCell>
                                                    {row.zip_code ||
                                                      row.client_zip_vertical_zip_code ||
                                                      row.client_zip_vertical?.zip_code ||
                                                      '-'}
                                                  </TableCell>
                                                  <TableCell>{formatTerritory(row)}</TableCell>
                                                </TableRow>
                                              ));
                                            })()}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                        <TablePagination
                                          component="div"
                                          rowsPerPageOptions={[BACKEND_PAGE_SIZE]}
                                          rowsPerPage={BACKEND_PAGE_SIZE}
                                          count={detail.backendCount ?? (detail.backendFull || detail.backend || []).length}
                                          page={detail.backendPage || 0}
                                          onPageChange={(_, newPage) => handleVerticalPageChange(name, 'backend', newPage)}
                                        />
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                    {sortedVerticals.length > 0 && (
                      <TableRow>
                        <TableCell sx={{ ...stickyCol, fontWeight: 700 }}>Total</TableCell>
                        <TableCell><DollarFormatter value={verticalTotals.spend} /></TableCell>
                        <TableCell>{verticalTotals.leads}</TableCell>
                        <TableCell>
                          <DollarFormatter value={safeDiv(verticalTotals.spend, verticalTotals.leads)} />
                        </TableCell>
                        <TableCell>{verticalTotals.selfBook}</TableCell>
                        <TableCell>
                          <DollarFormatter value={safeDiv(verticalTotals.spend, verticalTotals.selfBook)} />
                        </TableCell>
                        <TableCell>{verticalTotals.accepted}</TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(verticalTotals.accepted, verticalTotals.leads)} />
                        </TableCell>
                        <TableCell>{verticalTotals.success}</TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(verticalTotals.success, verticalTotals.leads)} />
                        </TableCell>
                        <TableCell>{verticalTotals.outOfArea}</TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(verticalTotals.outOfArea, verticalTotals.leads)} />
                        </TableCell>
                        <TableCell>{verticalTotals.booked}</TableCell>
                        <TableCell>
                          <DollarFormatter value={safeDiv(verticalTotals.spend, verticalTotals.booked)} />
                        </TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(verticalTotals.booked, verticalTotals.leads)} />
                        </TableCell>
                        <TableCell>{verticalTotals.issues}</TableCell>
                        <TableCell>
                          <DollarFormatter value={safeDiv(verticalTotals.spend, verticalTotals.issues)} />
                        </TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(verticalTotals.issues, verticalTotals.scheduled)} />
                        </TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(verticalTotals.issues, verticalTotals.leads)} />
                        </TableCell>
                        <TableCell>{verticalTotals.demos}</TableCell>
                        <TableCell>
                          <DollarFormatter value={safeDiv(verticalTotals.spend, verticalTotals.demos)} />
                        </TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(verticalTotals.demos, verticalTotals.issues)} />
                        </TableCell>
                        <TableCell>{verticalTotals.grossSold}</TableCell>
                        <TableCell>{verticalTotals.netSold}</TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(verticalTotals.netSold, verticalTotals.demos)} />
                        </TableCell>
                        <TableCell><DollarFormatter value={verticalTotals.netAmount} /></TableCell>
                        <TableCell>
                          <DollarFormatter value={safeDiv(verticalTotals.netAmount, verticalTotals.netSold)} />
                        </TableCell>
                        <TableCell>
                          <DollarFormatter value={safeDiv(verticalTotals.netAmount, verticalTotals.leads)} />
                        </TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(verticalTotals.spend, verticalTotals.netAmount)} />
                        </TableCell>
                        <TableCell>
                          <DollarFormatter value={budgetTotals?.budget_amount ?? 0} />
                        </TableCell>
                        <TableCell>
                          <DollarFormatter value={budgetTotals?.projected_spend ?? 0} />
                        </TableCell>
                        <TableCell>
                          <PercentageFormatter value={budgetTotals?.pacing_percentage ?? 0} isRatio={false} />
                        </TableCell>
                        <TableCell>
                          <DollarFormatter value={budgetTotals?.budget_remaining ?? 0} />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Section>
        </Grid>

        <Grid item xs={12} md={12}>
          <Section
            title="Channel Performance"
            subtitle="Media mix efficiency by channel."
            defaultExpanded={false}
            onToggle={handleBreakdownsToggle}
          >
            <Box sx={{ p: 2 }}>
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={compactTableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={stickyHeadCol}>Channel</TableCell>
                      <TableCell>{withFees('Spend')}</TableCell>
                      <TableCell>Leads</TableCell>
                      <TableCell>CPL</TableCell>
                      <TableCell>Self Book</TableCell>
                      <TableCell>CP Self Book</TableCell>
                      <TableCell>Accepted</TableCell>
                      <TableCell>Accepted Rate</TableCell>
                      <TableCell>Success</TableCell>
                      <TableCell>Success Rate</TableCell>
                      <TableCell>Out of Area</TableCell>
                      <TableCell>Out of Area Rate</TableCell>
                      <TableCell>Booked</TableCell>
                      <TableCell>CP Booked</TableCell>
                      <TableCell>Set Rate</TableCell>
                      <TableCell>Issues</TableCell>
                      <TableCell>CP Issue</TableCell>
                      <TableCell>Issue Rate</TableCell>
                      <TableCell>Lead To Issue</TableCell>
                      <TableCell>Demos</TableCell>
                      <TableCell>CP Demo</TableCell>
                      <TableCell>Demo Rate</TableCell>
                      <TableCell>Gross Sold</TableCell>
                      <TableCell>Net Sold</TableCell>
                      <TableCell>Net Close Rate</TableCell>
                      <TableCell>Net Amount</TableCell>
                      <TableCell>Avg Deal</TableCell>
                      <TableCell>Revenue per Lead</TableCell>
                      <TableCell>Cost of Marketing</TableCell>
                      <TableCell>Budget</TableCell>
                      <TableCell>Projected Spend</TableCell>
                      <TableCell>Pacing %</TableCell>
                      <TableCell>Remaining Budget</TableCell>
                      <TableCell>Remaining / Day</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedChannels.length === 0 && renderBreakdownsLoadingRow(34)}
                    {sortedChannels.map((ch) => {
                      const channelName = ch.channel__name || ch.channel || 'Unspecified';
                      const channelId = getChannelId(ch);
                      const channelBudgetKey = normalizeValue(channelName);
                      const channelBudgetInfo = budgetChannels[channelBudgetKey] || {};
                      const baseSpend = ch.total_spend_sum || 0;
                      const adjSpend = baseSpend * (1 + feeRate);
                      const spend = showFee ? adjSpend : baseSpend;
                      const leads = ch.total_leads_sum || 0;
                      const selfBook = ch.self_books_sum || 0;
                      const accepted = ch.accepted_sum || 0;
                      const success = ch.success_sum || 0;
                      const outOfArea = ch.out_of_areas_count_sum || 0;
                      const booked = ch.booked_sum || 0;
                      const issues = ch.issues_sum || 0;
                      const scheduled = ch.scheduled_sum || 0;
                      const demos = ch.demos_sum || 0;
                      const grossSold = ch.gross_sales_sum || ch.gross_sold_sum || 0;
                      const netSold = ch.net_sales_sum || ch.net_sold_sum || 0;
                      const netAmount = ch.net_amount_sum || 0;
                      const detail = channelDetails[channelName] || {};
                      const startLabel = currentRange.startDate ? formatDateToYMD(currentRange.startDate) : null;
                      const endLabel = currentRange.endDate ? formatDateToYMD(currentRange.endDate) : null;
                      return (
                        <Fragment key={ch.uuid || channelName}>
                          <TableRow hover onClick={() => handleToggleChannelRow(channelName, channelId)} sx={{ cursor: 'pointer' }}>
                            <TableCell sx={stickyCol}>{channelName}</TableCell>
                            <TableCell><DollarFormatter value={spend} /></TableCell>
                            <TableCell>{leads}</TableCell>
                            <TableCell><DollarFormatter value={safeDiv(spend, leads)} /></TableCell>
                            <TableCell>{selfBook}</TableCell>
                            <TableCell><DollarFormatter value={safeDiv(spend, selfBook)} /></TableCell>
                            <TableCell>{accepted}</TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(accepted, leads)} /></TableCell>
                            <TableCell>{success}</TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(success, leads)} /></TableCell>
                            <TableCell>{outOfArea}</TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(outOfArea, leads)} /></TableCell>
                            <TableCell>{booked}</TableCell>
                            <TableCell><DollarFormatter value={safeDiv(spend, booked)} /></TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(booked, leads)} /></TableCell>
                            <TableCell>{issues}</TableCell>
                            <TableCell><DollarFormatter value={safeDiv(spend, issues)} /></TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(issues, scheduled)} /></TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(issues, leads)} /></TableCell>
                            <TableCell>{demos}</TableCell>
                            <TableCell><DollarFormatter value={safeDiv(spend, demos)} /></TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(demos, issues)} /></TableCell>
                            <TableCell>{grossSold}</TableCell>
                            <TableCell>{netSold}</TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(netSold, demos)} /></TableCell>
                            <TableCell><DollarFormatter value={netAmount} /></TableCell>
                            <TableCell><DollarFormatter value={safeDiv(netAmount, netSold)} /></TableCell>
                            <TableCell><DollarFormatter value={safeDiv(netAmount, leads)} /></TableCell>
                            <TableCell><PercentageFormatter value={safeDiv(spend, netAmount)} /></TableCell>
                            <TableCell><DollarFormatter value={channelBudgetInfo?.budget_amount} /></TableCell>
                            <TableCell><DollarFormatter value={channelBudgetInfo?.projected_spend} /></TableCell>
                            <TableCell>
                              <PercentageFormatter value={channelBudgetInfo?.pacing_percentage} isRatio={false} />
                            </TableCell>
                            <TableCell><DollarFormatter value={channelBudgetInfo?.budget_remaining} /></TableCell>
                            <TableCell>
                              <DollarFormatter
                                value={
                                  channelBudgetInfo?.calendar_budget_remaining_per_day ??
                                  channelBudgetInfo?.remaining_per_day
                                }
                              />
                            </TableCell>
                          </TableRow>
                          {detail.expanded && (
                            <TableRow>
                              <TableCell colSpan={34} sx={{ p: 0, bgcolor: 'background.default' }}>
                                <Box
                                  sx={{
                                    px: 2,
                                    py: 1.5,
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'background.paper',
                                  }}
                                >
                                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                                    <Typography variant="subtitle2" fontWeight={700}>
                                      {channelName} records
                                    </Typography>
                                    <ToggleButtonGroup
                                      size="small"
                                      color="primary"
                                      value={detail.activeTab || 'backend'}
                                      exclusive
                                      onChange={(_, val) => val && handleChannelTabChange(channelName, val)}
                                    >
                                      <ToggleButton value="backend">Backend</ToggleButton>
                                      <ToggleButton value="leads">Leads</ToggleButton>
                                    </ToggleButtonGroup>
                                  </Stack>
                                  {(startLabel || endLabel) && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                      Range: {startLabel || '?'} – {endLabel || '?'}
                                    </Typography>
                                  )}
                                  {(() => {
                                    const counts = computeBackendCounts(detail);
                                    return (
                                      <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                          Booked: <Typography component="span" variant="caption" fontWeight={700}>{counts.bookedCount}</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Scheduled: <Typography component="span" variant="caption" fontWeight={700}>{counts.apptCount}</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Issue: <Typography component="span" variant="caption" fontWeight={700}>{counts.issueCount}</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Demo: <Typography component="span" variant="caption" fontWeight={700}>{counts.demoCount}</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Gross: <Typography component="span" variant="caption" fontWeight={700}>{counts.soldCount}</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Net: <Typography component="span" variant="caption" fontWeight={700}>{counts.netCount}</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Gross $: <Typography component="span" variant="caption" fontWeight={700}>{counts.grossSum.toLocaleString()}</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Net $: <Typography component="span" variant="caption" fontWeight={700}>{counts.netSum.toLocaleString()}</Typography>
                                        </Typography>
                                      </Stack>
                                    );
                                  })()}
                                  {detail.loading ? (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <CircularProgress size={18} />
                                      <Typography variant="body2" color="text.secondary">
                                        Loading records…
                                      </Typography>
                                    </Stack>
                                  ) : detail.error ? (
                                    <Typography variant="body2" color="error">
                                      {detail.error}
                                    </Typography>
                                  ) : detail.activeTab === 'leads' ? (
                                    <Box sx={{ width: '100%' }}>
                                      <Typography variant="subtitle2" gutterBottom>
                                        Lead records ({detail.leads?.length || 0})
                                      </Typography>
                                      <TableContainer component={Paper} sx={{ maxHeight: 320 }}>
                                        <Table size="small" stickyHeader>
                                          <TableHead>
                                            <TableRow>
                                              <TableCell sx={{ width: 140 }}>Date Created (EST)</TableCell>
                                              <TableCell sx={{ width: 160 }}>Name</TableCell>
                                              <TableCell sx={{ width: 140 }}>Phone</TableCell>
                                              <TableCell sx={{ width: 200 }}>Email</TableCell>
                                              <TableCell sx={{ width: 140 }}>Source</TableCell>
                                              <TableCell sx={{ width: 140 }}>Territory</TableCell>
                                              <TableCell sx={{ width: 140 }}>Vertical</TableCell>
                                              <TableCell sx={{ width: 120 }}>ZIP</TableCell>
                                              <TableCell sx={{ width: 160 }}>Self-Book Appt</TableCell>
                                              <TableCell sx={{ width: 100 }}>Success</TableCell>
                                              <TableCell sx={{ width: 180 }}>Post Response</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {(detail.leads || []).slice(
                                              (detail.leadsPage || 0) * BACKEND_PAGE_SIZE,
                                              (detail.leadsPage || 0) * BACKEND_PAGE_SIZE + BACKEND_PAGE_SIZE
                                            ).map((lead) => {
                                              const leadName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'View';
                                              return (
                                                <TableRow key={lead.id || `${leadName}-${lead.date_created}`}>
                                                  <TableCell>{formatToEST(lead.date_created)}</TableCell>
                                                  <TableCell>
                                                    {lead.id ? (
                                                      <Link
                                                        to={`/leads/${lead.id}`}
                                                        style={{ textDecoration: 'none', color: '#2f80ed' }}
                                                      >
                                                        {leadName}
                                                      </Link>
                                                    ) : (
                                                      leadName
                                                    )}
                                                  </TableCell>
                                                  <TableCell>{lead.phone || '-'}</TableCell>
                                                  <TableCell>{lead.email || '-'}</TableCell>
                                                  <TableCell>{lead.source || '-'}</TableCell>
                                                  <TableCell>{lead.territory || '-'}</TableCell>
                                                  <TableCell>{lead.vertical || '-'}</TableCell>
                                                  <TableCell>{lead.zip_code || '-'}</TableCell>
                                                  <TableCell>{lead.self_book_appointment_date_time || '-'}</TableCell>
                                                  <TableCell>{lead.success_post ? '✅' : '❌'}</TableCell>
                                                  <TableCell>{lead.post_response || '-'}</TableCell>
                                                </TableRow>
                                              );
                                            })}
                                            {!detail.leads?.length && (
                                              <TableRow>
                                                <TableCell colSpan={11} align="center">
                                                  No lead records for this channel.
                                                </TableCell>
                                              </TableRow>
                                            )}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                        <TablePagination
                                          component="div"
                                          rowsPerPageOptions={[BACKEND_PAGE_SIZE]}
                                          rowsPerPage={BACKEND_PAGE_SIZE}
                                          count={detail.leadsCount || 0}
                                          page={detail.leadsPage || 0}
                                          onPageChange={(_, newPage) => handleChannelPageChange(channelName, 'leads', newPage)}
                                        />
                                      </Box>
                                    </Box>
                                  ) : (
                                    <Box sx={{ width: '100%' }}>
                                      <Typography variant="subtitle2" gutterBottom>
                                        Backend data ({detail.backend?.length || 0})
                                      </Typography>
                                      <TableContainer component={Paper} sx={{ maxHeight: 320 }}>
                                        <Table size="small" stickyHeader>
                                          <TableHead>
                                            <TableRow>
                                              <TableCell sx={{ width: 130 }}>Sold Date</TableCell>
                                              <TableCell sx={{ width: 140 }}>Appointment Date</TableCell>
                                              <TableCell sx={{ width: 140 }}>Phone</TableCell>
                                              <TableCell sx={{ width: 240 }}>Email</TableCell>
                                              <TableCell sx={{ width: 80 }}>Issue</TableCell>
                                              <TableCell sx={{ width: 80 }}>Demo</TableCell>
                                              <TableCell sx={{ width: 120 }}>Gross</TableCell>
                                              <TableCell sx={{ width: 120 }}>Net</TableCell>
                                              <TableCell sx={{ width: 180 }}>Status</TableCell>
                                              <TableCell sx={{ width: 170 }}>Vertical</TableCell>
                                              <TableCell sx={{ width: 140 }}>ZIP</TableCell>
                                              <TableCell sx={{ width: 170 }}>Territory</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {(() => {
                                              const key = 'sold_date';
                                              const desc = true;
                                              const rangeStart = detail.dateRange?.startDate || null;
                                              const rangeEnd = detail.dateRange?.endDate || null;
                                              const getMatchFlags = (row) => {
                                                if (!rangeStart || !rangeEnd) {
                                                  return { soldOk: true, apptOk: true };
                                                }
                                                const soldOk = isDateInRange(row?.sold_date, rangeStart, rangeEnd);
                                                const apptOk = isDateInRange(row?.appointment_date, rangeStart, rangeEnd);
                                                return { soldOk, apptOk };
                                              };
                                              const getTime = (val) => {
                                                const t = val ? new Date(val).getTime() : 0;
                                                return Number.isFinite(t) ? t : 0;
                                              };
                                              const rowsWithFlags = [...(detail.backendFull || detail.backend || [])].map((row) => ({
                                                row,
                                                ...getMatchFlags(row),
                                              }));
                                              const rows = rowsWithFlags.filter((item) => item.soldOk || item.apptOk);
                                              const pageSize = BACKEND_PAGE_SIZE;
                                              const page = detail.backendPage || 0;
                                              const withSold = rows
                                                .filter((item) => item.soldOk && item.row?.sold_date)
                                                .sort((a, b) => {
                                                  const aVal = getTime(a.row?.[key]);
                                                  const bVal = getTime(b.row?.[key]);
                                                  return desc ? bVal - aVal : aVal - bVal;
                                                });
                                              const apptOnly = rows
                                                .filter((item) => item.apptOk && !item.soldOk && item.row?.appointment_date)
                                                .sort((a, b) => {
                                                  const aVal = getTime(a.row?.appointment_date);
                                                  const bVal = getTime(b.row?.appointment_date);
                                                  return desc ? bVal - aVal : aVal - bVal;
                                                });
                                              const orderedRows = [...withSold, ...apptOnly].map((item) => item.row);
                                              const startIdx = page * pageSize;
                                              const pagedRows = orderedRows.slice(startIdx, startIdx + pageSize);
                                              if (!orderedRows.length) {
                                                return (
                                                  <TableRow>
                                                    <TableCell colSpan={12} align="center">
                                                      No backend data for this channel.
                                                    </TableCell>
                                                  </TableRow>
                                                );
                                              }
                                              return pagedRows.map((row) => (
                                                <TableRow key={row.unique_id || row.id}>
                                                  <TableCell>{row.sold_date || '-'}</TableCell>
                                                  <TableCell>{row.appointment_date || '-'}</TableCell>
                                                  <TableCell>{row.phone || '-'}</TableCell>
                                                  <TableCell>{row.email || '-'}</TableCell>
                                                  <TableCell>{row.issue ? '✅' : '❌'}</TableCell>
                                                  <TableCell>{row.demo ? '✅' : '❌'}</TableCell>
                                                  <TableCell>
                                                    {row.gross_amount ? `$${row.gross_amount}` : '-'}
                                                  </TableCell>
                                                  <TableCell>
                                                    {row.net_amount ? `$${row.net_amount}` : '-'}
                                                  </TableCell>
                                                  <TableCell>{row.status || '-'}</TableCell>
                                                  <TableCell>{row.vertical || '-'}</TableCell>
                                                  <TableCell>
                                                    {row.zip_code ||
                                                      row.client_zip_vertical_zip_code ||
                                                      row.client_zip_vertical?.zip_code ||
                                                      '-'}
                                                  </TableCell>
                                                  <TableCell>{formatTerritory(row)}</TableCell>
                                                </TableRow>
                                              ));
                                            })()}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                        <TablePagination
                                          component="div"
                                          rowsPerPageOptions={[BACKEND_PAGE_SIZE]}
                                          rowsPerPage={BACKEND_PAGE_SIZE}
                                          count={detail.backendCount ?? (detail.backendFull || detail.backend || []).length}
                                          page={detail.backendPage || 0}
                                          onPageChange={(_, newPage) => handleChannelPageChange(channelName, 'backend', newPage)}
                                        />
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                    {channels.length > 0 && (
                      <TableRow>
                        <TableCell sx={{ ...stickyCol, fontWeight: 700 }}>Total</TableCell>
                        <TableCell><DollarFormatter value={channelTotals.spend} /></TableCell>
                        <TableCell>{channelTotals.leads}</TableCell>
                        <TableCell>
                          <DollarFormatter value={safeDiv(channelTotals.spend, channelTotals.leads)} />
                        </TableCell>
                        <TableCell>{channelTotals.selfBook}</TableCell>
                        <TableCell>
                          <DollarFormatter value={safeDiv(channelTotals.spend, channelTotals.selfBook)} />
                        </TableCell>
                        <TableCell>{channelTotals.accepted}</TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(channelTotals.accepted, channelTotals.leads)} />
                        </TableCell>
                        <TableCell>{channelTotals.success}</TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(channelTotals.success, channelTotals.leads)} />
                        </TableCell>
                        <TableCell>{channelTotals.outOfArea}</TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(channelTotals.outOfArea, channelTotals.leads)} />
                        </TableCell>
                        <TableCell>{channelTotals.booked}</TableCell>
                        <TableCell>
                          <DollarFormatter value={safeDiv(channelTotals.spend, channelTotals.booked)} />
                        </TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(channelTotals.booked, channelTotals.leads)} />
                        </TableCell>
                        <TableCell>{channelTotals.issues}</TableCell>
                        <TableCell>
                          <DollarFormatter value={safeDiv(channelTotals.spend, channelTotals.issues)} />
                        </TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(channelTotals.issues, channelTotals.scheduled)} />
                        </TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(channelTotals.issues, channelTotals.leads)} />
                        </TableCell>
                        <TableCell>{channelTotals.demos}</TableCell>
                        <TableCell>
                          <DollarFormatter value={safeDiv(channelTotals.spend, channelTotals.demos)} />
                        </TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(channelTotals.demos, channelTotals.issues)} />
                        </TableCell>
                        <TableCell>{channelTotals.grossSold}</TableCell>
                        <TableCell>{channelTotals.netSold}</TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(channelTotals.netSold, channelTotals.demos)} />
                        </TableCell>
                        <TableCell><DollarFormatter value={channelTotals.netAmount} /></TableCell>
                        <TableCell>
                          <DollarFormatter value={safeDiv(channelTotals.netAmount, channelTotals.netSold)} />
                        </TableCell>
                        <TableCell>
                          <DollarFormatter value={safeDiv(channelTotals.netAmount, channelTotals.leads)} />
                        </TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(channelTotals.spend, channelTotals.netAmount)} />
                        </TableCell>
                        <TableCell>
                          <DollarFormatter value={channelBudgetTotals.budget_amount} />
                        </TableCell>
                        <TableCell>
                          <DollarFormatter value={channelBudgetTotals.projected_spend} />
                        </TableCell>
                        <TableCell>
                          <PercentageFormatter
                            value={safeDiv(channelBudgetTotals.projected_spend, channelBudgetTotals.budget_amount) * 100}
                            isRatio={false}
                          />
                        </TableCell>
                        <TableCell>
                          <DollarFormatter value={channelBudgetTotals.budget_remaining} />
                        </TableCell>
                        <TableCell>
                          <DollarFormatter value={channelBudgetTotals.remaining_per_day} />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Section>
        </Grid>

        <Grid item xs={12} md={12}>
          <Section
            title="Campaign Performance"
            subtitle="Stack-ranked campaigns by spend."
            defaultExpanded={false}
            onToggle={handleBreakdownsToggle}
          >
            <Box sx={{ p: 2 }}>
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={compactTableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={stickyHeadCol}>Campaign</TableCell>
                      <TableCell>Channel</TableCell>
                      <TableCell>{withFees('Spend')}</TableCell>
                      <TableCell>Leads</TableCell>
                      <TableCell>CPL</TableCell>
                      <TableCell>Self Book</TableCell>
                      <TableCell>CP Self Book</TableCell>
                      <TableCell>Accepted</TableCell>
                      <TableCell>Accepted Rate</TableCell>
                      <TableCell>Success</TableCell>
                      <TableCell>Success Rate</TableCell>
                      <TableCell>Out of Area</TableCell>
                      <TableCell>Out of Area Rate</TableCell>
                      <TableCell>Booked</TableCell>
                      <TableCell>CP Booked</TableCell>
                      <TableCell>Set Rate</TableCell>
                      <TableCell>Issues</TableCell>
                      <TableCell>CP Issue</TableCell>
                      <TableCell>Issue Rate</TableCell>
                      <TableCell>Lead To Issue</TableCell>
                      <TableCell>Demos</TableCell>
                      <TableCell>CP Demo</TableCell>
                      <TableCell>Demo Rate</TableCell>
                      <TableCell>Gross Sold</TableCell>
                      <TableCell>Net Sold</TableCell>
                      <TableCell>Net Close Rate</TableCell>
                      <TableCell>Net Amount</TableCell>
                      <TableCell>Avg Deal</TableCell>
                      <TableCell>Revenue per Lead</TableCell>
                      <TableCell>Cost of Marketing</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedCampaigns.length === 0 && renderBreakdownsLoadingRow(30)}
                    {sortedCampaigns.map((c) => {
                      const campaignName = c.campaign || 'Unspecified';
                      const campaignId = c.campaign_id ?? c.id ?? null;
                      const campaignKey = campaignId ? `${campaignName}__${campaignId}` : campaignName;
                      const detail = campaignDetails[campaignKey] || {};
                      const baseSpend = c.total_spend_sum || 0;
                      const adjSpend = baseSpend * (1 + feeRate);
                      const spend = showFee ? adjSpend : baseSpend;
                      const leads = c.total_leads_sum || 0;
                      const selfBook = c.self_books_sum || 0;
                      const accepted = c.accepted_sum || 0;
                      const success = c.success_sum || 0;
                      const outOfArea = c.out_of_areas_count_sum || 0;
                      const booked = c.booked_sum || 0;
                      const issues = c.issues_sum || 0;
                      const scheduled = c.scheduled_sum || 0;
                      const demos = c.demos_sum || 0;
                      const grossSold = c.gross_sales_sum || c.gross_sold_sum || 0;
                      const netSold = c.net_sales_sum || c.net_sold_sum || 0;
                      const netAmount = c.net_amount_sum || 0;
                      const startLabel = currentRange.startDate ? formatDateToYMD(currentRange.startDate) : null;
                      const endLabel = currentRange.endDate ? formatDateToYMD(currentRange.endDate) : null;
                      return (
                        <Fragment key={c.uuid || campaignKey}>
                          <TableRow
                            hover
                            onClick={() => handleToggleCampaignRow(campaignKey, campaignName, campaignId)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell sx={stickyCol}>{campaignName}</TableCell>
                          <TableCell>{c.channel__name || c.channel}</TableCell>
                          <TableCell><DollarFormatter value={spend} /></TableCell>
                          <TableCell>{leads}</TableCell>
                          <TableCell><DollarFormatter value={safeDiv(spend, leads)} /></TableCell>
                          <TableCell>{selfBook}</TableCell>
                          <TableCell><DollarFormatter value={safeDiv(spend, selfBook)} /></TableCell>
                          <TableCell>{accepted}</TableCell>
                          <TableCell><PercentageFormatter value={safeDiv(accepted, leads)} /></TableCell>
                          <TableCell>{success}</TableCell>
                          <TableCell><PercentageFormatter value={safeDiv(success, leads)} /></TableCell>
                          <TableCell>{outOfArea}</TableCell>
                          <TableCell><PercentageFormatter value={safeDiv(outOfArea, leads)} /></TableCell>
                          <TableCell>{booked}</TableCell>
                          <TableCell><DollarFormatter value={safeDiv(spend, booked)} /></TableCell>
                          <TableCell><PercentageFormatter value={safeDiv(booked, leads)} /></TableCell>
                          <TableCell>{issues}</TableCell>
                          <TableCell><DollarFormatter value={safeDiv(spend, issues)} /></TableCell>
                          <TableCell><PercentageFormatter value={safeDiv(issues, scheduled)} /></TableCell>
                          <TableCell><PercentageFormatter value={safeDiv(issues, leads)} /></TableCell>
                          <TableCell>{demos}</TableCell>
                          <TableCell><DollarFormatter value={safeDiv(spend, demos)} /></TableCell>
                          <TableCell><PercentageFormatter value={safeDiv(demos, issues)} /></TableCell>
                          <TableCell>{grossSold}</TableCell>
                          <TableCell>{netSold}</TableCell>
                          <TableCell><PercentageFormatter value={safeDiv(netSold, demos)} /></TableCell>
                          <TableCell><DollarFormatter value={netAmount} /></TableCell>
                          <TableCell><DollarFormatter value={safeDiv(netAmount, netSold)} /></TableCell>
                          <TableCell><DollarFormatter value={safeDiv(netAmount, leads)} /></TableCell>
                          <TableCell><PercentageFormatter value={safeDiv(spend, netAmount)} /></TableCell>
                          </TableRow>
                          {detail.expanded && (
                            <TableRow>
                              <TableCell colSpan={30} sx={{ p: 0, bgcolor: 'background.default' }}>
                                <Box
                                  sx={{
                                    px: 2,
                                    py: 1.5,
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'background.paper',
                                  }}
                                >
                                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                                    <Typography variant="subtitle2" fontWeight={700}>
                                      {campaignName} records
                                    </Typography>
                                    <ToggleButtonGroup
                                      size="small"
                                      color="primary"
                                      value={detail.activeTab || 'backend'}
                                      exclusive
                                      onChange={(_, val) => val && handleCampaignTabChange(campaignKey, val)}
                                    >
                                      <ToggleButton value="backend">Backend</ToggleButton>
                                      <ToggleButton value="leads">Leads</ToggleButton>
                                    </ToggleButtonGroup>
                                  </Stack>
                                  {(startLabel || endLabel) && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                      Range: {startLabel || '?'} – {endLabel || '?'}
                                    </Typography>
                                  )}
                                  {(() => {
                                    const counts = computeBackendCounts(detail);
                                    return (
                                      <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                          Booked: <Typography component="span" variant="caption" fontWeight={700}>{counts.bookedCount}</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Scheduled: <Typography component="span" variant="caption" fontWeight={700}>{counts.apptCount}</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Issue: <Typography component="span" variant="caption" fontWeight={700}>{counts.issueCount}</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Demo: <Typography component="span" variant="caption" fontWeight={700}>{counts.demoCount}</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Gross: <Typography component="span" variant="caption" fontWeight={700}>{counts.soldCount}</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Net: <Typography component="span" variant="caption" fontWeight={700}>{counts.netCount}</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Gross $: <Typography component="span" variant="caption" fontWeight={700}>{counts.grossSum.toLocaleString()}</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Net $: <Typography component="span" variant="caption" fontWeight={700}>{counts.netSum.toLocaleString()}</Typography>
                                        </Typography>
                                      </Stack>
                                    );
                                  })()}
                                  {detail.loading ? (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <CircularProgress size={18} />
                                      <Typography variant="body2" color="text.secondary">
                                        Loading records…
                                      </Typography>
                                    </Stack>
                                  ) : detail.error ? (
                                    <Typography variant="body2" color="error">
                                      {detail.error}
                                    </Typography>
                                  ) : detail.activeTab === 'leads' ? (
                                    <Box sx={{ width: '100%' }}>
                                      <Typography variant="subtitle2" gutterBottom>
                                        Lead records ({detail.leads?.length || 0})
                                      </Typography>
                                      <TableContainer component={Paper} sx={{ maxHeight: 320 }}>
                                        <Table size="small" stickyHeader>
                                          <TableHead>
                                            <TableRow>
                                              <TableCell sx={{ width: 140 }}>Date Created (EST)</TableCell>
                                              <TableCell sx={{ width: 160 }}>Name</TableCell>
                                              <TableCell sx={{ width: 140 }}>Phone</TableCell>
                                              <TableCell sx={{ width: 200 }}>Email</TableCell>
                                              <TableCell sx={{ width: 140 }}>Source</TableCell>
                                              <TableCell sx={{ width: 140 }}>Territory</TableCell>
                                              <TableCell sx={{ width: 140 }}>Vertical</TableCell>
                                              <TableCell sx={{ width: 120 }}>ZIP</TableCell>
                                              <TableCell sx={{ width: 160 }}>Self-Book Appt</TableCell>
                                              <TableCell sx={{ width: 100 }}>Success</TableCell>
                                              <TableCell sx={{ width: 180 }}>Post Response</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {(detail.leads || []).slice(
                                              (detail.leadsPage || 0) * BACKEND_PAGE_SIZE,
                                              (detail.leadsPage || 0) * BACKEND_PAGE_SIZE + BACKEND_PAGE_SIZE
                                            ).map((lead) => {
                                              const leadName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'View';
                                              return (
                                                <TableRow key={lead.id || `${leadName}-${lead.date_created}`}>
                                                  <TableCell>{formatToEST(lead.date_created)}</TableCell>
                                                  <TableCell>
                                                    {lead.id ? (
                                                      <Link
                                                        to={`/leads/${lead.id}`}
                                                        style={{ textDecoration: 'none', color: '#2f80ed' }}
                                                      >
                                                        {leadName}
                                                      </Link>
                                                    ) : (
                                                      leadName
                                                    )}
                                                  </TableCell>
                                                  <TableCell>{lead.phone || '-'}</TableCell>
                                                  <TableCell>{lead.email || '-'}</TableCell>
                                                  <TableCell>{lead.source || '-'}</TableCell>
                                                  <TableCell>{lead.territory || '-'}</TableCell>
                                                  <TableCell>{lead.vertical || '-'}</TableCell>
                                                  <TableCell>{lead.zip_code || '-'}</TableCell>
                                                  <TableCell>{lead.self_book_appointment_date_time || '-'}</TableCell>
                                                  <TableCell>{lead.success_post ? '✅' : '❌'}</TableCell>
                                                  <TableCell>{lead.post_response || '-'}</TableCell>
                                                </TableRow>
                                              );
                                            })}
                                            {!detail.leads?.length && (
                                              <TableRow>
                                                <TableCell colSpan={11} align="center">
                                                  No lead records for this campaign.
                                                </TableCell>
                                              </TableRow>
                                            )}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                        <TablePagination
                                          component="div"
                                          rowsPerPageOptions={[BACKEND_PAGE_SIZE]}
                                          rowsPerPage={BACKEND_PAGE_SIZE}
                                          count={detail.leadsCount || 0}
                                          page={detail.leadsPage || 0}
                                          onPageChange={(_, newPage) => handleCampaignPageChange(campaignKey, 'leads', newPage)}
                                        />
                                      </Box>
                                    </Box>
                                  ) : (
                                    <Box sx={{ width: '100%' }}>
                                      <Typography variant="subtitle2" gutterBottom>
                                        Backend data ({detail.backend?.length || 0})
                                      </Typography>
                                      <TableContainer component={Paper} sx={{ maxHeight: 320 }}>
                                        <Table size="small" stickyHeader>
                                          <TableHead>
                                            <TableRow>
                                              <TableCell sx={{ width: 130 }}>Sold Date</TableCell>
                                              <TableCell sx={{ width: 140 }}>Appointment Date</TableCell>
                                              <TableCell sx={{ width: 140 }}>Phone</TableCell>
                                              <TableCell sx={{ width: 240 }}>Email</TableCell>
                                              <TableCell sx={{ width: 80 }}>Issue</TableCell>
                                              <TableCell sx={{ width: 80 }}>Demo</TableCell>
                                              <TableCell sx={{ width: 120 }}>Gross</TableCell>
                                              <TableCell sx={{ width: 120 }}>Net</TableCell>
                                              <TableCell sx={{ width: 180 }}>Status</TableCell>
                                              <TableCell sx={{ width: 170 }}>Vertical</TableCell>
                                              <TableCell sx={{ width: 140 }}>ZIP</TableCell>
                                              <TableCell sx={{ width: 170 }}>Territory</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {(() => {
                                              const key = 'sold_date';
                                              const desc = true;
                                              const rangeStart = detail.dateRange?.startDate || null;
                                              const rangeEnd = detail.dateRange?.endDate || null;
                                              const getMatchFlags = (row) => {
                                                if (!rangeStart || !rangeEnd) {
                                                  return { soldOk: true, apptOk: true };
                                                }
                                                const soldOk = isDateInRange(row?.sold_date, rangeStart, rangeEnd);
                                                const apptOk = isDateInRange(row?.appointment_date, rangeStart, rangeEnd);
                                                return { soldOk, apptOk };
                                              };
                                              const getTime = (val) => {
                                                const t = val ? new Date(val).getTime() : 0;
                                                return Number.isFinite(t) ? t : 0;
                                              };
                                              const rowsWithFlags = [...(detail.backendFull || detail.backend || [])].map((row) => ({
                                                row,
                                                ...getMatchFlags(row),
                                              }));
                                              const rows = rowsWithFlags.filter((item) => item.soldOk || item.apptOk);
                                              const pageSize = BACKEND_PAGE_SIZE;
                                              const page = detail.backendPage || 0;
                                              const withSold = rows
                                                .filter((item) => item.soldOk && item.row?.sold_date)
                                                .sort((a, b) => {
                                                  const aVal = getTime(a.row?.[key]);
                                                  const bVal = getTime(b.row?.[key]);
                                                  return desc ? bVal - aVal : aVal - bVal;
                                                });
                                              const apptOnly = rows
                                                .filter((item) => item.apptOk && !item.soldOk && item.row?.appointment_date)
                                                .sort((a, b) => {
                                                  const aVal = getTime(a.row?.appointment_date);
                                                  const bVal = getTime(b.row?.appointment_date);
                                                  return desc ? bVal - aVal : aVal - bVal;
                                                });
                                              const orderedRows = [...withSold, ...apptOnly].map((item) => item.row);
                                              const startIdx = page * pageSize;
                                              const pagedRows = orderedRows.slice(startIdx, startIdx + pageSize);
                                              if (!orderedRows.length) {
                                                return (
                                                  <TableRow>
                                                    <TableCell colSpan={12} align="center">
                                                      No backend data for this campaign.
                                                    </TableCell>
                                                  </TableRow>
                                                );
                                              }
                                              return pagedRows.map((row) => (
                                                <TableRow key={row.unique_id || row.id}>
                                                  <TableCell>{row.sold_date || '-'}</TableCell>
                                                  <TableCell>{row.appointment_date || '-'}</TableCell>
                                                  <TableCell>{row.phone || '-'}</TableCell>
                                                  <TableCell>{row.email || '-'}</TableCell>
                                                  <TableCell>{row.issue ? '✅' : '❌'}</TableCell>
                                                  <TableCell>{row.demo ? '✅' : '❌'}</TableCell>
                                                  <TableCell>
                                                    {row.gross_amount ? `$${row.gross_amount}` : '-'}
                                                  </TableCell>
                                                  <TableCell>
                                                    {row.net_amount ? `$${row.net_amount}` : '-'}
                                                  </TableCell>
                                                  <TableCell>{row.status || '-'}</TableCell>
                                                  <TableCell>{row.vertical || '-'}</TableCell>
                                                  <TableCell>
                                                    {row.zip_code ||
                                                      row.client_zip_vertical_zip_code ||
                                                      row.client_zip_vertical?.zip_code ||
                                                      '-'}
                                                  </TableCell>
                                                  <TableCell>{formatTerritory(row)}</TableCell>
                                                </TableRow>
                                              ));
                                            })()}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                        <TablePagination
                                          component="div"
                                          rowsPerPageOptions={[BACKEND_PAGE_SIZE]}
                                          rowsPerPage={BACKEND_PAGE_SIZE}
                                          count={detail.backendCount ?? (detail.backendFull || detail.backend || []).length}
                                          page={detail.backendPage || 0}
                                          onPageChange={(_, newPage) => handleCampaignPageChange(campaignKey, 'backend', newPage)}
                                        />
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                    {sortedCampaigns.length > 0 && (
                      <TableRow>
                        <TableCell sx={{ ...stickyCol, fontWeight: 700 }}>Total</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>All channels</TableCell>
                        <TableCell><DollarFormatter value={campaignTotals.spend} /></TableCell>
                        <TableCell>{campaignTotals.leads}</TableCell>
                        <TableCell>
                          <DollarFormatter value={safeDiv(campaignTotals.spend, campaignTotals.leads)} />
                        </TableCell>
                        <TableCell>{campaignTotals.selfBook}</TableCell>
                        <TableCell>
                          <DollarFormatter value={safeDiv(campaignTotals.spend, campaignTotals.selfBook)} />
                        </TableCell>
                        <TableCell>{campaignTotals.accepted}</TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(campaignTotals.accepted, campaignTotals.leads)} />
                        </TableCell>
                        <TableCell>{campaignTotals.success}</TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(campaignTotals.success, campaignTotals.leads)} />
                        </TableCell>
                        <TableCell>{campaignTotals.outOfArea}</TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(campaignTotals.outOfArea, campaignTotals.leads)} />
                        </TableCell>
                        <TableCell>{campaignTotals.booked}</TableCell>
                        <TableCell>
                          <DollarFormatter value={safeDiv(campaignTotals.spend, campaignTotals.booked)} />
                        </TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(campaignTotals.booked, campaignTotals.leads)} />
                        </TableCell>
                        <TableCell>{campaignTotals.issues}</TableCell>
                        <TableCell>
                          <DollarFormatter value={safeDiv(campaignTotals.spend, campaignTotals.issues)} />
                        </TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(campaignTotals.issues, campaignTotals.scheduled)} />
                        </TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(campaignTotals.issues, campaignTotals.leads)} />
                        </TableCell>
                        <TableCell>{campaignTotals.demos}</TableCell>
                        <TableCell>
                          <DollarFormatter value={safeDiv(campaignTotals.spend, campaignTotals.demos)} />
                        </TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(campaignTotals.demos, campaignTotals.issues)} />
                        </TableCell>
                        <TableCell>{campaignTotals.grossSold}</TableCell>
                        <TableCell>{campaignTotals.netSold}</TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(campaignTotals.netSold, campaignTotals.demos)} />
                        </TableCell>
                        <TableCell><DollarFormatter value={campaignTotals.netAmount} /></TableCell>
                        <TableCell>
                          <DollarFormatter value={safeDiv(campaignTotals.netAmount, campaignTotals.netSold)} />
                        </TableCell>
                        <TableCell>
                          <PercentageFormatter value={safeDiv(campaignTotals.spend, campaignTotals.netAmount)} />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section
            title="Territory Performance"
            subtitle="Filter by vertical to zero in on the right territories."
            action={
              <FormControl
                size="small"
                sx={{ minWidth: 220 }}
                disabled={!territoryVerticalOptions.length}
              >
                <InputLabel id="territory-vertical-filter-label">
                  Verticals
                </InputLabel>
                <Select
                  labelId="territory-vertical-filter-label"
                  multiple
                  value={territoryVerticalFilter}
                  onChange={handleTerritoryVerticalChange}
                  input={<OutlinedInput label="Verticals" />}
                  renderValue={(selected) => {
                    if (!selected.length) {
                      return 'All verticals';
                    }
                    const lookup = new Map(
                      territoryVerticalOptions.map((option) => [
                        option.id,
                        option.name,
                      ])
                    );
                    return selected
                      .map((value) => lookup.get(value) || value)
                      .join(', ');
                  }}
                  MenuProps={MULTI_SELECT_MENU_PROPS}
                >
                  <MenuItem value="__all">
                    <ListItemText primary="All verticals" />
                  </MenuItem>
                  {territoryVerticalOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      <Checkbox
                        checked={
                          territoryVerticalFilter.indexOf(option.id) > -1
                        }
                      />
                      <ListItemText primary={option.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            }
            defaultExpanded={false}
            onToggle={handleBreakdownsToggle}
          >
            <Box sx={{ p: 2 }}>
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={compactTableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={stickyHeadCol}>Territory</TableCell>
                      <TableCell>{withFees('Spend')}</TableCell>
                      <TableCell>Leads</TableCell>
                      <TableCell>CPL</TableCell>
                      <TableCell>Self Book</TableCell>
                      <TableCell>CP Self Book</TableCell>
                      <TableCell>Accepted</TableCell>
                      <TableCell>Accepted Rate</TableCell>
                      <TableCell>Success</TableCell>
                      <TableCell>Success Rate</TableCell>
                      <TableCell>Out of Area</TableCell>
                      <TableCell>Out of Area Rate</TableCell>
                      <TableCell>Booked</TableCell>
                      <TableCell>CP Booked</TableCell>
                      <TableCell>Set Rate</TableCell>
                      <TableCell>Issues</TableCell>
                      <TableCell>CP Issue</TableCell>
                      <TableCell>Issue Rate</TableCell>
                      <TableCell>Lead To Issue</TableCell>
                      <TableCell>Demos</TableCell>
                      <TableCell>CP Demo</TableCell>
                      <TableCell>Demo Rate</TableCell>
                      <TableCell>Gross Sold</TableCell>
                      <TableCell>Net Sold</TableCell>
                      <TableCell>Net Close Rate</TableCell>
                      <TableCell>Net Amount</TableCell>
                      <TableCell>Avg Deal</TableCell>
                      <TableCell>Revenue per Lead</TableCell>
                      <TableCell>Cost of Marketing</TableCell>
                      <TableCell>Budget</TableCell>
                      <TableCell>Projected Spend</TableCell>
                      <TableCell>Pacing %</TableCell>
                      <TableCell>Remaining Budget</TableCell>
                      <TableCell>Remaining / Day</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedTerritories.length > 0 ? (
                      <>
                        {sortedTerritories.map((territory) => {
                          const baseSpend = territory.total_spend_sum || 0;
                          const adjSpend = baseSpend * (1 + feeRate);
                          const spend = showFee ? adjSpend : baseSpend;
                          const leads = territory.total_leads_sum || 0;
                          const selfBook = territory.self_books_sum || 0;
                          const accepted = territory.accepted_sum || 0;
                          const success = territory.success_sum || 0;
                          const outOfArea = territory.out_of_areas_count_sum || 0;
                          const booked = territory.booked_sum || 0;
                          const issues = territory.issues_sum || 0;
                          const scheduled = territory.scheduled_sum || 0;
                          const demos = territory.demos_sum || 0;
                          const grossSold =
                            territory.gross_sales_sum || territory.gross_sold_sum || 0;
                          const netSold =
                            territory.net_sales_sum || territory.net_sold_sum || 0;
                          const netAmount = territory.net_amount_sum || 0;
                          const displayName =
                            territory.displayName ||
                            territory.client_territory__name ||
                            territory.territory_name ||
                            'Unknown Territory';
                          const territoryBudgetKey = normalizeValue(displayName);
                          const territoryBudgetInfo =
                            budgetTerritories[territoryBudgetKey] || {};

                          return (
                            <TableRow key={territory.uuid || displayName}>
                              <TableCell sx={stickyCol}>{displayName}</TableCell>
                              <TableCell><DollarFormatter value={spend} /></TableCell>
                              <TableCell>{leads}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, leads)} /></TableCell>
                              <TableCell>{selfBook}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, selfBook)} /></TableCell>
                              <TableCell>{accepted}</TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(accepted, leads)} />
                              </TableCell>
                              <TableCell>{success}</TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(success, leads)} />
                              </TableCell>
                              <TableCell>{outOfArea}</TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(outOfArea, leads)} />
                              </TableCell>
                              <TableCell>{booked}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, booked)} /></TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(booked, leads)} />
                              </TableCell>
                              <TableCell>{issues}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, issues)} /></TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(issues, scheduled)} />
                              </TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(issues, leads)} />
                              </TableCell>
                              <TableCell>{demos}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, demos)} /></TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(demos, issues)} />
                              </TableCell>
                              <TableCell>{grossSold}</TableCell>
                              <TableCell>{netSold}</TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(netSold, demos)} />
                              </TableCell>
                              <TableCell><DollarFormatter value={netAmount} /></TableCell>
                              <TableCell><DollarFormatter value={safeDiv(netAmount, netSold)} /></TableCell>
                              <TableCell><DollarFormatter value={safeDiv(netAmount, leads)} /></TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(spend, netAmount)} />
                              </TableCell>
                              <TableCell><DollarFormatter value={territoryBudgetInfo?.budget_amount} /></TableCell>
                              <TableCell><DollarFormatter value={territoryBudgetInfo?.projected_spend} /></TableCell>
                              <TableCell>
                                <PercentageFormatter value={territoryBudgetInfo?.pacing_percentage} isRatio={false} />
                              </TableCell>
                              <TableCell><DollarFormatter value={territoryBudgetInfo?.budget_remaining} /></TableCell>
                              <TableCell>
                                <DollarFormatter
                                  value={
                                    territoryBudgetInfo?.calendar_budget_remaining_per_day ??
                                    territoryBudgetInfo?.remaining_per_day
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow>
                          <TableCell sx={{ ...stickyCol, fontWeight: 700 }}>Total</TableCell>
                          <TableCell><DollarFormatter value={territoryTotals.spend} /></TableCell>
                          <TableCell>{territoryTotals.leads}</TableCell>
                          <TableCell>
                            <DollarFormatter value={safeDiv(territoryTotals.spend, territoryTotals.leads)} />
                          </TableCell>
                          <TableCell>{territoryTotals.selfBook}</TableCell>
                          <TableCell>
                            <DollarFormatter value={safeDiv(territoryTotals.spend, territoryTotals.selfBook)} />
                          </TableCell>
                          <TableCell>{territoryTotals.accepted}</TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(territoryTotals.accepted, territoryTotals.leads)} />
                          </TableCell>
                          <TableCell>{territoryTotals.success}</TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(territoryTotals.success, territoryTotals.leads)} />
                          </TableCell>
                          <TableCell>{territoryTotals.outOfArea}</TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(territoryTotals.outOfArea, territoryTotals.leads)} />
                          </TableCell>
                          <TableCell>{territoryTotals.booked}</TableCell>
                          <TableCell>
                            <DollarFormatter value={safeDiv(territoryTotals.spend, territoryTotals.booked)} />
                          </TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(territoryTotals.booked, territoryTotals.leads)} />
                          </TableCell>
                          <TableCell>{territoryTotals.issues}</TableCell>
                          <TableCell>
                            <DollarFormatter value={safeDiv(territoryTotals.spend, territoryTotals.issues)} />
                          </TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(territoryTotals.issues, territoryTotals.scheduled)} />
                          </TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(territoryTotals.issues, territoryTotals.leads)} />
                          </TableCell>
                          <TableCell>{territoryTotals.demos}</TableCell>
                          <TableCell>
                            <DollarFormatter value={safeDiv(territoryTotals.spend, territoryTotals.demos)} />
                          </TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(territoryTotals.demos, territoryTotals.issues)} />
                          </TableCell>
                          <TableCell>{territoryTotals.grossSold}</TableCell>
                          <TableCell>{territoryTotals.netSold}</TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(territoryTotals.netSold, territoryTotals.demos)} />
                          </TableCell>
                          <TableCell><DollarFormatter value={territoryTotals.netAmount} /></TableCell>
                          <TableCell>
                            <DollarFormatter value={safeDiv(territoryTotals.netAmount, territoryTotals.netSold)} />
                          </TableCell>
                          <TableCell>
                            <DollarFormatter value={safeDiv(territoryTotals.netAmount, territoryTotals.leads)} />
                          </TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(territoryTotals.spend, territoryTotals.netAmount)} />
                          </TableCell>
                          <TableCell>
                            <DollarFormatter value={territoryBudgetTotals.budget_amount} />
                          </TableCell>
                          <TableCell>
                            <DollarFormatter value={territoryBudgetTotals.projected_spend} />
                          </TableCell>
                          <TableCell>
                            <PercentageFormatter
                              value={safeDiv(territoryBudgetTotals.projected_spend, territoryBudgetTotals.budget_amount) * 100}
                              isRatio={false}
                            />
                          </TableCell>
                          <TableCell>
                            <DollarFormatter value={territoryBudgetTotals.budget_remaining} />
                          </TableCell>
                          <TableCell>
                            <DollarFormatter value={territoryBudgetTotals.remaining_per_day} />
                          </TableCell>
                        </TableRow>
                      </>
                    ) : breakdownsLoading ? (
                      renderBreakdownsLoadingRow(33)
                    ) : (
                      <TableRow>
                        <TableCell colSpan={33} align="center">
                          No territory performance data available for the selected range.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section
            title="State Performance"
            subtitle="Regional view by state."
            defaultExpanded={false}
            onToggle={handleBreakdownsToggle}
          >
            <Box sx={{ p: 2 }}>
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={compactTableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={stickyHeadCol}>State</TableCell>
                      <TableCell>{withFees('Spend')}</TableCell>
                      <TableCell>Leads</TableCell>
                      <TableCell>CPL</TableCell>
                      <TableCell>Self Book</TableCell>
                      <TableCell>CP Self Book</TableCell>
                      <TableCell>Accepted</TableCell>
                      <TableCell>Accepted Rate</TableCell>
                      <TableCell>Success</TableCell>
                      <TableCell>Success Rate</TableCell>
                      <TableCell>Out of Area</TableCell>
                      <TableCell>Out of Area Rate</TableCell>
                      <TableCell>Booked</TableCell>
                      <TableCell>CP Booked</TableCell>
                      <TableCell>Set Rate</TableCell>
                      <TableCell>Issues</TableCell>
                      <TableCell>CP Issue</TableCell>
                      <TableCell>Issue Rate</TableCell>
                      <TableCell>Lead To Issue</TableCell>
                      <TableCell>Demos</TableCell>
                      <TableCell>CP Demo</TableCell>
                      <TableCell>Demo Rate</TableCell>
                      <TableCell>Gross Sold</TableCell>
                      <TableCell>Net Sold</TableCell>
                      <TableCell>Net Close Rate</TableCell>
                      <TableCell>Net Amount</TableCell>
                      <TableCell>Avg Deal</TableCell>
                      <TableCell>Revenue per Lead</TableCell>
                      <TableCell>Cost of Marketing</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedStates.length > 0 ? (
                      <>
                        {sortedStates.map((s) => {
                          const baseSpend = s.total_spend_sum || 0;
                          const adjSpend = baseSpend * (1 + feeRate);
                          const spend = showFee ? adjSpend : baseSpend;
                          const leads = s.total_leads_sum || 0;
                          const selfBook = s.self_books_sum || 0;
                          const accepted = s.accepted_sum || 0;
                          const success = s.success_sum || 0;
                          const outOfArea = s.out_of_areas_count_sum || 0;
                          const booked = s.booked_sum || 0;
                          const issues = s.issues_sum || 0;
                          const scheduled = s.scheduled_sum || 0;
                          const demos = s.demos_sum || 0;
                          const grossSold = s.gross_sales_sum || s.gross_sold_sum || 0;
                          const netSold = s.net_sales_sum || s.net_sold_sum || 0;
                          const netAmount = s.net_amount_sum || 0;
                          return (
                            <TableRow key={s.uuid || s.displayName}>
                              <TableCell sx={stickyCol}>{s.displayName}</TableCell>
                              <TableCell><DollarFormatter value={spend} /></TableCell>
                              <TableCell>{leads}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, leads)} /></TableCell>
                              <TableCell>{selfBook}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, selfBook)} /></TableCell>
                              <TableCell>{accepted}</TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(accepted, leads)} />
                              </TableCell>
                              <TableCell>{success}</TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(success, leads)} />
                              </TableCell>
                              <TableCell>{outOfArea}</TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(outOfArea, leads)} />
                              </TableCell>
                              <TableCell>{booked}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, booked)} /></TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(booked, leads)} />
                              </TableCell>
                              <TableCell>{issues}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, issues)} /></TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(issues, scheduled)} />
                              </TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(issues, leads)} />
                              </TableCell>
                              <TableCell>{demos}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, demos)} /></TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(demos, issues)} />
                              </TableCell>
                              <TableCell>{grossSold}</TableCell>
                              <TableCell>{netSold}</TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(netSold, demos)} />
                              </TableCell>
                              <TableCell><DollarFormatter value={netAmount} /></TableCell>
                              <TableCell><DollarFormatter value={safeDiv(netAmount, netSold)} /></TableCell>
                              <TableCell><DollarFormatter value={safeDiv(netAmount, leads)} /></TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(spend, netAmount)} />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow>
                          <TableCell sx={{ ...stickyCol, fontWeight: 700 }}>Total</TableCell>
                          <TableCell><DollarFormatter value={stateTotals.spend} /></TableCell>
                          <TableCell>{stateTotals.leads}</TableCell>
                          <TableCell>
                            <DollarFormatter value={safeDiv(stateTotals.spend, stateTotals.leads)} />
                          </TableCell>
                          <TableCell>{stateTotals.selfBook}</TableCell>
                          <TableCell>
                            <DollarFormatter value={safeDiv(stateTotals.spend, stateTotals.selfBook)} />
                          </TableCell>
                          <TableCell>{stateTotals.accepted}</TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(stateTotals.accepted, stateTotals.leads)} />
                          </TableCell>
                          <TableCell>{stateTotals.success}</TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(stateTotals.success, stateTotals.leads)} />
                          </TableCell>
                          <TableCell>{stateTotals.outOfArea}</TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(stateTotals.outOfArea, stateTotals.leads)} />
                          </TableCell>
                          <TableCell>{stateTotals.booked}</TableCell>
                          <TableCell>
                            <DollarFormatter value={safeDiv(stateTotals.spend, stateTotals.booked)} />
                          </TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(stateTotals.booked, stateTotals.leads)} />
                          </TableCell>
                          <TableCell>{stateTotals.issues}</TableCell>
                          <TableCell>
                            <DollarFormatter value={safeDiv(stateTotals.spend, stateTotals.issues)} />
                          </TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(stateTotals.issues, stateTotals.scheduled)} />
                          </TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(stateTotals.issues, stateTotals.leads)} />
                          </TableCell>
                          <TableCell>{stateTotals.demos}</TableCell>
                          <TableCell>
                            <DollarFormatter value={safeDiv(stateTotals.spend, stateTotals.demos)} />
                          </TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(stateTotals.demos, stateTotals.issues)} />
                          </TableCell>
                          <TableCell>{stateTotals.grossSold}</TableCell>
                          <TableCell>{stateTotals.netSold}</TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(stateTotals.netSold, stateTotals.demos)} />
                          </TableCell>
                          <TableCell><DollarFormatter value={stateTotals.netAmount} /></TableCell>
                          <TableCell>
                            <DollarFormatter value={safeDiv(stateTotals.netAmount, stateTotals.netSold)} />
                          </TableCell>
                          <TableCell>
                            <DollarFormatter value={safeDiv(stateTotals.netAmount, stateTotals.leads)} />
                          </TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(stateTotals.spend, stateTotals.netAmount)} />
                          </TableCell>
                        </TableRow>
                      </>
                    ) : breakdownsLoading ? (
                      renderBreakdownsLoadingRow(29)
                    ) : (
                      <TableRow>
                        <TableCell colSpan={29} align="center">
                          No state performance data available for the selected range.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section
            title="DMA Performance"
            subtitle="DMAs ranked by spend and leads."
            defaultExpanded={false}
            onToggle={handleBreakdownsToggle}
          >
            <Box sx={{ p: 2 }}>
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={compactTableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={stickyHeadCol}>DMA</TableCell>
                      <TableCell>{withFees('Spend')}</TableCell>
                      <TableCell>Leads</TableCell>
                      <TableCell>CPL</TableCell>
                      <TableCell>Self Book</TableCell>
                      <TableCell>CP Self Book</TableCell>
                      <TableCell>Accepted</TableCell>
                      <TableCell>Accepted Rate</TableCell>
                      <TableCell>Success</TableCell>
                      <TableCell>Success Rate</TableCell>
                      <TableCell>Out of Area</TableCell>
                      <TableCell>Out of Area Rate</TableCell>
                      <TableCell>Booked</TableCell>
                      <TableCell>CP Booked</TableCell>
                      <TableCell>Set Rate</TableCell>
                      <TableCell>Issues</TableCell>
                      <TableCell>CP Issue</TableCell>
                      <TableCell>Issue Rate</TableCell>
                      <TableCell>Lead To Issue</TableCell>
                      <TableCell>Demos</TableCell>
                      <TableCell>CP Demo</TableCell>
                      <TableCell>Demo Rate</TableCell>
                      <TableCell>Gross Sold</TableCell>
                      <TableCell>Net Sold</TableCell>
                      <TableCell>Net Close Rate</TableCell>
                      <TableCell>Net Amount</TableCell>
                      <TableCell>Avg Deal</TableCell>
                      <TableCell>Revenue per Lead</TableCell>
                      <TableCell>Cost of Marketing</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedDmas.length > 0 ? (
                      <>
                        {sortedDmas.map((d) => {
                          const baseSpend = d.total_spend_sum || 0;
                          const adjSpend = baseSpend * (1 + feeRate);
                          const spend = showFee ? adjSpend : baseSpend;
                          const leads = d.total_leads_sum || 0;
                          const selfBook = d.self_books_sum || 0;
                          const accepted = d.accepted_sum || 0;
                          const success = d.success_sum || 0;
                          const outOfArea = d.out_of_areas_count_sum || 0;
                          const booked = d.booked_sum || 0;
                          const issues = d.issues_sum || 0;
                          const scheduled = d.scheduled_sum || 0;
                          const demos = d.demos_sum || 0;
                          const grossSold = d.gross_sales_sum || d.gross_sold_sum || 0;
                          const netSold = d.net_sales_sum || d.net_sold_sum || 0;
                          const netAmount = d.net_amount_sum || 0;
                          return (
                            <TableRow key={d.uuid || d.displayName}>
                              <TableCell sx={stickyCol}>{d.displayName}</TableCell>
                              <TableCell><DollarFormatter value={spend} /></TableCell>
                              <TableCell>{leads}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, leads)} /></TableCell>
                              <TableCell>{selfBook}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, selfBook)} /></TableCell>
                              <TableCell>{accepted}</TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(accepted, leads)} />
                              </TableCell>
                              <TableCell>{success}</TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(success, leads)} />
                              </TableCell>
                              <TableCell>{outOfArea}</TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(outOfArea, leads)} />
                              </TableCell>
                              <TableCell>{booked}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, booked)} /></TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(booked, leads)} />
                              </TableCell>
                              <TableCell>{issues}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, issues)} /></TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(issues, scheduled)} />
                              </TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(issues, leads)} />
                              </TableCell>
                              <TableCell>{demos}</TableCell>
                              <TableCell><DollarFormatter value={safeDiv(spend, demos)} /></TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(demos, issues)} />
                              </TableCell>
                              <TableCell>{grossSold}</TableCell>
                              <TableCell>{netSold}</TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(netSold, demos)} />
                              </TableCell>
                              <TableCell><DollarFormatter value={netAmount} /></TableCell>
                              <TableCell><DollarFormatter value={safeDiv(netAmount, netSold)} /></TableCell>
                              <TableCell><DollarFormatter value={safeDiv(netAmount, leads)} /></TableCell>
                              <TableCell>
                                <PercentageFormatter value={safeDiv(spend, netAmount)} />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow>
                          <TableCell sx={{ ...stickyCol, fontWeight: 700 }}>Total</TableCell>
                          <TableCell><DollarFormatter value={dmaTotals.spend} /></TableCell>
                          <TableCell>{dmaTotals.leads}</TableCell>
                          <TableCell>
                            <DollarFormatter value={safeDiv(dmaTotals.spend, dmaTotals.leads)} />
                          </TableCell>
                          <TableCell>{dmaTotals.selfBook}</TableCell>
                          <TableCell>
                            <DollarFormatter value={safeDiv(dmaTotals.spend, dmaTotals.selfBook)} />
                          </TableCell>
                          <TableCell>{dmaTotals.accepted}</TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(dmaTotals.accepted, dmaTotals.leads)} />
                          </TableCell>
                          <TableCell>{dmaTotals.success}</TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(dmaTotals.success, dmaTotals.leads)} />
                          </TableCell>
                          <TableCell>{dmaTotals.outOfArea}</TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(dmaTotals.outOfArea, dmaTotals.leads)} />
                          </TableCell>
                          <TableCell>{dmaTotals.booked}</TableCell>
                          <TableCell>
                            <DollarFormatter value={safeDiv(dmaTotals.spend, dmaTotals.booked)} />
                          </TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(dmaTotals.booked, dmaTotals.leads)} />
                          </TableCell>
                          <TableCell>{dmaTotals.issues}</TableCell>
                          <TableCell>
                            <DollarFormatter value={safeDiv(dmaTotals.spend, dmaTotals.issues)} />
                          </TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(dmaTotals.issues, dmaTotals.scheduled)} />
                          </TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(dmaTotals.issues, dmaTotals.leads)} />
                          </TableCell>
                          <TableCell>{dmaTotals.demos}</TableCell>
                          <TableCell>
                            <DollarFormatter value={safeDiv(dmaTotals.spend, dmaTotals.demos)} />
                          </TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(dmaTotals.demos, dmaTotals.issues)} />
                          </TableCell>
                          <TableCell>{dmaTotals.grossSold}</TableCell>
                          <TableCell>{dmaTotals.netSold}</TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(dmaTotals.netSold, dmaTotals.demos)} />
                          </TableCell>
                          <TableCell><DollarFormatter value={dmaTotals.netAmount} /></TableCell>
                          <TableCell>
                            <DollarFormatter value={safeDiv(dmaTotals.netAmount, dmaTotals.netSold)} />
                          </TableCell>
                          <TableCell>
                            <DollarFormatter value={safeDiv(dmaTotals.netAmount, dmaTotals.leads)} />
                          </TableCell>
                          <TableCell>
                            <PercentageFormatter value={safeDiv(dmaTotals.spend, dmaTotals.netAmount)} />
                          </TableCell>
                        </TableRow>
                      </>
                    ) : breakdownsLoading ? (
                      renderBreakdownsLoadingRow(29)
                    ) : (
                      <TableRow>
                        <TableCell colSpan={29} align="center">
                          No DMA performance data available for the selected range.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section
            title="City Performance"
            subtitle="Drill into city-level results."
            defaultExpanded={false}
            onToggle={handleBreakdownsToggle}
          >
            <Box sx={{ p: 2 }}>
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={compactTableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={stickyHeadCol}>City</TableCell>
                      <TableCell>State</TableCell>
                      <TableCell>Leads</TableCell>
                      <TableCell>Self Book</TableCell>
                      <TableCell>Accepted</TableCell>
                      <TableCell>Accepted Rate</TableCell>
                      <TableCell>Success</TableCell>
                      <TableCell>Success Rate</TableCell>
                      <TableCell>Out of Area</TableCell>
                      <TableCell>Out of Area Rate</TableCell>
                      <TableCell>Booked</TableCell>
                      <TableCell>Set Rate</TableCell>
                      <TableCell>Issues</TableCell>
                      <TableCell>Issue Rate</TableCell>
                      <TableCell>Lead To Issue</TableCell>
                      <TableCell>Demos</TableCell>
                      <TableCell>Demo Rate</TableCell>
                      <TableCell>Gross Sold</TableCell>
                      <TableCell>Net Sold</TableCell>
                      <TableCell>Net Close Rate</TableCell>
                      <TableCell>Net Amount</TableCell>
                      <TableCell>Avg Deal</TableCell>
                      <TableCell>Revenue per Lead</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedCities.length > 0 ? (
                      paginatedCities.map((cityRow, index) => {
                        const leads = cityRow.total_leads_sum || 0;
                        const selfBook = cityRow.self_books_sum || 0;
                        const accepted = cityRow.accepted_sum || 0;
                        const success = cityRow.success_sum || 0;
                        const outOfArea = cityRow.out_of_areas_count_sum || 0;
                        const booked = cityRow.booked_sum || 0;
                        const issues = cityRow.issues_sum || 0;
                        const scheduled = cityRow.scheduled_sum || 0;
                        const demos = cityRow.demos_sum || 0;
                        const grossSold = cityRow.gross_sales_sum || cityRow.gross_sold_sum || 0;
                        const netSold = cityRow.net_sales_sum || cityRow.net_sold_sum || 0;
                        const netAmount = cityRow.net_amount_sum || 0;
                        const cityName = cityRow.city_name || cityRow.city || 'Unknown City';
                        const stateName = cityRow.state_name || cityRow.state || '';
                        const key = `${cityName}-${stateName}-${index}`;

                        return (
                          <TableRow key={key}>
                            <TableCell sx={stickyCol}>{cityName}</TableCell>
                            <TableCell>{stateName}</TableCell>
                            <TableCell>{leads}</TableCell>
                            <TableCell>{selfBook}</TableCell>
                            <TableCell>{accepted}</TableCell>
                            <TableCell>
                              <PercentageFormatter value={safeDiv(accepted, leads)} />
                            </TableCell>
                            <TableCell>{success}</TableCell>
                            <TableCell>
                              <PercentageFormatter value={safeDiv(success, leads)} />
                            </TableCell>
                            <TableCell>{outOfArea}</TableCell>
                            <TableCell>
                              <PercentageFormatter value={safeDiv(outOfArea, leads)} />
                            </TableCell>
                            <TableCell>{booked}</TableCell>
                            <TableCell>
                              <PercentageFormatter value={safeDiv(booked, leads)} />
                            </TableCell>
                            <TableCell>{issues}</TableCell>
                            <TableCell>
                              <PercentageFormatter value={safeDiv(issues, scheduled)} />
                            </TableCell>
                            <TableCell>
                              <PercentageFormatter value={safeDiv(issues, leads)} />
                            </TableCell>
                            <TableCell>{demos}</TableCell>
                            <TableCell>
                              <PercentageFormatter value={safeDiv(demos, issues)} />
                            </TableCell>
                            <TableCell>{grossSold}</TableCell>
                            <TableCell>{netSold}</TableCell>
                            <TableCell>
                              <PercentageFormatter value={safeDiv(netSold, demos)} />
                            </TableCell>
                            <TableCell>
                              <DollarFormatter value={netAmount} />
                            </TableCell>
                            <TableCell>
                              <DollarFormatter value={safeDiv(netAmount, netSold)} />
                            </TableCell>
                            <TableCell>
                              <DollarFormatter value={safeDiv(netAmount, leads)} />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : breakdownsLoading ? (
                      renderBreakdownsLoadingRow(23)
                    ) : (
                      <TableRow>
                        <TableCell colSpan={23} align="center">
                          No city performance data available for the selected range.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                rowsPerPageOptions={[25, 50, 100]}
                count={sortedCities.length}
                page={cityPage}
                onPageChange={(_, newPage) => setCityPage(newPage)}
                rowsPerPage={cityRowsPerPage}
                onRowsPerPageChange={(event) => {
                  setCityRowsPerPage(parseInt(event.target.value, 10));
                  setCityPage(0);
                }}
              />
            </Box>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section
            title="County Performance"
            subtitle="Drill into county-level results."
            defaultExpanded={false}
            onToggle={handleBreakdownsToggle}
          >
            <Box sx={{ p: 2 }}>
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={compactTableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={stickyHeadCol}>County</TableCell>
                      <TableCell>State</TableCell>
                      <TableCell>Leads</TableCell>
                      <TableCell>Self Book</TableCell>
                      <TableCell>Accepted</TableCell>
                      <TableCell>Accepted Rate</TableCell>
                      <TableCell>Success</TableCell>
                      <TableCell>Success Rate</TableCell>
                      <TableCell>Out of Area</TableCell>
                      <TableCell>Out of Area Rate</TableCell>
                      <TableCell>Booked</TableCell>
                      <TableCell>Set Rate</TableCell>
                      <TableCell>Issues</TableCell>
                      <TableCell>Issue Rate</TableCell>
                      <TableCell>Lead To Issue</TableCell>
                      <TableCell>Demos</TableCell>
                      <TableCell>Demo Rate</TableCell>
                      <TableCell>Gross Sold</TableCell>
                      <TableCell>Net Sold</TableCell>
                      <TableCell>Net Close Rate</TableCell>
                      <TableCell>Net Amount</TableCell>
                      <TableCell>Avg Deal</TableCell>
                      <TableCell>Revenue per Lead</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedCounties.length > 0 ? (
                      paginatedCounties.map((countyRow, index) => {
                        const leads = countyRow.total_leads_sum || 0;
                        const selfBook = countyRow.self_books_sum || 0;
                        const accepted = countyRow.accepted_sum || 0;
                        const success = countyRow.success_sum || 0;
                        const outOfArea = countyRow.out_of_areas_count_sum || 0;
                        const booked = countyRow.booked_sum || 0;
                        const issues = countyRow.issues_sum || 0;
                        const scheduled = countyRow.scheduled_sum || 0;
                        const demos = countyRow.demos_sum || 0;
                        const grossSold = countyRow.gross_sales_sum || countyRow.gross_sold_sum || 0;
                        const netSold = countyRow.net_sales_sum || countyRow.net_sold_sum || 0;
                        const netAmount = countyRow.net_amount_sum || 0;
                        const countyName = countyRow.county_name || countyRow.county || 'Unknown County';
                        const stateName = countyRow.state_name || countyRow.state || '';
                        const key = `${countyName}-${stateName}-${index}`;

                        return (
                          <TableRow key={key}>
                            <TableCell sx={stickyCol}>{countyName}</TableCell>
                            <TableCell>{stateName}</TableCell>
                            <TableCell>{leads}</TableCell>
                            <TableCell>{selfBook}</TableCell>
                            <TableCell>{accepted}</TableCell>
                            <TableCell>
                              <PercentageFormatter value={safeDiv(accepted, leads)} />
                            </TableCell>
                            <TableCell>{success}</TableCell>
                            <TableCell>
                              <PercentageFormatter value={safeDiv(success, leads)} />
                            </TableCell>
                            <TableCell>{outOfArea}</TableCell>
                            <TableCell>
                              <PercentageFormatter value={safeDiv(outOfArea, leads)} />
                            </TableCell>
                            <TableCell>{booked}</TableCell>
                            <TableCell>
                              <PercentageFormatter value={safeDiv(booked, leads)} />
                            </TableCell>
                            <TableCell>{issues}</TableCell>
                            <TableCell>
                              <PercentageFormatter value={safeDiv(issues, scheduled)} />
                            </TableCell>
                            <TableCell>
                              <PercentageFormatter value={safeDiv(issues, leads)} />
                            </TableCell>
                            <TableCell>{demos}</TableCell>
                            <TableCell>
                              <PercentageFormatter value={safeDiv(demos, issues)} />
                            </TableCell>
                            <TableCell>{grossSold}</TableCell>
                            <TableCell>{netSold}</TableCell>
                            <TableCell>
                              <PercentageFormatter value={safeDiv(netSold, demos)} />
                            </TableCell>
                            <TableCell>
                              <DollarFormatter value={netAmount} />
                            </TableCell>
                            <TableCell>
                              <DollarFormatter value={safeDiv(netAmount, netSold)} />
                            </TableCell>
                            <TableCell>
                              <DollarFormatter value={safeDiv(netAmount, leads)} />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : breakdownsLoading ? (
                      renderBreakdownsLoadingRow(23)
                    ) : (
                      <TableRow>
                        <TableCell colSpan={23} align="center">
                          No county performance data available for the selected range.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                rowsPerPageOptions={[25, 50, 100]}
                count={sortedCounties.length}
                page={countyPage}
                onPageChange={(_, newPage) => setCountyPage(newPage)}
                rowsPerPage={countyRowsPerPage}
                onRowsPerPageChange={(event) => {
                  setCountyRowsPerPage(parseInt(event.target.value, 10));
                  setCountyPage(0);
                }}
              />
            </Box>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section
            title="Geo Coverage"
            subtitle="Lead density by ZIP"
            defaultExpanded={false}
          >
            <Box sx={{ p: 2 }}>
              <ClientZipMap
                clientId={
                  selectedScopeClientId
                    ? String(selectedScopeClientId)
                    : scopeType === 'client' && clientPk
                      ? String(clientPk)
                      : ''
                }
                parentCompanyId={
                  scopeType === 'parent' && !selectedScopeClientId
                    ? parentCompanyId
                    : null
                }
                startDate={startDate}
                endDate={endDate}
              />
            </Box>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section
            title="Leads"
            subtitle="Search, filter, and drill into individual leads."
            defaultExpanded={false}
          >
            <Divider />
            <Box sx={{ p: 2 }}>
              <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" alignItems="center">
                <TextField
                  label="Phone"
                  value={filters.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  size="small"
                />
                <TextField
                  label="Email"
                  value={filters.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  size="small"
                />
                <TextField
                  label="Lead ID"
                  value={filters.id}
                  onChange={(e) => handleInputChange('id', e.target.value)}
                  size="small"
                />
                <TextField
                  label="ZIP Code"
                  value={filters.zip_code}
                  onChange={(e) => handleInputChange('zip_code', e.target.value)}
                  size="small"
                />
                <TextField
                  label="Source"
                  value={filters.source}
                  onChange={(e) => handleInputChange('source', e.target.value)}
                  size="small"
                />
                <Box sx={{ minWidth: 200 }}>
                  <VerticalDropdown
                    value={filters.vertical}
                    valueType="name"
                    onChange={(value) => handleInputChange('vertical', value)}
                  />
                </Box>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Success Post</InputLabel>
                  <Select
                    value={filters.success_post}
                    label="Success Post"
                    onChange={(e) => handleInputChange('success_post', e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Any</em>
                    </MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  sx={{ height: 40 }}
                >
                  Search
                </Button>
              </Stack>

              <TableContainer component={Paper}>
                <Table size="small" sx={compactTableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Client</TableCell>
                      <TableCell>Date Created (EST)</TableCell>
                      <TableCell>Date Sent (EST)</TableCell>
                      <TableCell>First Name</TableCell>
                      <TableCell>Last Name</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Territory</TableCell>
                      <TableCell>ZIP Code</TableCell>
                      <TableCell>Self-Book Appt</TableCell>
                      <TableCell>Vertical</TableCell>
                      <TableCell>Processed</TableCell>
                      <TableCell>Success Post</TableCell>
                      <TableCell>Post Response</TableCell>
                      <TableCell>Appointment Date</TableCell>
                      <TableCell>Issue</TableCell>
                      <TableCell>Demo</TableCell>
                      <TableCell>Gross Amount</TableCell>
                      <TableCell>Net Amount</TableCell>
                      <TableCell>Sold Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leads.map((lead) => {
                      const toEST = (utc) =>
                        utc
                          ? new Date(utc).toLocaleString('en-US', {
                              timeZone: 'America/New_York'
                            })
                          : '-';
                      const backend = lead.related_backend_data?.[0];
                      return (
                        <TableRow key={lead.id}>
                          <TableCell>{lead.client_name || '-'}</TableCell>
                          <TableCell>{toEST(lead.date_created)}</TableCell>
                          <TableCell>{toEST(lead.webhook_sent_datetime)}</TableCell>
                          <TableCell>
                            <Link
                              to={`/leads/${lead.id}`}
                              style={{ textDecoration: 'none', color: '#2f80ed' }}
                            >
                              {lead.first_name || 'View'}
                            </Link>
                          </TableCell>
                          <TableCell>{lead.last_name || '-'}</TableCell>
                          <TableCell>{lead.phone || '-'}</TableCell>
                          <TableCell>{lead.email || '-'}</TableCell>
                          <TableCell>{lead.source || '-'}</TableCell>
                          <TableCell>{lead.territory || '-'}</TableCell>
                          <TableCell>{lead.zip_code || '-'}</TableCell>
                          <TableCell>{lead.self_book_appointment_date_time}</TableCell>
                          <TableCell>{lead.vertical || '-'}</TableCell>
                          <TableCell>{lead.processed ? '✅' : '❌'}</TableCell>
                          <TableCell>{lead.success_post ? '✅' : '❌'}</TableCell>
                          <TableCell>{lead.post_response || '-'}</TableCell>
                          <TableCell>{backend?.appointment_date || '-'}</TableCell>
                          <TableCell>{backend?.issue ? '✅' : '❌'}</TableCell>
                          <TableCell>{backend?.demo ? '✅' : '❌'}</TableCell>
                          <TableCell>
                            {backend?.gross_amount ? `$${backend.gross_amount}` : '-'}
                          </TableCell>
                          <TableCell>
                            {backend?.net_amount ? `$${backend.net_amount}` : '-'}
                          </TableCell>
                          <TableCell>{backend?.sold_date || '-'}</TableCell>

                        </TableRow>
                      );
                    })}
                    {leads.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={21} align="center">
                          {leadsLoading ? 'Loading...' : 'No leads found.'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Stack direction="row" justifyContent="flex-end" mt={2}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  shape="rounded"
                />
              </Stack>
            </Box>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section
            title="Data Files"
            subtitle="Latest processed CSV imported for this client."
            defaultExpanded={false}
            action={(
              <Button
                variant="outlined"
                size="small"
                disabled={!latestProcessedCsv?.id || csvDownloadLoading}
                onClick={handleDownloadLatestProcessedCsv}
              >
                {csvDownloadLoading ? <CircularProgress size={16} /> : 'Download CSV'}
              </Button>
            )}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {scopeType === 'parent' && !selectedScopeClientId
                  ? 'Select a client to view processed CSV files.'
                  : latestProcessedCsvLoading
                    ? 'Loading latest file...'
                    : latestProcessedCsv
                      ? `${latestProcessedCsv.original_filename || 'processed.csv'} • ${new Date(
                          latestProcessedCsv.processed_at
                        ).toLocaleString('en-US', { timeZone: 'America/New_York' })} • ${formatFileSize(
                          latestProcessedCsv.file_size
                        )}`
                      : latestProcessedCsvError || 'No processed CSV found for this client.'}
              </Typography>
            </Box>
          </Section>
        </Grid>

      </Grid>
    </Box>
  );
}
